import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useNavigate, useSearchParams } from "react-router-dom";
import { env } from "../config/env";
import { useAuth } from "../context/AuthContext";
import { isSuperAdmin } from "../utils/roles";
import { analyticsService, trackingService } from "../features/analytics/service";
import { boostService } from "../features/boost/service";
import { authStorage } from "../lib/storage/authStorage";
import { formatISTSessionDayLabel, formatISTTimeShort, parseInstant } from "../utils/dateUtils";
import NotificationBell from "../components/NotificationBell";
import LoginSignupModal from "../components/LoginSignupModal";
import VerificationRequiredModal from "../components/VerificationRequiredModal";
import { emitQuotaExceeded } from "../lib/http/quotaEvents";
import { currencySymbol } from "../lib/currency";
import { subscriptionsService } from "../features/subscriptions/service";
import FlyerTemplateDevForm from "../modules/ai_media/flyer/FlyerTemplateDevForm";
import VideoTemplateDevForm from "../modules/ai_media/video/VideoTemplateDevForm";
import ChatOverlayEditor from "../modules/ai_media/video/ChatOverlayEditor";
import ItineraryTemplateDevForm from "../modules/ai_media/itinerary/ItineraryTemplateDevForm";
import type { VideoTemplateInit } from "../modules/ai_media/video/videoTemplateService";
import type { ItineraryTemplateInit } from "../modules/ai_media/itinerary/itineraryTemplateService";
import type {
    AnyTemplateItem,
    TemplateCategory,
    TemplatesListResponse as TemplatesListApiResponse,
} from "../modules/ai_media/common/templateTypes";
import { stripTemplateInputTip } from "../modules/ai_media/common/templateFieldValues";
import { fetchApiErrorMessage, parseFetchApiError, type FetchApiError } from "../lib/http/fetchApiError";
import TemplateGenerationProgress from "../modules/ai_media/common/TemplateGenerationProgress";
import FlyerMediaAttach from "../modules/ai_media/common/TemplateChatAttachments";
import {
    mapTemplateProgressToMessage,
    mergeStreamStep,
    streamGenerateFromTemplate,
    type TemplateGenerationPhase,
    type StreamStep,
} from "../modules/ai_media/common/templateGenerationStream";
import {
    streamGenerateFlyerMedia,
} from "../modules/ai_media/common/flyerMediaStream";
import { useBackgroundJobs } from "../lib/notifications/useBackgroundJobs";
import {
    uploadFlyerReferenceImages,
    type FlyerMediaAttachment,
} from "../modules/ai_media/common/flyerMediaUpload";
import "./RagChatPage.styles.css";

// ─── Types ───────────────────────────────────────────────────────────────────

interface Deal {
    id: string;
    deal_id?: string;
    title: string;
    description?: string;
    item_type?: string;
    category?: string;
    category_group?: string;
    categories?: { primary_slug?: string; group_slug?: string; path?: string };
    /** Legacy flat fields (kept for old `/api/deals/chat` payloads). */
    image?: string;
    image_url?: string;
    image_clean?: string;
    video_url?: string;
    pdf_url?: string;
    html_url?: string;
    thumbnail_url?: string;
    page_urls?: string[];
    session_id?: string;
    /** Typed-deal canonical media block — preferred over the flat fields above. */
    media?: {
        image?: string | null;
        image_clean?: string | null;
        pdf_url?: string | null;
        video_url?: string | null;
        gallery?: string[];
    };
    tags?: string[];
    star_rating?: number;
    /** Canonical nested pricing (backend ChatPricing) */
    pricing?: { original_price?: number; discounted_price?: number; currency?: string };
    /** Canonical nested destination */
    destination?: { cities?: string[]; countries?: string[] };
    /** Canonical nested validity */
    validity?: { valid_from?: string; valid_until?: string };
    /** Canonical nested package */
    package?: {
        cities?: string[];
        countries?: string[];
        duration_nights?: number;
        inclusions?: string[];
        exclusions?: string[];
        highlights?: string[];
    };
    dealer_id?: string;
    dealer_name?: string;
    is_featured?: boolean;
    is_verified?: boolean;
    is_active?: boolean;
    /** Boost: set from live Redis (search surface) in the RAG pipeline. is_boosted
     * drives the "Featured" badge; boost_campaign_id attributes impression/click events. */
    is_boosted?: boolean;
    boost_count?: number;
    boost_campaign_id?: string | null;
    /** Convenience display fields joined from users on the backend */
    seller_name?: string;
    seller_whatsapp?: string;
    seller_email?: string;
    /** Public trust label shown next to the verified badge — defaults to "Gullivr Verified Seller". */
    verified_name?: string;
    _from_cross_sell?: boolean;
}

interface DmcCard {
    dealer_id: string;
    seller_name: string;
    is_verified?: boolean;
    verified_name?: string;
    /** Destinations this DMC covers (cities, countries, regions combined). */
    covered?: string[];
    destination?: string | string[];
    destinations?: string[];
    cities?: string[];
    countries?: string[];
    coverage_areas?: string[];
    segments?: string[];
    tags?: string[];
    city?: string;
    country?: string;
    avatar_url?: string;
    profile_image?: string;
    seller_match?: { score?: number; reasons?: string[] };
}

interface ChatMessage {
    id: string;
    role: "user" | "assistant";
    content: string;
    /** Enriched prompt shown in preview cards before / after generation */
    prompt_used?: string;
    is_media_preview?: boolean;
    deals?: Deal[];
    /**
     * Stage 3 — recommended DMC cards. Each card has its own Send Enquiry
     * button that opens the EnquiryModal with `dealer_id` pre-filled and
     * NO `deal_id`. One enquiry per tap; no broadcasting.
     */
    dmcs?: DmcCard[];
    intent?: string;
    stage?: "SEARCH" | "INTERESTED" | "CONNECT" | "ENQUIRY";
    show_cta?: boolean;
    cta_deal?: Deal | null;
    suggestions?: string[];
    timestamp: Date;
    /** @deprecated — new flow uses show_cta + cta_deal; kept for ConnectPanel compatibility */
    showConnectPanel?: boolean;
    /** @deprecated — new flow uses show_cta + cta_deal; kept for ConnectPanel compatibility */
    connectStatus?: Record<number, "pending" | "sent" | "error">;
    gathered_info?: Record<string, unknown>;
    cross_sell_sections?: Array<{ category: string; label: string; deals: Deal[] }>;
    /** S3 presigned URL of an attachment uploaded with this message (shown in session) */
    attachmentUrl?: string;
    /** Template thumbnail shown in user message bubble when session started from a template */
    templateThumbnailUrl?: string;
    /** True while SSE tokens are still arriving — used to show live cursor */
    streaming?: boolean;
    /** Market Research and Fresh RAG (deals) — live progress label from the backend's
     * `stage` SSE events (real pipeline step, e.g. "Checking our Thailand listings…"),
     * shown by StreamingPlaceholder while content is still empty. Not a fake timer. */
    researchStageLabel?: string;
    /** Live status while template generation streams from the backend */
    generation_status?: string;
    generation_stage?: string;
    generation_title?: string;
    generation_detail?: string;
    generation_progress?: number;
    generation_phase?: TemplateGenerationPhase;
    generation_media_type?: string;
    generation_template_name?: string;
    show_generation_skeleton?: boolean;
    is_generating?: boolean;
    /** Accumulated thinking steps — ChatGPT-style streaming log */
    generation_steps?: StreamStep[];
    /** asset_type from SSE data — "flyer" | "video" | "itinerary" | "slides" */
    generation_asset_type?: string;
    template_name?: string | null;
    /** Special message types for rich UI rendering */
    type?: "enquiry_success";
    lead_id?: string;
    seller_name?: string;
}

interface TabQuotaInfo {
    used: number;
    limit: number;
    remaining: number;
    warning?: string | null;
    action_type?: string;
}

interface Session {
    session_id: string;
    created_at: string;
    updated_at?: string;
    last_message_at?: string;
    preview?: string;
    first_message?: string;
    message_count?: number;
    selected_tab?: SessionTab;
    /** "fresh_rag" for /rag-v2 sessions, "legacy" for /rag sessions */
    source?: "fresh_rag" | "legacy";
}

interface SessionMessagePayload {
    role: string;
    message: string;
    timestamp: string;
    type?: "text" | "image" | "video" | "itinerary" | "slides";
    image_url?: string;
    html_url?: string | null;
    thumbnail_url?: string | null;
    page_urls?: string[] | null;
    asset_type?: string | null;
    s3_key?: string | null;
    intent?: string;
    resultsCount?: number;
    searchResults?: Array<{
        id?: string;
        dealId?: string;
        title?: string;
    }>;
    suggestions?: string[];
    template_name?: string | null;
    template_id?: string | null;
    prompt_used?: string | null;
    video_url?: string | null;
    uploaded_flyer_s3_key?: string | null;
    flyer_edit_upload?: boolean;
    flyer_edit?: boolean;
    /** Fresh RAG per-turn deal cards */
    deals?: Partial<Deal>[];
    /** Fresh RAG per-turn cross-sell {namespace: [cards]} */
    cross_sell?: Record<string, Partial<Deal>[]>;
    /** Fresh RAG per-turn chips */
    chips?: string[];
    /** S3 presigned URL of an attachment uploaded with this message (shown in session) */
    attachmentUrl?: string;
    /** Video chat's style-picker previews, persisted so a reload doesn't drop the card carousel. */
    style_options?: Array<{
        name: string;
        video_url?: string | null;
        font_family?: string | null;
        brand_color?: string | null;
        overlay_style?: string | null;
    }>;
}

interface SessionResponse {
    selected_tab?: SessionTab;
    messages?: SessionMessagePayload[];
    agent_state?: {
        last_deals?: Partial<Deal>[];
        gathered_info?: Record<string, string>;
    };
    /** Persisted context from the last template generation in this session */
    flyer_state?: Record<string, unknown>;
    /** Template context — present when session was started from a flyer/video template */
    template_context?: {
        template_id?: string;
        template_name?: string;
        category_slug?: string;
        asset_type?: string;
        thumbnail_url?: string;
    };
}

interface PersonalizationProfile {
    preferred_countries?: string[];
    preferred_categories?: string[];
    last_destination?: string;
    last_budget?: number | string;
    conversation_stage?: string;
}

interface ChatProcessingMetadata {
    personalization_profile?: PersonalizationProfile;
    gathered_info_snapshot?: Record<string, unknown>;
}

type MediaMode = "chat" | "image" | "video";
type SessionTab = "flyers" | "videos" | "deals" | "market_research" | "my_assets" | "ai_itinerary" | "ai_slide";

// FlyerTemplate = AnyTemplateItem — kept as an alias so internal usage doesn't need renaming
type FlyerTemplate = AnyTemplateItem;

/** Tab metadata from GET /rag/chat-tabs, /rag/templates, /rag/my-assets */
interface ChatTab {
    id: string;
    label: string;
    icon: string;
    asset_type?: string | null;
    kind?: string;
    media_mode?: string;
    header_title?: string | null;
    header_subtitle?: string | null;
}

type TemplatesListResponse = TemplatesListApiResponse;

interface MyAssetsListResponse {
    active_tab: string;
    tabs: ChatTab[];
    assets: UserAsset[];
    total: number;
    has_more?: boolean;
    skip?: number;
    limit?: number;
    header_title?: string | null;
    header_subtitle?: string | null;
}

interface ChatTabsResponse {
    tabs: ChatTab[];
}

interface GenerateMediaApiResponse {
    image_url: string;
    caption: string;
    prompt_used: string;
    media_type: "image" | "video";
    session_id: string;
    s3_key?: string | null;
    video_url?: string | null;
    html_url?: string | null;
    is_preview?: boolean;
    needs_followup?: boolean;
    asset_type?: "flyer" | "video" | "itinerary" | "slides";
    thumbnail_url?: string | null;
    suggestions?: string[];
    quota_warning?: string | null;
    quota_remaining?: number | null;
    quota_limit?: number | null;
    quota_used?: number | null;
    style_options?: Array<{
        name: string;
        video_url?: string | null;
        font_family?: string | null;
        brand_color?: string | null;
        overlay_style?: string | null;
    }>;
}

interface PendingTemplateGeneration {
    templateId: string;
    customPrompt: string;
    assetType: FlyerTemplate["asset_type"];
    mediaType: "image" | "video";
    categoryLabel: string;
}

interface UserAsset {
    session_id: string;
    type: "image" | "video" | "itinerary" | "slides";
    asset_type: "flyer" | "video" | "itinerary" | "slides";
    url: string;
    html_url?: string | null;
    thumbnail_url?: string | null;
    caption: string;
    s3_key?: string | null;
    timestamp?: string | null;
}

// ─── API helpers ─────────────────────────────────────────────────────────────

const authHeaders = () => {
    const token = authStorage.getToken();
    return {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
};

const authHeadersMultipart = (): Record<string, string> => {
    const token = authStorage.getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
};

/** Flyer + upload button — Flyers tab (no template), or Videos tab with a template selected
 *  (lets the seller add their own photos for the video). Not used for itinerary/slides. */
const showFlyerMediaAttach = (tab: SessionTab, hasTemplate: boolean) =>
    (tab === "flyers" && !hasTemplate) || (tab === "videos" && hasTemplate);

const apiBase = env.apiBaseUrl;

// ─── Markdown rendering for chat bubbles — maps to the .md-* classes in RagChatPage.styles.css ──
const chatMarkdownComponents: Record<string, React.ElementType> = {
    p: ({ ...props }) => <p className="md-p" {...props} />,
    strong: ({ ...props }) => <strong className="md-strong" {...props} />,
    em: ({ ...props }) => <em className="md-em" {...props} />,
    del: ({ ...props }) => <del className="md-del" {...props} />,
    h1: ({ ...props }) => <h1 className="md-h1" {...props} />,
    h2: ({ ...props }) => <h2 className="md-h2" {...props} />,
    h3: ({ ...props }) => <h3 className="md-h3" {...props} />,
    blockquote: ({ ...props }) => <blockquote className="md-blockquote" {...props} />,
    hr: ({ ...props }) => <hr className="md-hr" {...props} />,
    ul: ({ ...props }) => <ul className="md-ul" {...props} />,
    ol: ({ ...props }) => <ol className="md-ol" {...props} />,
    li: ({ ...props }) => <li className="md-li" {...props} />,
    table: ({ ...props }) => <table className="md-table" {...props} />,
    th: ({ ...props }) => <th className="md-th" {...props} />,
    td: ({ ...props }) => <td className="md-td" {...props} />,
};

// ─── Persist confirmation messages in localStorage so they survive session switches ──

interface PersistedMsg { id: string; content: string; timestamp: string; }

// Template video flow's scene-review style picker has no rendered video per
// option (CSS-only preview, no render cost — see ethereal-booping-crescent.md
// plan) — this builds a small SVG thumbnail from the preset's real
// font/color/style so the DealCard carousel still has something to show.
// The free-form flow's options keep a real `video_url` and never hit this path.
function buildStyleCardImage(opt: { name: string; font_family?: string | null; brand_color?: string | null; overlay_style?: string | null }): string {
    const color = opt.brand_color || "#ffffff";
    const font = (opt.font_family || "sans-serif").replace(/["<>&]/g, "");
    const weight = opt.overlay_style === "bold" ? "800" : opt.overlay_style === "elegant" ? "400" : "600";
    const safeName = opt.name.replace(/[<>&]/g, "");
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="320" height="180">`
        + `<rect width="100%" height="100%" fill="#0f172a"/>`
        + `<text x="50%" y="42%" dominant-baseline="middle" text-anchor="middle" `
        + `font-family="${font}, sans-serif" font-size="26" font-weight="${weight}" fill="${color}">Sample Text</text>`
        + `<text x="50%" y="78%" dominant-baseline="middle" text-anchor="middle" `
        + `font-family="sans-serif" font-size="13" fill="#94a3b8">${safeName}</text>`
        + `</svg>`;
    return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

function _confirmKey(sessionId: string) { return `rag_confirms_${sessionId}`; }


function loadConfirmMsgs(sessionId: string): PersistedMsg[] {
    try {
        return JSON.parse(localStorage.getItem(_confirmKey(sessionId)) ?? "[]");
    } catch { return []; }
}

// ─── Persist cross-sell sections in localStorage so they survive session switches ──
type PersistedCrossSell = Array<{ category: string; label: string; deals: Deal[] }>;
type CrossSellStore = { ts: string; sections: PersistedCrossSell };
function _crossSellKey(sessionId: string) { return `rag_crosssell_${sessionId}`; }
function saveCrossSell(sessionId: string, sections: PersistedCrossSell, msgTs: Date) {
    try { localStorage.setItem(_crossSellKey(sessionId), JSON.stringify({ ts: msgTs.toISOString(), sections })); } catch { /* noop */ }
}
function loadCrossSell(sessionId: string): CrossSellStore | null {
    try {
        const raw = localStorage.getItem(_crossSellKey(sessionId));
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        // Handle legacy format (plain array)
        if (Array.isArray(parsed)) return { ts: "", sections: parsed };
        return parsed as CrossSellStore;
    } catch { return null; }
}

async function apiListSessions(skip = 0, limit = 20): Promise<{ sessions: Session[]; has_more: boolean }> {
    const res = await fetch(`${apiBase}/rag/sessions?skip=${skip}&limit=${limit}`, { headers: authHeaders() });
    if (res.status === 401) {
        const err = new Error("Unauthorized") as Error & { status: number };
        err.status = 401;
        throw err;
    }
    if (!res.ok) throw new Error("Failed to load sessions");
    const data = await res.json();
    // Backend tags fresh_rag sessions as "rag-v2"; normalize to "fresh_rag" so
    // restore/delete route correctly. /rag/sessions get+delete already handle both collections.
    const sessions = (data.sessions ?? []).map((s: Record<string, unknown>) =>
        (s.source as string) === "rag-v2" ? { ...s, source: "fresh_rag" as const } : s as unknown as Session
    );
    return { sessions, has_more: data.has_more ?? false };
}

function mirrorDealMediaFields(item: Deal): Deal {
    const media = item.media;
    const image =
        item.image_url ||
        item.image ||
        item.image_clean ||
        media?.image_clean ||
        media?.image ||
        undefined;
    const videoUrl = item.video_url || media?.video_url || undefined;
    return {
        ...item,
        media: media ?? item.media,
        image: image || item.image,
        image_url: image || item.image_url,
        image_clean: item.image_clean || media?.image_clean || undefined,
        video_url: videoUrl || item.video_url,
        pdf_url: item.pdf_url || media?.pdf_url || undefined,
    };
}

function mapApiDeal(raw: Partial<Deal> | null | undefined): Deal {
    return mirrorDealMediaFields({
        id: raw?.id ?? raw?.deal_id ?? "",
        deal_id: raw?.deal_id ?? raw?.id,
        title: raw?.title ?? "Untitled",
        description: raw?.description,
        item_type: raw?.item_type,
        category: raw?.category,
        category_group: raw?.category_group,
        categories: raw?.categories,
        media: raw?.media,
        image: raw?.image,
        image_url: raw?.image_url,
        image_clean: raw?.image_clean,
        video_url: raw?.video_url,
        pdf_url: raw?.pdf_url,
        tags: Array.isArray(raw?.tags) ? raw.tags.filter(Boolean) : [],
        star_rating: raw?.star_rating,
        pricing: raw?.pricing,
        destination: raw?.destination,
        validity: raw?.validity,
        package: raw?.package,
        dealer_id: raw?.dealer_id,
        dealer_name: raw?.dealer_name,
        is_featured: raw?.is_featured,
        is_verified: raw?.is_verified,
        is_active: raw?.is_active,
        is_boosted: raw?.is_boosted,
        boost_count: raw?.boost_count,
        boost_campaign_id: raw?.boost_campaign_id,
        seller_name: raw?.seller_name,
        seller_whatsapp: raw?.seller_whatsapp,
        seller_email: raw?.seller_email,
        verified_name: raw?.verified_name,
    });
}

function isVideoMediaUrl(url?: string): boolean {
    if (!url) return false;
    const clean = url.split("?")[0].toLowerCase();
    return clean.endsWith(".mp4") || clean.endsWith(".webm") || clean.endsWith(".mov") || clean.endsWith(".m4v");
}

function isPptxMediaUrl(url?: string): boolean {
    if (!url) return false;
    return url.split("?")[0].toLowerCase().endsWith(".pptx");
}

function isPdfMediaUrl(url?: string): boolean {
    if (!url) return false;
    return url.split("?")[0].toLowerCase().endsWith(".pdf");
}

function isHtmlMediaUrl(url?: string): boolean {
    if (!url) return false;
    return url.split("?")[0].toLowerCase().endsWith(".html");
}

const GENERATED_MEDIA_CATEGORIES = new Set(["Flyer", "Video", "Itinerary", "Slides"]);

function assetTypeToCategory(assetType?: string): string {
    switch (assetType) {
        case "video": return "Video";
        case "itinerary": return "Itinerary";
        case "slides": return "Slides";
        default: return "Flyer";
    }
}

function mediaBadgeMeta(category: string): { label: string; emoji: string; bg: string; color: string; border: string } {
    switch (category) {
        case "Video":
            return { label: "Video", emoji: "🎬", bg: "rgba(99,102,241,0.2)", color: "#818cf8", border: "rgba(99,102,241,0.3)" };
        case "Itinerary":
            return { label: "Itinerary", emoji: "🗺️", bg: "rgba(52,211,153,0.2)", color: "#6ee7b7", border: "rgba(52,211,153,0.25)" };
        case "Slides":
            return { label: "Slides", emoji: "📊", bg: "rgba(251,191,36,0.2)", color: "#fcd34d", border: "rgba(251,191,36,0.25)" };
        default:
            return { label: "Flyer", emoji: "🖼️", bg: "rgba(236,72,153,0.18)", color: "#f472b6", border: "rgba(236,72,153,0.25)" };
    }
}

function isGeneratedMediaDeal(deal: Deal): boolean {
    return GENERATED_MEDIA_CATEGORIES.has(deal.category ?? "");
}

async function apiGetSession(sessionId: string): Promise<SessionResponse> {
    const res = await fetch(`${apiBase}/rag/sessions/${sessionId}`, { headers: authHeaders() });
    if (!res.ok) throw new Error("Failed to load session");
    return res.json();
}

async function apiDeleteSession(sessionId: string): Promise<void> {
    const res = await fetch(`${apiBase}/rag/sessions/${sessionId}`, { method: "DELETE", headers: authHeaders() });
    if (!res.ok) throw new Error("Failed to delete session");
}

async function apiFreshRagGetSession(sessionId: string): Promise<SessionResponse> {
    const res = await fetch(`${apiBase}/rag-v2/sessions/${sessionId}`, { headers: authHeaders() });
    if (!res.ok) throw new Error("Failed to load session");
    return res.json();
}

async function apiFreshRagDeleteSession(sessionId: string): Promise<void> {
    const res = await fetch(`${apiBase}/rag-v2/sessions/${sessionId}`, { method: "DELETE", headers: authHeaders() });
    if (!res.ok) throw new Error("Failed to delete session");
}

async function streamFreshRagChat(
    message: string,
    sessionId: string | undefined,
    selectedTab: string,
    onSession: (sid: string) => void,
    onToken: (text: string) => void,
    onMetadata: (data: Record<string, unknown>) => void,
    onStage: (stage: string, label: string) => void,
): Promise<void> {
    const res = await fetch(`${apiBase}/rag-v2/chat`, {
        method: "POST",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({
            message,
            session_id: sessionId ?? null,
            selected_tab: selectedTab,
            offset: 0,
        }),
    });
    if (!res.ok || !res.body) {
        const errText = await res.text().catch(() => "Fresh RAG request failed");
        throw new Error(errText);
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buf = "";
    while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const frames = buf.split("\n\n");
        buf = frames.pop() ?? "";
        for (const frame of frames) {
            if (!frame.trim()) continue;
            const lines = frame.split("\n");
            let eventType = "";
            let dataStr = "";
            for (const line of lines) {
                if (line.startsWith("event: ")) eventType = line.slice(7).trim();
                else if (line.startsWith("data: ")) dataStr = line.slice(6).trim();
            }
            if (!eventType || !dataStr) continue;
            try {
                const data = JSON.parse(dataStr) as Record<string, unknown>;
                if (eventType === "session") onSession(data.session_id as string);
                else if (eventType === "token") onToken((data.text as string) ?? "");
                else if (eventType === "metadata") onMetadata(data);
                else if (eventType === "stage") onStage((data.stage as string) ?? "", (data.label as string) ?? "");
                else if (eventType === "error") {
                    const err = new Error((data.message as string) ?? "Stream error") as Error & { code?: string };
                    err.code = (data.code as string | undefined);
                    throw err;
                }
            } catch (e) {
                if (eventType === "error") throw e;
            }
        }
    }
}

// Perplexity Sonar embeds inline reference markers ([1], [2,3], [4-6]) tied to a
// citations list we never fetch or show — same cleanup as the backend's
// _strip_citation_markers (backend/app/modules/rag/market_research/routes.py),
// re-applied here so the mid-stream partial text never flashes a raw marker.
function stripCitationMarkers(text: string): string {
    return text
        .replace(/\s?\[\d+(?:\s*[-,]\s*\d+)*\]/g, "")
        .replace(/[ \t]+([.,!?;:])/g, "$1")
        .replace(/[ \t]{2,}/g, " ");
}

async function streamMarketResearchChat(
    message: string,
    sessionId: string | undefined,
    onSession: (sid: string) => void,
    onToken: (text: string) => void,
    onStage: (stage: string, label: string) => void,
): Promise<void> {
    const res = await fetch(`${apiBase}/rag/market-research`, {
        method: "POST",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ message, session_id: sessionId ?? null }),
    });
    if (!res.ok || !res.body) {
        const errText = await res.text().catch(() => "Market research request failed");
        throw new Error(errText);
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buf = "";
    while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const frames = buf.split("\n\n");
        buf = frames.pop() ?? "";
        for (const frame of frames) {
            if (!frame.trim()) continue;
            const lines = frame.split("\n");
            let eventType = "";
            let dataStr = "";
            for (const line of lines) {
                if (line.startsWith("event: ")) eventType = line.slice(7).trim();
                else if (line.startsWith("data: ")) dataStr = line.slice(6).trim();
            }
            if (!eventType || !dataStr) continue;
            try {
                const data = JSON.parse(dataStr) as Record<string, unknown>;
                if (eventType === "session") onSession(data.session_id as string);
                else if (eventType === "token") onToken((data.text as string) ?? "");
                else if (eventType === "stage") onStage((data.stage as string) ?? "", (data.label as string) ?? "");
                else if (eventType === "error") throw new Error((data.message as string) ?? "Stream error");
            } catch (e) {
                if (eventType === "error") throw e;
            }
        }
    }
}


// Agentic chat API — replaces old semantic-search for buyer chat UI
async function apiChat(
    message: string,
    sessionId?: string,
    conversationHistory: { role: string; content: string }[] = [],
    categoryFilter?: string,
    selectedTab?: SessionTab,
    options?: { offset?: number; sortBy?: string; pageSize?: number }
) {
    // Market Research runs on its own standalone endpoint/module — see
    // backend/app/modules/rag/market_research/routes.py. Not part of fresh_rag,
    // not the (deleted) legacy V3-agent /rag/chat endpoint. It's SSE-streaming
    // only now (see streamMarketResearchChat) — the send-handler always takes
    // that path for selectedTab === "market_research" before ever reaching this
    // function, so this plain POST/JSON call is never routed there.
    const res = await fetch(`${apiBase}/rag/chat`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({
            message,
            session_id: sessionId ?? null,
            conversation_history: conversationHistory,
            category_filter: categoryFilter ?? null,
            selected_tab: selectedTab ?? null,
            offset: options?.offset ?? 0,
            page_size: options?.pageSize ?? 10,
            sort_by: options?.sortBy ?? "relevance",
        }),
    });
    if (!res.ok) throw new Error("Chat failed");
    return res.json();
}

// Create a lead (custom enquiry) directly from the Send Enquiry modal.
//
// Either `deal_id` (deal-specific enquiry from a Deal card) OR `dealer_id`
// (DMC-direct enquiry from a DmcCard) must be set, never both. Backend
// (routes.py::CreateLeadRequest) enforces exactly-one-of and rejects with
// a clear 400 if both are sent.
async function apiCreateLead(payload: {
    session_id: string;
    deal_id?: string;
    dealer_id?: string;
    destination?: string | string[];
    trip_type?: string;
    pax?: number;
    duration?: string;
    travel_dates?: string;
    budget?: string;
    message?: string;
    gathered_info?: Record<string, unknown>;
    campaign_id?: string;
}): Promise<{ lead_id: string; status: string; created_at: string }> {
    const res = await fetch(`${apiBase}/rag/leads`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify(payload),
    });
    if (!res.ok) {
        let msg = res.statusText;
        try {
            const body = await res.json();
            const detail = body?.detail;
            if (detail && typeof detail === "object" && typeof detail.message === "string") {
                msg = detail.message;
            } else if (typeof detail === "string") {
                msg = detail;
            }
        } catch { /* ignore */ }
        throw new Error(msg || "We are experiencing high volume right now. Please try again in a moment.");
    }
    return res.json();
}


const TAB_LABELS: Record<SessionTab, string> = {
    flyers: "Flyers",
    videos: "Videos",
    deals: "Deals",
    market_research: "Market Research",
    my_assets: "My Assets",
    ai_itinerary: "AI Itinerary",
    ai_slide: "AI Slide",
};

function templateTabForAsset(assetType: string): SessionTab {
    if (assetType === "video") return "videos";
    if (assetType === "itinerary") return "ai_itinerary";
    if (assetType === "slides") return "ai_slide";
    return "flyers";
}

function tabToMediaMode(tab: SessionTab): MediaMode {
    if (tab === "flyers" || tab === "ai_itinerary" || tab === "ai_slide") return "image";
    if (tab === "videos") return "video";
    return "chat";
}


function tabToCategoryFilter(tab: SessionTab): string | undefined {
    if (tab === "market_research") return "market_research";
    if (tab === "ai_itinerary") return "ai_itinerary";
    if (tab === "deals") return "Deals";
    return undefined;
}

function buildPersonalizedSuggestions(profile?: PersonalizationProfile): string[] {
    if (!profile) return [];
    const chips: string[] = [];

    if (profile.last_destination) {
        chips.push(`Show latest packages for ${profile.last_destination}`);
        chips.push(`Budget options in ${profile.last_destination}`);
    }
    if (profile.last_budget !== undefined && profile.last_budget !== null && profile.last_budget !== "") {
        chips.push(`Packages around ₹${profile.last_budget}`);
    }
    if (profile.preferred_categories?.length) {
        chips.push(`Show ${profile.preferred_categories[0]} deals`);
    }
    if (profile.preferred_countries?.length) {
        chips.push(`Top deals for ${profile.preferred_countries[0]}`);
    }

    const unique: string[] = [];
    const seen = new Set<string>();
    for (const chip of chips) {
        const key = chip.toLowerCase();
        if (!seen.has(key)) {
            seen.add(key);
            unique.push(chip);
        }
    }
    return unique.slice(0, 6);
}

/** Extract message + backend suggestions from a non-ok API response.
 *  Also surfaces the global upgrade prompt when the backend reports a quota
 *  limit (HTTP 429 / daily_/monthly_quota_exceeded) — the RAG streaming flows
 *  use raw fetch, so they bypass the axios interceptor that normally fires this. */
const apiErrorResponse = async (
    res: Response,
    fallbackMessage?: string,
): Promise<FetchApiError> => {
    const parsed = await parseFetchApiError(res, fallbackMessage);
    const isQuota =
        res.status === 429 ||
        parsed.isDailyQuotaExceeded ||
        (parsed.code ? /QUOTA/i.test(parsed.code) : false);
    if (isQuota) {
        emitQuotaExceeded({
            code: parsed.code ?? "quota_exceeded",
            message: parsed.message,
        });
    }
    return parsed;
};

/** Backwards-compat wrapper used by non-template call sites. */
const apiErrorMessage = fetchApiErrorMessage;

/** Fire the global upgrade prompt from a streaming "error" event payload when it
 *  represents a quota limit (the SSE stream bypasses the axios interceptor). */
const maybeEmitQuotaFromStream = (
    detail: Record<string, unknown> | undefined,
    fallbackMessage: string,
): void => {
    const rawCode =
        (typeof detail?.code === "string" && detail.code) ||
        (typeof detail?.error === "string" && detail.error) ||
        "";
    const message =
        (typeof detail?.message === "string" && detail.message) || fallbackMessage;
    if (rawCode ? /quota/i.test(rawCode) : /quota|limit/i.test(message)) {
        emitQuotaExceeded({ code: rawCode || "quota_exceeded", message });
    }
};

// Agentic Media generation API — currently not wired into the unified chat UI.
// Kept here because the /rag/generate-media endpoint is still used by the dedicated
// flyer/video generation page. If you remove that page too, delete this function.
// @ts-ignore - preserved for the standalone media page
// eslint-disable-next-line @typescript-eslint/no-unused-vars
async function apiGenerateMedia(
    prompt: string,
    mediaType: "image" | "video",
    sessionId?: string,
    selectedTab?: SessionTab,
    previewOnly = false,
    inputImageUrl?: string | null,
    uploadedFlyerS3Key?: string | null,
): Promise<GenerateMediaApiResponse> {
    const res = await fetch(`${apiBase}/rag/generate-media`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({
            prompt,
            media_type: mediaType,
            session_id: sessionId ?? null,
            selected_tab: selectedTab ?? null,
            preview_only: previewOnly,
            ...(inputImageUrl ? { input_image_url: inputImageUrl } : {}),
            ...(uploadedFlyerS3Key ? { uploaded_flyer_s3_key: uploadedFlyerS3Key } : {}),
        }),
    });
    if (!res.ok) throw new Error(await apiErrorMessage(res, "Media generation failed"));
    return res.json();
}


// ─── Deal cards rail (horizontal carousel) ─────────────────────────────────

const DealsCarousel: React.FC<{
    deals: Deal[];
    onInterest?: (deal: Deal) => void;
}> = ({ deals, onInterest }) => {
    const trackRef = useRef<HTMLDivElement>(null);
    if (!deals.length) return null;

    const scrollBy = (dir: -1 | 1) => {
        const el = trackRef.current;
        if (!el) return;
        const step = Math.min(320, el.clientWidth * 0.85);
        el.scrollBy({ left: dir * step, behavior: "smooth" });
    };

    return (
        <div className="deals-rail">
            {deals.length > 1 && (
                <button
                    type="button"
                    className="deals-rail-nav deals-rail-nav--prev"
                    onClick={() => scrollBy(-1)}
                    aria-label="Previous deals"
                >
                    ‹
                </button>
            )}
            <div className="deals-rail-track" ref={trackRef}>
                {deals.map((deal, i) => (
                    <DealCard
                        key={deal.id ?? deal.deal_id ?? i}
                        deal={deal}
                        index={i}
                        onInterest={onInterest}
                    />
                ))}
            </div>
            {deals.length > 1 && (
                <button
                    type="button"
                    className="deals-rail-nav deals-rail-nav--next"
                    onClick={() => scrollBy(1)}
                    aria-label="Next deals"
                >
                    ›
                </button>
            )}
        </div>
    );
};

// ─── Boost analytics (search surface) ─────────────────────────────────────────
// The chat is the "search" surface. Mirrors DealsPage's browse-surface tracking so
// boosted deals shown in chat actually report impressions/clicks back to the
// campaign (otherwise the seller's analytics stay at 0). One session id per page
// load; impressions deduped per deal so a re-render doesn't double-count.
const boostSearchSessionId =
    typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random()}`;
const firedBoostImpressions = new Set<string>();

/** The live RAG session id. The backend's sponsored-slot auction stashes each
 *  winner's price under THIS id, and bills the impression by looking it up — so
 *  the event must carry the same id or billing falls back to the seller's full
 *  bid instead of the (cheaper) second-price the auction settled on. */
let currentRagSessionId = "";
export function setBoostRagSessionId(id: string): void {
    currentRagSessionId = id || "";
}
function boostSessionId(): string {
    return currentRagSessionId || boostSearchSessionId;
}

function boostDealId(deal: Deal): string {
    return deal.deal_id ?? deal.id ?? "";
}

function fireBoostImpression(deal: Deal, position: number): void {
    const did = boostDealId(deal);
    // eslint-disable-next-line no-console
    console.log("[boost] impression check", {
        did, is_boosted: deal.is_boosted, campaign_id: deal.boost_campaign_id, position,
    });
    if (!deal.is_boosted || !deal.boost_campaign_id) {
        // eslint-disable-next-line no-console
        console.warn("[boost] impression SKIP — missing is_boosted or campaign_id", did);
        return;
    }
    if (!did || firedBoostImpressions.has(did)) {
        // eslint-disable-next-line no-console
        console.log("[boost] impression SKIP — already fired this session", did);
        return;
    }
    firedBoostImpressions.add(did);
    void boostService
        .recordEvent({
            event_id: crypto.randomUUID(),
            campaign_id: deal.boost_campaign_id,
            deal_id: did,
            event_type: "impression",
            surface: "search",
            position,
            session_id: boostSessionId(),
        })
        // eslint-disable-next-line no-console
        .then(() => console.log("[boost] impression SENT ✓", did))
        // eslint-disable-next-line no-console
        .catch((e) => console.error("[boost] impression FAILED ✗", did, e?.response?.status, e?.response?.data, e));
}

function fireBoostClick(deal: Deal, position = 1): void {
    const did = boostDealId(deal);
    // eslint-disable-next-line no-console
    console.log("[boost] click check", { did, is_boosted: deal.is_boosted, campaign_id: deal.boost_campaign_id });
    if (!deal.is_boosted || !deal.boost_campaign_id) return;
    if (!did) return;
    void boostService
        .recordEvent({
            event_id: crypto.randomUUID(),
            campaign_id: deal.boost_campaign_id,
            deal_id: did,
            event_type: "click",
            surface: "search",
            position,
            session_id: boostSessionId(),
        })
        // eslint-disable-next-line no-console
        .then(() => console.log("[boost] click SENT ✓", did))
        // eslint-disable-next-line no-console
        .catch((e) => console.error("[boost] click FAILED ✗", did, e?.response?.status, e?.response?.data, e));
    // Feed the campaign conversion funnel (Views) — tagged by campaign_id.
    const sid = (deal as { dealer?: { id?: string } }).dealer?.id ?? deal.dealer_id ?? "";
    trackingService.dealView(did, sid, { campaignId: deal.boost_campaign_id });
}

// ─── Deal Card ───────────────────────────────────────────────────────────────
// Contact info is hidden. Send Enquiry opens the enquiry modal.

const DealCard: React.FC<{ deal: Deal; index: number; onInterest?: (deal: Deal) => void }> = ({ deal, index, onInterest }) => {
    // Report a search-surface impression once when a boosted card mounts.
    useEffect(() => {
        fireBoostImpression(deal, index + 1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deal.deal_id, deal.is_boosted, deal.boost_campaign_id]);

    const typeIcon: Record<string, string> = {
        DEAL: "🤝",
        PACKAGE: "🧳",
        FLIGHT: "✈️",
        HOTEL: "🏨",
        AI_HOTEL: "🏨",
        CRUISE: "🛳️",
        TRANSFER: "🚐",
        NIGHTLIFE: "🌃",
        SERVICE: "🛎️",
        FLYER_VIDEO: "🎬",
        AI_CONTACT: "👤",
        AI_WEBINAR: "📹",
        AI_SHOWCASE: "✨",
    };
    const poster =
        deal.image_url ||
        deal.media?.image_clean ||
        deal.media?.image ||
        deal.image_clean ||
        deal.image;
    const videoSrc = deal.video_url || deal.media?.video_url || undefined;
    const isVideoDeal =
        Boolean(videoSrc) ||
        deal.item_type === "FLYER_VIDEO" ||
        deal.categories?.primary_slug === "flyer_video" ||
        isVideoMediaUrl(videoSrc ?? undefined) ||
        isVideoMediaUrl(poster);
    const summaryText = deal.description || deal.package?.highlights?.[0];
    const categoryLabel =
        deal.categories?.primary_slug?.replace(/_/g, " ") ||
        deal.category ||
        deal.item_type ||
        "Travel";
    const [imgFailed, setImgFailed] = useState(false);

    return (
        <div className="deal-card deal-card--rail">
            <div className="deal-card-img">
                {isVideoDeal && videoSrc ? (
                    <video
                        src={videoSrc}
                        poster={poster && !isVideoMediaUrl(poster) ? poster : undefined}
                        controls
                        playsInline
                        preload="metadata"
                    />
                ) : poster && !imgFailed ? (
                    <img
                        src={poster}
                        alt={deal.title}
                        loading="lazy"
                        onError={() => setImgFailed(true)}
                    />
                ) : (
                    <span className="deal-card-icon">{typeIcon[deal.item_type ?? ""] ?? "🌍"}</span>
                )}
                {deal.item_type && <span className="deal-card-badge">{deal.item_type}</span>}
                <span className="deal-card-num">#{index + 1}</span>
                {deal.is_featured && <span className="deal-card-featured">⭐ Featured</span>}
                {deal.is_boosted && <span className="deal-card-boosted">Featured</span>}
            </div>
            <div className="deal-card-body">
                <p className="deal-card-category">{categoryLabel}</p>
                <h3 className="deal-card-title">{deal.title}</h3>
                {deal.destination && <p className="deal-card-dest">📍 {[...(deal.destination.cities ?? []), ...(deal.destination.countries ?? [])].join(", ")}</p>}
                {deal.package?.duration_nights && <p className="deal-card-duration">🕐 {deal.package.duration_nights}N</p>}
                {summaryText && (
                    <p className="deal-card-desc">{summaryText}</p>
                )}
                <div className="deal-card-price-row">
                    {deal.pricing?.discounted_price ? (
                        <>
                            <span className="deal-card-price">{currencySymbol(deal.pricing.currency)}{deal.pricing.discounted_price.toLocaleString("en-IN")}</span>
                            <span className="deal-card-unit"> per person</span>
                            {deal.pricing.original_price && deal.pricing.discounted_price < deal.pricing.original_price && (
                                <span className="deal-card-orig">{currencySymbol(deal.pricing.currency)}{deal.pricing.original_price.toLocaleString("en-IN")}</span>
                            )}
                        </>
                    ) : deal.pricing?.original_price ? (
                        <span className="deal-card-price">{currencySymbol(deal.pricing.currency)}{deal.pricing.original_price.toLocaleString("en-IN")}</span>
                    ) : (
                        <span className="deal-card-contact">Contact for pricing</span>
                    )}
                </div>
                {/* ── Send enquiry — opens a modal to capture trip details as a Lead ──
                     News cards have nothing to enquire about, so the button is suppressed there. */}
                {onInterest && deal.item_type !== "NEWS" && (
                    <button
                        className="deal-card-interest-btn"
                        onClick={() => onInterest(deal)}
                    >
                        Send Enquiry
                    </button>
                )}
            </div>
        </div>
    );
};

// ─── CTA Panel (WhatsApp + Email) — shown in INTERESTED/CONNECT stage ─────────

const CTAPanel: React.FC<{ deal: Deal; sessionId?: string }> = ({ deal, sessionId }) => {
    if (!deal.seller_whatsapp && !deal.seller_email) return null;
    const highlights = deal.package?.highlights ?? [];
    const inclusions = deal.package?.inclusions ?? [];
    const exclusions = deal.package?.exclusions ?? [];

    const handleContactClick = (channel: "whatsapp" | "email", destinationUrl: string) => {
        void analyticsService.trackContactClick({
            channel,
            source: "rag_chat",
            item_id: deal.deal_id ?? deal.id,
            item_type: deal.item_type ?? "DEAL",
            item_title: deal.title,
            seller_id: deal.dealer_id,
            seller_name: deal.seller_name,
            destination_url: destinationUrl,
            session_id: sessionId,
            metadata: {
                category: deal.categories?.primary_slug ?? deal.category,
                category_group: deal.categories?.group_slug ?? deal.category_group,
            },
        });
    };

    return (
        <div className="cta-panel">
            <div className="cta-panel-header">
                <span className="cta-panel-icon">📞</span>
                <div>
                    <p className="cta-panel-title">
                        Connect with {deal.seller_name || "the seller"}
                        {deal.is_verified && (
                            <span title={deal.verified_name || "Gullivr Verified Seller"} aria-hidden style={{ marginLeft: 4 }}>✔️</span>
                        )}
                    </p>
                    <p className="cta-panel-subtitle">{deal.title}</p>
                </div>
            </div>
            <div className="cta-panel-meta">
                {deal.destination && <span>📍 {[...(deal.destination.cities ?? []), ...(deal.destination.countries ?? [])].join(", ")}</span>}
                {deal.package?.duration_nights && <span>🕐 {deal.package.duration_nights}N</span>}
                {(deal.pricing?.discounted_price ?? deal.pricing?.original_price) != null && (
                    <span>💰 {currencySymbol(deal.pricing!.currency)}{((deal.pricing!.discounted_price ?? deal.pricing!.original_price)!).toLocaleString("en-IN")} per person</span>
                )}
            </div>
            {deal.description && (
                <p className="cta-panel-description">{deal.description}</p>
            )}
            {highlights.length > 0 && (
                <div className="cta-panel-section">
                    <p className="cta-panel-section-title">Highlights</p>
                    <ul className="cta-panel-list">
                        {highlights.slice(0, 5).map((item) => (
                            <li key={item}>{item}</li>
                        ))}
                    </ul>
                </div>
            )}
            {inclusions.length > 0 && (
                <div className="cta-panel-section">
                    <p className="cta-panel-section-title">Inclusions</p>
                    <ul className="cta-panel-list">
                        {inclusions.slice(0, 6).map((item) => (
                            <li key={item}>{item}</li>
                        ))}
                    </ul>
                </div>
            )}
            {exclusions.length > 0 && (
                <div className="cta-panel-section">
                    <p className="cta-panel-section-title">Exclusions</p>
                    <ul className="cta-panel-list">
                        {exclusions.slice(0, 4).map((item) => (
                            <li key={item}>{item}</li>
                        ))}
                    </ul>
                </div>
            )}
            <div className="cta-buttons">
                {deal.seller_whatsapp && (
                    <a
                        href={`https://wa.me/${deal.seller_whatsapp}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => handleContactClick("whatsapp", `https://wa.me/${deal.seller_whatsapp}`)}
                        className="cta-btn cta-btn--whatsapp"
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" style={{ flexShrink: 0 }}>
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
                            <path d="M12 0C5.373 0 0 5.373 0 12c0 2.123.554 4.116 1.523 5.843L.057 23.927l6.265-1.439A11.95 11.95 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818c-1.926 0-3.72-.528-5.254-1.443l-.376-.223-3.9.895.947-3.77-.246-.39A9.818 9.818 0 012.182 12C2.182 6.57 6.57 2.182 12 2.182S21.818 6.57 21.818 12 17.43 21.818 12 21.818z" />
                        </svg>
                        Chat on WhatsApp
                    </a>
                )}
                {deal.seller_email && (
                    <a
                        href={`mailto:${deal.seller_email}`}
                        onClick={() => handleContactClick("email", `mailto:${deal.seller_email}`)}
                        className="cta-btn cta-btn--email"
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0 }}>
                            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                            <polyline points="22,6 12,13 2,6" />
                        </svg>
                        Send Email
                    </a>
                )}
            </div>
        </div>
    );
};


// ─── Enquiry Modal — opens when buyer clicks "Send Enquiry" on a deal card ────

interface GatheredHints {
    destination?: string | string[];
    trip_type?: string;
    pax?: number | string;
    duration?: string;
    duration_nights?: number;
    travel_date?: string;
    travel_dates?: string;
    budget?: string;
    category_filter?: string;
    original_intent?: string;
    check_in_date?: string;
    check_out_date?: string;
    departure_city?: string;
    /** Answers collected by the custom enquiry flow, keyed by field name */
    _enquiry_fields?: Record<string, string>;
}

// ─── Enquiry API helpers ──────────────────────────────────────────────────────


async function apiRefineEnquiry(enquiry_text: string, user_instruction?: string): Promise<string> {
    const res = await fetch(`${apiBase}/rag/leads/enquiry-refine`, {
        method: "POST",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ enquiry_text, user_instruction: user_instruction?.trim() || undefined }),
    });
    if (!res.ok) throw new Error(`Refine failed (${res.status})`);
    const data = await res.json();
    return (data?.refined_text as string) || "";
}

// ─── DMC Cards + DMC Enquiry Modal (Stage 3) ────────────────────────────────

/** Normalise whichever coverage field the backend provided into one display-ready list. */
function dmcCoverage(dmc: DmcCard): string[] {
    const parts = [
        ...(dmc.covered ?? []),
        ...(Array.isArray(dmc.destination)
            ? dmc.destination
            : dmc.destination ? [dmc.destination] : []),
        ...(dmc.destinations ?? []),
        ...(dmc.cities ?? []),
        ...(dmc.countries ?? []),
        ...(dmc.coverage_areas ?? []),
    ]
        .filter((x): x is string => typeof x === "string" && x.length > 0);
    // Dedupe while preserving order.
    return Array.from(new Set(parts));
}

const DmcCards: React.FC<{
    dmcs: DmcCard[];
    itineraryText: string;
    onEnquire: (dmc: DmcCard) => void;
}> = ({ dmcs, onEnquire }) => {
    if (!dmcs || dmcs.length === 0) return null;
    return (
        <div style={{ marginTop: 10 }}>
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "0 4px 6px",
                    fontSize: 10,
                    letterSpacing: 1,
                    color: "rgba(255,255,255,0.6)",
                    textTransform: "uppercase",
                }}
            >
                <span>{dmcs.length} recommended DMC{dmcs.length > 1 ? "s" : ""}</span>
                <span style={{ opacity: 0.55 }}>Tap to enquire</span>
            </div>
            <div
                className="deals-scroll"
                style={{ paddingBottom: 4 }}
            >
                {dmcs.map((dmc) => {
                    const covers = dmcCoverage(dmc);
                    const coversText = covers.slice(0, 3).join(", ") +
                        (covers.length > 3 ? ` +${covers.length - 3} more` : "");
                    const segs = (dmc.segments ?? dmc.tags ?? []).slice(0, 3);
                    const score = dmc.seller_match?.score ?? 0;
                    const initial = (dmc.seller_name?.[0] ?? "?").toUpperCase();
                    return (
                        <div
                            key={dmc.dealer_id}
                            className="deal-card"
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 8,
                                padding: 12,
                                minWidth: 230,
                                maxWidth: 230,
                                background: "rgba(255,255,255,0.04)",
                                borderRadius: 10,
                                border: "1px solid rgba(255,255,255,0.08)",
                            }}
                        >
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <div
                                    style={{
                                        width: 28,
                                        height: 28,
                                        borderRadius: "50%",
                                        background: "rgba(255,255,255,0.1)",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        fontWeight: 700,
                                        fontSize: 13,
                                        color: "#fff",
                                        flexShrink: 0,
                                    }}
                                >
                                    {initial}
                                </div>
                                <div style={{ overflow: "hidden", flex: 1 }}>
                                    <div
                                        style={{
                                            fontWeight: 600,
                                            fontSize: 13,
                                            color: "#fff",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {dmc.seller_name}
                                        {dmc.is_verified && (
                                            <span title={dmc.verified_name || "Gullivr Verified Seller"} aria-hidden style={{ marginLeft: 4 }}>✔️</span>
                                        )}
                                    </div>
                                    {score > 0 && (
                                        <div style={{ fontSize: 10, color: "rgba(255,255,255,0.45)" }}>
                                            Match {score.toFixed(2)}
                                        </div>
                                    )}
                                </div>
                            </div>
                            {coversText && (
                                <div
                                    style={{
                                        fontSize: 11,
                                        color: "rgba(255,255,255,0.7)",
                                        overflow: "hidden",
                                        display: "-webkit-box",
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: "vertical",
                                    }}
                                >
                                    Covers {coversText}
                                </div>
                            )}
                            {segs.length > 0 && (
                                <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                                    {segs.map((s) => (
                                        <span
                                            key={s}
                                            style={{
                                                fontSize: 9,
                                                padding: "2px 6px",
                                                borderRadius: 999,
                                                background: "rgba(255,255,255,0.08)",
                                                color: "rgba(255,255,255,0.75)",
                                                textTransform: "uppercase",
                                                letterSpacing: 0.5,
                                            }}
                                        >
                                            {s}
                                        </span>
                                    ))}
                                </div>
                            )}
                            <button
                                type="button"
                                onClick={() => onEnquire(dmc)}
                                disabled={!dmc.dealer_id}
                                style={{
                                    marginTop: "auto",
                                    padding: "8px 10px",
                                    fontSize: 11,
                                    fontWeight: 600,
                                    letterSpacing: 0.3,
                                    textTransform: "uppercase",
                                    borderRadius: 6,
                                    border: "none",
                                    background: dmc.dealer_id
                                        ? "#fff"
                                        : "rgba(255,255,255,0.1)",
                                    color: dmc.dealer_id
                                        ? "#000"
                                        : "rgba(255,255,255,0.4)",
                                    cursor: dmc.dealer_id ? "pointer" : "not-allowed",
                                }}
                            >
                                📝 Send Enquiry
                            </button>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

/**
 * Compact modal for sending an enquiry to a DMC (no specific deal).
 *
 * Structurally mirrors `EnquiryModal` but posts with `dealer_id` set and
 * leaves `deal_id` undefined so the backend routes the lead to this DMC
 * directly. Pre-fills the message with the generated itinerary text so the
 * buyer can review + customise before sending — matches the "here's your
 * plan; send it to a DMC" handoff the user described.
 */
const DmcEnquiryModal: React.FC<{
    dmc: DmcCard;
    itineraryText: string;
    sessionId: string;
    gatheredInfo?: GatheredHints;
    ownsSelf?: boolean;
    onClose: () => void;
    onSubmitted: (leadId: string) => void;
}> = ({ dmc, itineraryText, sessionId, gatheredInfo, ownsSelf, onClose, onSubmitted }) => {
    const [body, setBody] = useState<string>("");
    const [maxChars, setMaxChars] = useState<number>(8000);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string>(
        ownsSelf ? "You can't send an enquiry to yourself." : "",
    );
    const [showVerificationModal, setShowVerificationModal] = useState(false);

    useEffect(() => {
        void (async () => {
            setLoading(true);
            try {
                const res = await fetch(`${apiBase}/rag/leads/enquiry-draft`, {
                    method: "POST",
                    headers: { ...authHeaders(), "Content-Type": "application/json" },
                    body: JSON.stringify({
                        dealer_id: dmc.dealer_id,
                        session_id: sessionId || undefined,
                        itinerary_text: itineraryText || undefined,
                    }),
                });
                if (res.status === 403) {
                    const errBody = await res.json().catch(() => ({}));
                    const msg = errBody?.detail?.message || errBody?.detail || "Your account is pending verification. You cannot send enquiries until an admin verifies your account.";
                    setError(typeof msg === "string" ? msg : "Your account is not verified. Please contact support.");
                    setLoading(false);
                    return;
                }
                if (!res.ok) throw new Error(`Draft fetch failed (${res.status})`);
                const data = await res.json();
                setBody((data.draft_text as string) || "");
                setMaxChars((data.max_chars as number) || 8000);
            } catch {
                // fall through with empty body
            } finally {
                setLoading(false);
            }
        })();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const charCount = body.length;
    const overLimit = charCount > maxChars;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (ownsSelf) {
            setError("You can't send an enquiry to yourself.");
            return;
        }
        if (!body.trim()) {
            setError("Enquiry can't be empty.");
            return;
        }
        if (overLimit) {
            setError(`Too long — keep under ${maxChars} characters.`);
            return;
        }
        setError("");
        setSubmitting(true);
        try {
            const dest = (() => {
                if (gatheredInfo?.destination) {
                    return Array.isArray(gatheredInfo.destination)
                        ? gatheredInfo.destination
                        : gatheredInfo.destination;
                }
                const c = dmcCoverage(dmc);
                return c.length > 0 ? c : undefined;
            })();
            const paxRaw = gatheredInfo?.pax;
            const paxNum = typeof paxRaw === "number"
                ? paxRaw
                : typeof paxRaw === "string" && paxRaw.trim()
                    ? parseInt(paxRaw, 10)
                    : undefined;
            // DMC-direct enquiry — send `dealer_id`, NOT `deal_id`.
            // Backend (routes.py) enforces exactly-one-of.
            const result = await apiCreateLead({
                session_id: sessionId,
                dealer_id: dmc.dealer_id,
                destination: dest,
                trip_type: gatheredInfo?.trip_type,
                pax: Number.isFinite(paxNum as number) ? (paxNum as number) : undefined,
                duration: gatheredInfo?.duration,
                travel_dates: gatheredInfo?.travel_dates ?? gatheredInfo?.travel_date,
                budget: gatheredInfo?.budget,
                message: body,
                gathered_info: {
                    ...(gatheredInfo ?? {}),
                    dmc_name: dmc.seller_name,
                    dealer_id: dmc.dealer_id,
                },
            });
            onSubmitted(result.lead_id);
        } catch (err) {
            const msg = err instanceof Error ? err.message : "Failed to send enquiry. Please try again.";
            setError(msg);
            // Don't make the user hunt for a "Request Verification" link inside the
            // inline error — pop the modal straight away when the block IS a
            // verification gate (as opposed to any other failure, e.g. network).
            if (msg.toLowerCase().includes("pending admin verification")) {
                setShowVerificationModal(true);
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div
            style={{
                position: "fixed",
                inset: 0,
                background: "rgba(0,0,0,0.65)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1000,
                padding: "16px",
            }}
            onClick={onClose}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                style={{
                    width: "100%",
                    maxWidth: 560,
                    maxHeight: "90vh",
                    display: "flex",
                    flexDirection: "column",
                    background: "#18181b",
                    borderRadius: 12,
                    boxShadow: "0 20px 60px rgba(0,0,0,0.55)",
                    border: "1px solid rgba(255,255,255,0.08)",
                    overflow: "hidden",
                }}
            >
                <div
                    style={{
                        padding: "14px 18px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        borderBottom: "1px solid rgba(255,255,255,0.08)",
                    }}
                >
                    <div>
                        <div style={{ fontWeight: 600, fontSize: 16, color: "#fff" }}>
                            Send enquiry
                        </div>
                        <div
                            style={{
                                fontSize: 12,
                                color: "rgba(255,255,255,0.6)",
                                marginTop: 2,
                            }}
                        >
                            To {dmc.seller_name}
                            {(() => {
                                const c = dmcCoverage(dmc).slice(0, 2).join(", ");
                                return c ? ` · Covers ${c}` : "";
                            })()}
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={submitting}
                        style={{
                            border: "none",
                            background: "transparent",
                            color: "#fff",
                            fontSize: 20,
                            cursor: submitting ? "not-allowed" : "pointer",
                        }}
                    >
                        ×
                    </button>
                </div>
                <form
                    onSubmit={handleSubmit}
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        flex: 1,
                        padding: 16,
                        gap: 10,
                        minHeight: 0,
                    }}
                >
                    <textarea
                        value={body}
                        onChange={(e) => setBody(e.target.value)}
                        disabled={submitting || ownsSelf || loading}
                        placeholder={loading ? "Loading draft…" : undefined}
                        style={{
                            flex: 1,
                            minHeight: 240,
                            padding: 12,
                            fontSize: 13,
                            lineHeight: 1.5,
                            background: "#0e0e10",
                            border: `1px solid ${overLimit ? "#ef4444" : "rgba(255,255,255,0.1)"}`,
                            borderRadius: 8,
                            color: "#fff",
                            resize: "vertical",
                            fontFamily: "inherit",
                        }}
                    />
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            fontSize: 11,
                            color: overLimit ? "#ef4444" : "rgba(255,255,255,0.5)",
                        }}
                    >
                        <span>One tap, one enquiry — we never broadcast on your behalf.</span>
                        <span>{charCount}/{maxChars}</span>
                    </div>
                    {error && (
                        <div
                            style={{
                                padding: 10,
                                fontSize: 12,
                                color: "#fca5a5",
                                background: "rgba(239,68,68,0.1)",
                                border: "1px solid rgba(239,68,68,0.3)",
                                borderRadius: 8,
                            }}
                        >
                            {error}
                            {error.toLowerCase().includes("pending admin verification") && (
                                <div style={{ marginTop: 6 }}>
                                    <button
                                        type="button"
                                        onClick={() => setShowVerificationModal(true)}
                                        style={{ background: "none", border: "none", color: "#818cf8", fontSize: 12, textDecoration: "underline", cursor: "pointer", padding: 0 }}
                                    >
                                        Request Verification
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                    {showVerificationModal && (
                        <VerificationRequiredModal message={error} onClose={() => setShowVerificationModal(false)} />
                    )}
                    <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={submitting}
                            style={{
                                padding: "8px 14px",
                                fontSize: 13,
                                background: "transparent",
                                color: "rgba(255,255,255,0.7)",
                                border: "1px solid rgba(255,255,255,0.15)",
                                borderRadius: 6,
                                cursor: submitting ? "not-allowed" : "pointer",
                            }}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={submitting || overLimit || ownsSelf}
                            style={{
                                padding: "8px 16px",
                                fontSize: 13,
                                fontWeight: 600,
                                background: submitting || overLimit || ownsSelf
                                    ? "rgba(255,255,255,0.1)"
                                    : "#fff",
                                color: submitting || overLimit || ownsSelf
                                    ? "rgba(255,255,255,0.4)"
                                    : "#000",
                                border: "none",
                                borderRadius: 6,
                                cursor: submitting || overLimit || ownsSelf ? "not-allowed" : "pointer",
                            }}
                        >
                            {submitting ? "Sending…" : ownsSelf ? "Yourself" : "Send"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export const EnquiryModal: React.FC<{
    deal: Deal;
    sessionId: string;
    gatheredInfo?: GatheredHints;
    ownsDeal?: boolean;
    onClose: () => void;
    onSubmitted: (leadId: string) => void;
}> = ({ deal, sessionId, gatheredInfo, ownsDeal, onClose, onSubmitted }) => {
    const [body, setBody] = useState<string>("");
    const [maxChars, setMaxChars] = useState<number>(8000);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string>(
        ownsDeal
            ? "You own this deal — self-enquiry is not applicable. Close this and browse other sellers' deals."
            : ""
    );
    const [showVerificationModal, setShowVerificationModal] = useState(false);
    const [refining, setRefining] = useState(false);
    const [refineInstruction, setRefineInstruction] = useState<string>("");

    const resolvedDealId = deal.id ?? deal.deal_id;

    const fetchDraft = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${apiBase}/rag/leads/enquiry-draft`, {
                method: "POST",
                headers: { ...authHeaders(), "Content-Type": "application/json" },
                body: JSON.stringify({
                    deal_id: resolvedDealId,
                    session_id: sessionId || undefined,
                }),
            });
            if (res.status === 403) {
                const errBody = await res.json().catch(() => ({}));
                const msg = errBody?.detail?.message || errBody?.detail || "Your account is pending verification. You cannot send enquiries until an admin verifies your account.";
                setError(typeof msg === "string" ? msg : "Your account is not verified. Please contact support.");
                setLoading(false);
                return;
            }
            if (!res.ok) throw new Error(`Draft fetch failed (${res.status})`);
            const data = await res.json();
            const rawDraft = (data.draft_text as string) || "";
            // Strip [Image #N] markers that leak in from chat context
            const cleanDraft = rawDraft.replace(/\[Image\s*#?\s*\d+\]/gi, "").replace(/\n{3,}/g, "\n\n").trim();
            setBody(cleanDraft);
            setMaxChars((data.max_chars as number) || 8000);
        } catch {
            // fall through with empty body — user can still type
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { void fetchDraft(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const charCount = body.length;
    const overLimit = charCount > maxChars;

    const handleReset = () => { void fetchDraft(); };

    const handleRefine = async () => {
        if (refining || !body.trim()) return;
        setRefining(true);
        setError("");
        try {
            const improved = await apiRefineEnquiry(body, refineInstruction);
            if (improved.trim()) {
                setBody(improved.trim().slice(0, maxChars));
            } else {
                setError("AI couldn't refine the enquiry. You can still send the current draft.");
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : "Refine failed.");
        } finally {
            setRefining(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (ownsDeal) {
            setError("You own this deal — self-enquiry is not applicable.");
            return;
        }
        if (!body.trim()) {
            setError("Enquiry can't be empty.");
            return;
        }
        if (overLimit) {
            setError(`Too long — keep under ${maxChars} characters.`);
            return;
        }
        setError("");
        setSubmitting(true);
        try {
            const dest = (() => {
                if (gatheredInfo?.destination) {
                    return Array.isArray(gatheredInfo.destination)
                        ? gatheredInfo.destination
                        : gatheredInfo.destination;
                }
                const parts = [...(deal.destination?.cities ?? []), ...(deal.destination?.countries ?? [])];
                return parts.length ? parts.join(", ") : undefined;
            })();
            const paxRaw = gatheredInfo?.pax;
            const paxNum = typeof paxRaw === "number"
                ? paxRaw
                : typeof paxRaw === "string" && paxRaw.trim()
                    ? parseInt(paxRaw, 10)
                    : undefined;
            const resolvedDealId = deal.id ?? deal.deal_id;
            if (!resolvedDealId) {
                // Surfaces an edge case where the enriched deal has no Mongo _id.
                // Without deal_id the lead can only reach the seller via destination match.
                // eslint-disable-next-line no-console
                console.warn(
                    "[EnquiryModal] Submitting enquiry without a deal_id — seller routing will fall back to destination only.",
                    deal,
                );
            }
            const result = await apiCreateLead({
                session_id: sessionId,
                deal_id: resolvedDealId,
                dealer_id: (deal as any).dealer?.id ?? deal.dealer_id ?? undefined,
                destination: dest,
                trip_type: gatheredInfo?.trip_type,
                pax: Number.isFinite(paxNum as number) ? (paxNum as number) : undefined,
                duration: gatheredInfo?.duration ?? (deal.package?.duration_nights ? `${deal.package.duration_nights}N` : undefined),
                travel_dates: gatheredInfo?.travel_dates ?? gatheredInfo?.travel_date,
                budget: gatheredInfo?.budget,
                message: body,
                campaign_id: (deal as any).boost_campaign_id || undefined,
                gathered_info: {
                    ...(gatheredInfo ?? {}),
                    deal_title: deal.title,
                    deal_id: deal.id ?? deal.deal_id,
                },
            });
            onSubmitted(result.lead_id);
        } catch (err) {
            const msg = err instanceof Error ? err.message : "Failed to send enquiry. Please try again.";
            setError(msg);
            // Don't make the user hunt for a "Request Verification" link inside the
            // inline error — pop the modal straight away when the block IS a
            // verification gate (as opposed to any other failure, e.g. network).
            if (msg.toLowerCase().includes("pending admin verification")) {
                setShowVerificationModal(true);
            }
        } finally {
            setSubmitting(false);
        }
    };

    const priceLabel = (deal.pricing?.discounted_price ?? deal.pricing?.original_price) != null
        ? `${currencySymbol(deal.pricing!.currency)}${((deal.pricing!.discounted_price ?? deal.pricing!.original_price) as number).toLocaleString("en-IN")} per person`
        : "Price on request";
    // Use verified_name if seller is verified; otherwise mask identity completely
    const isSellerVerified = Boolean(deal.is_verified);
    const sellerLabel = isSellerVerified
        ? (deal.verified_name || "Verified Seller")
        : "the seller";

    return (
        <div
            style={{
                position: "fixed",
                inset: 0,
                background: "rgba(0,0,0,0.65)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1000,
                padding: "16px",
            }}
            onClick={onClose}
        >
            <form
                onSubmit={handleSubmit}
                className="enquiry-modal"
                style={{
                    width: "100%",
                    maxWidth: "680px",
                    maxHeight: "92vh",
                    display: "flex",
                    flexDirection: "column",
                    background: "#ffffff",
                    color: "#111",
                    borderRadius: "12px",
                    boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
                    overflow: "hidden",
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "18px 22px 12px" }}>
                    <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: "#111" }}>Send enquiry</h3>
                    <button
                        type="button"
                        onClick={onClose}
                        style={{ background: "transparent", border: "none", color: "#888", fontSize: 22, cursor: "pointer", lineHeight: 1 }}
                        aria-label="Close"
                    >×</button>
                </div>

                {/* To: deal + seller pill */}
                <div style={{ padding: "0 22px 14px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <span style={{ color: "#444", fontWeight: 500, fontSize: 14 }}>To:</span>
                        <div
                            style={{
                                flex: 1,
                                display: "flex",
                                alignItems: "center",
                                gap: 12,
                                padding: "10px 12px",
                                border: "1px solid #e5e7eb",
                                borderRadius: 10,
                                background: "#fafafa",
                            }}
                        >
                            <div style={{ width: 36, height: 36, borderRadius: 18, background: isSellerVerified ? "#16a34a" : "#6b7280", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 600, fontSize: isSellerVerified ? 16 : 14, flexShrink: 0 }}>
                                {isSellerVerified ? "✓" : "S"}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 13, color: "#111", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: 6 }}>
                                    {sellerLabel}
                                    {isSellerVerified && (
                                        <span style={{ fontSize: 11, color: "#16a34a", fontWeight: 600, background: "#dcfce7", borderRadius: 4, padding: "1px 5px" }}>Verified</span>
                                    )}
                                </div>
                                <div style={{ fontSize: 12, color: "#666", marginTop: 2 }}>
                                    {deal.title}{deal.package?.duration_nights ? ` · ${deal.package.duration_nights}N` : ""} · {priceLabel}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Toolbar */}
                <div style={{ padding: "0 22px 10px", display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                    <input
                        type="text"
                        value={refineInstruction}
                        onChange={(e) => setRefineInstruction(e.target.value)}
                        disabled={refining || submitting || loading}
                        placeholder="Tell AI what to change (optional)…"
                        style={{
                            flex: "1 1 220px",
                            minWidth: 160,
                            padding: "8px 12px",
                            border: "1px solid #e5e7eb",
                            borderRadius: 8,
                            fontSize: 13,
                            fontFamily: "inherit",
                            outline: "none",
                            color: "#222",
                            background: "#fff",
                        }}
                    />
                    <button type="button" onClick={handleRefine} disabled={refining || submitting || loading} style={toolbarBtnStyle}>
                        {refining ? "✦ Refining…" : "✦ Refine with AI"}
                    </button>
                    <button type="button" onClick={handleReset} disabled={submitting || loading} style={toolbarBtnStyle}>
                        {loading ? "↺ Loading…" : "↺ Reset template"}
                    </button>
                </div>

                {/* Body: editable document */}
                <div style={{ padding: "0 22px", flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
                    <div style={{ position: "relative", flex: 1, minHeight: 260 }}>
                        <textarea
                            value={body}
                            onChange={(e) => setBody(e.target.value)}
                            disabled={submitting || refining || loading}
                            placeholder={loading ? "Loading draft…" : undefined}
                            style={{
                                width: "100%",
                                height: "100%",
                                minHeight: 260,
                                padding: "14px 16px",
                                border: "1px solid #e5e7eb",
                                borderRadius: 10,
                                fontSize: 14,
                                lineHeight: 1.55,
                                fontFamily: "inherit",
                                resize: "none",
                                outline: "none",
                                background: "#fff",
                                color: "#222",
                                boxSizing: "border-box",
                            }}
                        />
                        <div
                            style={{
                                position: "absolute",
                                right: 14,
                                bottom: 10,
                                fontSize: 11,
                                color: overLimit ? "#dc2626" : "#9ca3af",
                            }}
                        >
                            {charCount}/{maxChars}
                        </div>
                    </div>
                    {error && (
                        <div style={{ color: "#dc2626", fontSize: 13, marginTop: 10, padding: "8px 12px", background: "#fef2f2", borderRadius: 8, border: "1px solid #fecaca" }}>
                            {error}
                            {error.toLowerCase().includes("pending admin verification") && (
                                <div style={{ marginTop: 6 }}>
                                    <button
                                        type="button"
                                        onClick={() => setShowVerificationModal(true)}
                                        style={{ background: "none", border: "none", color: "#4f46e5", fontSize: 13, textDecoration: "underline", cursor: "pointer", padding: 0 }}
                                    >
                                        Request Verification
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                    {showVerificationModal && (
                        <VerificationRequiredModal message={error} onClose={() => setShowVerificationModal(false)} />
                    )}
                </div>

                {/* Footer */}
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 12,
                        padding: "14px 22px 18px",
                        borderTop: "1px solid #f3f4f6",
                        marginTop: 14,
                    }}
                >
                    <div style={{ fontSize: 12, color: "#6b7280" }}>
                        A matching seller is notified instantly.
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={submitting}
                            style={{
                                padding: "10px 16px",
                                borderRadius: 999,
                                border: "1px solid #d1d5db",
                                background: "#fff",
                                color: "#374151",
                                fontWeight: 500,
                                cursor: submitting ? "not-allowed" : "pointer",
                            }}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={submitting || overLimit || Boolean(ownsDeal)}
                            title={ownsDeal ? "You can't send an enquiry on your own deal" : undefined}
                            style={{
                                padding: "10px 28px",
                                borderRadius: 999,
                                border: "none",
                                background: submitting || overLimit || ownsDeal ? "#6b7280" : "#111",
                                color: "#fff",
                                fontWeight: 600,
                                cursor: submitting || overLimit || ownsDeal ? "not-allowed" : "pointer",
                                minWidth: 96,
                            }}
                        >
                            {ownsDeal ? "Your own deal" : submitting ? "Sending…" : "Send"}
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
};

const toolbarBtnStyle: React.CSSProperties = {
    padding: "8px 14px",
    borderRadius: 10,
    border: "1px solid #e5e7eb",
    background: "#fff",
    color: "#111",
    fontSize: 13,
    fontWeight: 500,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
};


// ─── Seller Connect Panel ─────────────────────────────────────────────────────

const ConnectPanel: React.FC<{
    deals: Deal[];
    sessionId: string;
    connectStatus: Record<number, "pending" | "sent" | "error">;
    onConnect: (idx: number) => void;
}> = ({ deals, connectStatus, onConnect }) => (
    <div className="connect-panel">
        <div className="connect-panel-header">
            <span className="connect-panel-icon">🔗</span>
            <div>
                <p className="connect-panel-title">Would you like to connect with a seller?</p>
                <p className="connect-panel-sub">Select a deal and we'll send your connection request anonymously.</p>
            </div>
        </div>
        <div className="connect-panel-btns">
            {deals.map((deal, i) => {
                const status = connectStatus[i];
                return (
                    <button
                        key={i}
                        className={`connect-btn connect-btn--${status ?? "idle"}`}
                        onClick={() => onConnect(i)}
                        disabled={status === "pending" || status === "sent"}
                    >
                        {status === "pending" && <span className="connect-spinner" />}
                        {status === "sent" && "✅"}
                        {status === "error" && "⚠️"}
                        {!status && "📩"}
                        <span>
                            {status === "sent" ? "Request sent!"
                                : status === "error" ? "Try again"
                                    : status === "pending" ? "Sending…"
                                        : `Connect for Deal #${i + 1}`}
                        </span>
                        <span className="connect-btn-title">{(deal.title ?? "").slice(0, 30)}{(deal.title ?? "").length > 30 ? "…" : ""}</span>
                    </button>
                );
            })}
        </div>
    </div>
);

// ─── Itinerary template page slider ─────────────────────────────────────────

const ItineraryPageSlider: React.FC<{ pages: string[] }> = ({ pages }) => {
    const [current, setCurrent] = React.useState(0);
    const total = pages.length;

    return (
        <div style={{ padding: "14px 22px 0" }}>
            {/* Current page image */}
            <div style={{ position: "relative", borderRadius: 10, overflow: "hidden", background: "#EEEEEE" }}>
                <img
                    src={pages[current]}
                    alt={`Page ${current + 1} of ${total}`}
                    style={{ width: "100%", display: "block", maxHeight: 420, objectFit: "contain" }}
                    loading="lazy"
                />
                {/* Prev / Next overlays */}
                {current > 0 && (
                    <button
                        type="button"
                        onClick={() => setCurrent(c => c - 1)}
                        style={{
                            position: "absolute", left: 0, top: 0, bottom: 0, width: 44,
                            background: "linear-gradient(to right, rgba(0,0,0,0.55), transparent)",
                            border: "none", color: "#fff", fontSize: 20, cursor: "pointer",
                            display: "flex", alignItems: "center", justifyContent: "center",
                        }}
                    >‹</button>
                )}
                {current < total - 1 && (
                    <button
                        type="button"
                        onClick={() => setCurrent(c => c + 1)}
                        style={{
                            position: "absolute", right: 0, top: 0, bottom: 0, width: 44,
                            background: "linear-gradient(to left, rgba(0,0,0,0.55), transparent)",
                            border: "none", color: "#fff", fontSize: 20, cursor: "pointer",
                            display: "flex", alignItems: "center", justifyContent: "center",
                        }}
                    >›</button>
                )}
                {/* Page counter badge */}
                <div style={{
                    position: "absolute", bottom: 8, left: "50%", transform: "translateX(-50%)",
                    background: "rgba(0,0,0,0.6)", color: "#fff", fontSize: 11, fontWeight: 600,
                    padding: "2px 10px", borderRadius: 20, backdropFilter: "blur(4px)",
                    pointerEvents: "none",
                }}>
                    {current + 1} / {total}
                </div>
            </div>

            {/* Thumbnail strip */}
            <div style={{
                display: "flex", gap: 5, overflowX: "auto", paddingTop: 8,
                scrollbarWidth: "none",
            }}>
                {pages.map((url, i) => (
                    <button
                        key={i}
                        type="button"
                        onClick={() => setCurrent(i)}
                        style={{
                            flex: "0 0 auto",
                            width: 46, height: 62,
                            border: i === current
                                ? "2px solid rgba(52,211,153,0.8)"
                                : "2px solid rgba(255,255,255,0.08)",
                            borderRadius: 5, overflow: "hidden", padding: 0,
                            cursor: "pointer", background: "#111",
                            opacity: i === current ? 1 : 0.6,
                            transition: "all 0.12s",
                        }}
                    >
                        <img
                            src={url}
                            alt={`Page ${i + 1}`}
                            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                            loading="lazy"
                        />
                    </button>
                ))}
            </div>
        </div>
    );
};


// ─── Generated media card (flyer / video / itinerary / slides + prompt preview) ─

const MediaPreviewCard: React.FC<{
    deal: Deal;
    caption: string;
    promptUsed?: string;
    isPreview?: boolean;
    onFullscreen: (url: string) => void;
    onDownload: (url: string, type: "image" | "video" | "itinerary" | "slides", caption?: string, sessionId?: string) => void;
    onEditText?: (deal: Deal) => void;
}> = ({ deal, caption, promptUsed, isPreview, onFullscreen, onDownload, onEditText }) => {
    const category = deal.category ?? "Flyer";
    const badge = mediaBadgeMeta(category);
    const mediaUrl = deal.image ?? "";
    const htmlUrl = deal.html_url ?? (isHtmlMediaUrl(mediaUrl) ? mediaUrl : "");
    const isVideo = category === "Video" || isVideoMediaUrl(mediaUrl);
    const isPptx = category === "Slides" || isPptxMediaUrl(mediaUrl);
    const isHtml = Boolean(htmlUrl) || (category === "Itinerary" && isHtmlMediaUrl(mediaUrl));
    const isPdf = !isHtml && (isPdfMediaUrl(mediaUrl));
    const isPortrait = category === "Flyer";
    const thumbnailUrl = deal.thumbnail_url ?? "";

    return (
        <div style={{
            marginTop: 12,
            background: "linear-gradient(135deg, rgba(30,30,46,0.95) 0%, rgba(20,20,35,0.98) 100%)",
            borderRadius: 16,
            border: isPreview ? "1px solid rgba(99,102,241,0.35)" : "1px solid rgba(255,255,255,0.08)",
            overflow: "hidden",
            boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
            maxWidth: isPortrait && !isPptx ? 360 : "100%",
        }}>
            <div style={{ padding: "8px 14px 0", display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                <span style={{
                    fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase",
                    padding: "2px 8px", borderRadius: 20,
                    background: badge.bg, color: badge.color, border: `1px solid ${badge.border}`,
                }}>
                    {badge.emoji} {badge.label}
                </span>
                {isPreview && (
                    <span style={{
                        fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase",
                        padding: "2px 8px", borderRadius: 20,
                        background: "rgba(99,102,241,0.15)", color: "#a5b4fc",
                        border: "1px solid rgba(99,102,241,0.3)",
                    }}>
                        Prompt Preview
                    </span>
                )}
                <span style={{ fontSize: 10, color: "#6b7280", marginLeft: "auto" }}>
                    {isPreview ? "Review prompt · then Generate" : "AI Generated · Saved to Assets"}
                </span>
            </div>

            {promptUsed && (
                <div style={{ margin: "10px 14px 0", padding: "10px 12px", borderRadius: 10, background: "rgba(15,23,42,0.65)", border: "1px solid rgba(99,102,241,0.2)" }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 6 }}>
                        Final prompt
                    </div>
                    <pre style={{
                        margin: 0, fontSize: 11, lineHeight: 1.55, color: "#cbd5e1",
                        whiteSpace: "pre-wrap", wordBreak: "break-word", fontFamily: "ui-monospace, monospace",
                        maxHeight: 220, overflow: "auto",
                    }}>{promptUsed}</pre>
                </div>
            )}

            {mediaUrl && !isPreview && (
                <div
                    style={{
                        margin: "10px 14px",
                        borderRadius: 10,
                        overflow: "hidden",
                        background: "#000",
                        position: "relative",
                        cursor: (isPptx || isPdf) ? "default" : "pointer",
                    }}
                    onClick={() => { if (!isPptx && !isPdf) onFullscreen(mediaUrl); }}
                >
                    {isPptx ? (
                        <div style={{ padding: "28px 20px", textAlign: "center", background: "linear-gradient(135deg,#1e293b,#0f172a)" }}>
                            <div style={{ fontSize: 40, marginBottom: 8 }}>📊</div>
                            <div style={{ fontSize: 14, fontWeight: 700, color: "#f1f5f9", marginBottom: 4 }}>PowerPoint ready</div>
                            <div style={{ fontSize: 12, color: "#94a3b8" }}>Download the .pptx presentation file</div>
                        </div>
                    ) : isHtml ? (
                        <div style={{ position: "relative" }}>
                            <div style={{
                                borderRadius: "0 0 10px 10px",
                                overflow: "hidden",
                                background: "#0d1117",
                                border: "1px solid rgba(255,255,255,0.06)",
                            }}>
                                {(deal.page_urls ?? []).length > 0 ? (
                                    <div style={{ overflowX: "auto", display: "flex", gap: 8, padding: 10, scrollSnapType: "x mandatory" }}>
                                        {(deal.page_urls ?? []).map((pageUrl, pi) => (
                                            <img
                                                key={pi}
                                                src={pageUrl}
                                                alt={`Page ${pi + 1}`}
                                                onClick={e => { e.stopPropagation(); window.open(htmlUrl || mediaUrl, "_blank"); }}
                                                style={{
                                                    flex: "0 0 auto",
                                                    width: 160,
                                                    height: 226,
                                                    objectFit: "cover",
                                                    borderRadius: 6,
                                                    border: pi === 0 ? "2px solid rgba(52,211,153,0.5)" : "1px solid rgba(255,255,255,0.1)",
                                                    cursor: "pointer",
                                                    scrollSnapAlign: "start",
                                                }}
                                            />
                                        ))}
                                    </div>
                                ) : deal.thumbnail_url ? (
                                    <img
                                        src={deal.thumbnail_url}
                                        alt="Itinerary cover"
                                        style={{ width: "100%", display: "block", maxHeight: 320, objectFit: "cover" }}
                                        onClick={e => { e.stopPropagation(); window.open(htmlUrl || mediaUrl, "_blank"); }}
                                    />
                                ) : (
                                    <iframe
                                        src={htmlUrl || mediaUrl}
                                        title="Itinerary preview"
                                        style={{ width: "100%", height: 520, border: "none", display: "block" }}
                                        sandbox="allow-same-origin allow-scripts allow-popups allow-downloads"
                                    />
                                )}
                            </div>
                            <div style={{
                                display: "flex", gap: 8, padding: "10px 14px",
                                background: "rgba(10,15,25,0.7)", borderRadius: "0 0 10px 10px",
                            }}>
                                <button
                                    type="button"
                                    onClick={e => { e.stopPropagation(); window.open(htmlUrl || mediaUrl, "_blank"); }}
                                    style={{
                                        flex: 1, padding: "7px 12px", borderRadius: 8,
                                        background: "rgba(52,211,153,0.15)", border: "1px solid rgba(52,211,153,0.35)",
                                        color: "#6ee7b7", fontSize: 11, fontWeight: 700, cursor: "pointer",
                                    }}
                                >⛶ Open full itinerary</button>
                                <button
                                    type="button"
                                    onClick={e => { e.stopPropagation(); onDownload(htmlUrl || mediaUrl, "itinerary", deal.title, deal.session_id); }}
                                    style={{
                                        padding: "7px 12px", borderRadius: 8,
                                        background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)",
                                        color: "#94a3b8", fontSize: 11, fontWeight: 600, cursor: "pointer",
                                    }}
                                >⬇ PDF</button>
                            </div>
                        </div>
                    ) : isPdf ? (
                        <div style={{ position: "relative" }}>
                            {thumbnailUrl ? (
                                <>
                                    <img
                                        src={thumbnailUrl}
                                        alt={deal.title || "Itinerary preview"}
                                        style={{ width: "100%", display: "block", aspectRatio: "9/16", objectFit: "cover" }}
                                    />
                                    <div style={{
                                        position: "absolute", inset: 0,
                                        background: "linear-gradient(to bottom, transparent 60%, rgba(0,0,0,0.75) 100%)",
                                        display: "flex", alignItems: "flex-end", justifyContent: "center",
                                        paddingBottom: 14,
                                    }}>
                                        <button
                                            type="button"
                                            onClick={e => { e.stopPropagation(); window.open(mediaUrl, "_blank"); }}
                                            style={{
                                                padding: "7px 18px", borderRadius: 8,
                                                background: "rgba(52,211,153,0.85)", border: "none",
                                                color: "#042f2e", fontSize: 12, fontWeight: 700,
                                                cursor: "pointer", backdropFilter: "blur(4px)",
                                            }}
                                        >⛶ Open Full PDF</button>
                                    </div>
                                </>
                            ) : (
                                <div style={{ padding: "28px 20px", textAlign: "center", background: "linear-gradient(135deg,#0d2e20,#0f172a)" }}>
                                    <div style={{ fontSize: 40, marginBottom: 8 }}>🗺️</div>
                                    <div style={{ fontSize: 14, fontWeight: 700, color: "#6ee7b7", marginBottom: 4 }}>Itinerary PDF ready</div>
                                    <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 12 }}>Multi-page PDF itinerary generated</div>
                                    <button
                                        type="button"
                                        onClick={e => { e.stopPropagation(); window.open(mediaUrl, "_blank"); }}
                                        style={{
                                            padding: "7px 18px", borderRadius: 8,
                                            background: "rgba(52,211,153,0.2)", border: "1px solid rgba(52,211,153,0.4)",
                                            color: "#6ee7b7", fontSize: 12, fontWeight: 700, cursor: "pointer",
                                        }}
                                    >Open PDF ↗</button>
                                </div>
                            )}
                        </div>
                    ) : isVideo ? (
                        <video
                            src={mediaUrl}
                            controls
                            playsInline
                            preload="metadata"
                            style={{ width: "100%", display: "block", aspectRatio: "16/9", objectFit: "contain" }}
                            onClick={e => e.stopPropagation()}
                        />
                    ) : (
                        <>
                            <img
                                src={mediaUrl}
                                alt={deal.title || category}
                                style={{ width: "100%", display: "block", aspectRatio: isPortrait ? "9/16" : "16/9", objectFit: "cover" }}
                            />
                            <div style={{
                                position: "absolute", top: 8, right: 8,
                                background: "rgba(0,0,0,0.55)", borderRadius: 6,
                                padding: "3px 7px", fontSize: 11, color: "#fff",
                                backdropFilter: "blur(4px)",
                            }}>⛶ Tap to expand</div>
                        </>
                    )}
                </div>
            )}

            <div style={{ padding: "0 14px 12px", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span style={{ flex: 1, fontSize: 12, color: "#9ca3af", lineHeight: 1.4, minWidth: 120 }}>
                    {deal.title || caption}
                </span>
                {mediaUrl && !isPreview && (
                    <>
                        {!isPptx && !isPdf && (
                            <button
                                type="button"
                                onClick={() => onFullscreen(mediaUrl)}
                                title="View fullscreen"
                                style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.1)", background: "rgba(255,255,255,0.05)", color: "#9ca3af", fontSize: 12, cursor: "pointer", whiteSpace: "nowrap" }}
                            >⛶</button>
                        )}
                        {isPdf && (
                            <button
                                type="button"
                                onClick={() => window.open(mediaUrl, "_blank")}
                                title="Open PDF in new tab"
                                style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid rgba(52,211,153,0.3)", background: "rgba(52,211,153,0.08)", color: "#6ee7b7", fontSize: 12, cursor: "pointer", whiteSpace: "nowrap" }}
                            >↗ Open PDF</button>
                        )}
                        <button
                            type="button"
                            onClick={() => onDownload(mediaUrl, isVideo ? "video" : "image", deal.title)}
                            title="Download"
                            style={{ padding: "6px 14px", borderRadius: 8, border: "1px solid rgba(99,102,241,0.35)", background: "rgba(99,102,241,0.15)", color: "#a5b4fc", fontSize: 12, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}
                        >↓ {isPdf ? "Download PDF" : "Download"}</button>
                        {isVideo && deal.session_id && onEditText && (
                            <button
                                type="button"
                                onClick={() => onEditText(deal)}
                                title="Manually edit text and shapes on this video"
                                style={{ padding: "6px 14px", borderRadius: 8, border: "1px solid rgba(253,230,138,0.35)", background: "rgba(253,230,138,0.12)", color: "#fde68a", fontSize: 12, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}
                            >✎ Edit text</button>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

// ─── Typing Indicator ─────────────────────────────────────────────────────────

const TypingIndicator: React.FC = () => (
    <div className="msg-row msg-row--ai">
        <div className="msg-avatar msg-avatar--ai">✈️</div>
        <div className="typing-indicator">
            <span /><span /><span />
        </div>
    </div>
);

// Market Research turns can take a couple seconds before the FIRST streamed token
// (destination extraction + inventory lookup/synthesis run before Perplexity even
// starts) — during that gap the streaming bubble has empty content, which renders as
// nothing at all (see the `msg.content ? <ReactMarkdown/> : null` bubble body below).
// This fills that gap: bouncing dots for any streaming tab, plus — for
// market_research and deals (Fresh RAG), since their pre-token wait is the
// longest — the REAL current pipeline step, driven by the backend's `stage`
// SSE events (see streamMarketResearchChat/streamFreshRagChat's onStage +
// researchStageLabel on the message). Not a timer cycling canned phrases —
// if the backend is on "check_inventory", that's what shows; if it skips
// straight to "researching" because no destination was found, no fake
// "Checking inventory…" ever flashes for a step that didn't run.
const StreamingPlaceholder: React.FC<{ tab?: SessionTab; stageLabel?: string }> = ({ tab, stageLabel }) => {
    const text = (tab === "market_research" || tab === "deals") ? (stageLabel || "Thinking…") : null;
    return (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            <span className="typing-indicator" style={{ display: "inline-flex", padding: "6px 10px" }}>
                <span /><span /><span />
            </span>
            {text && <span className="typing-indicator-text">{text}</span>}
        </span>
    );
};

// ─── Interaction Modes ────────────────────────────────────────────────────────
// /chat now supports:
// - chat   → standard RAG assistant
// - image  → flyer generation flow with prompt preview
// - video  → video generation flow with prompt preview

// ─── Main Page ────────────────────────────────────────────────────────────────

const RagChatPage: React.FC = () => {
    const { isAuthenticated, isAuthReady, user, logout } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { trackAiMedia } = useBackgroundJobs();

    const [sessions, setSessions] = useState<Session[]>([]);
    const [sessionsSkip, setSessionsSkip] = useState(0);
    const [sessionsHasMore, setSessionsHasMore] = useState(false);
    const [loadingMoreSessions, setLoadingMoreSessions] = useState(false);
    const sessionsListRef = useRef<HTMLDivElement>(null);
    const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
    // Keep the boost impression events keyed to the same session the backend
    // auction stashed its prices under — otherwise the seller is billed their
    // full bid instead of the second-price the auction actually settled on.
    useEffect(() => { setBoostRagSessionId(activeSessionId || ""); }, [activeSessionId]);
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [input, setInput] = useState("");
    const [isTyping, setIsTyping] = useState(false);
    const [selectedTab, setSelectedTab] = useState<SessionTab>("deals");
    const [mediaMode, setMediaMode] = useState<MediaMode>("chat");
    const [promptSidebarOpen, setPromptSidebarOpen] = useState(false);
    const [previewPrompt, setPreviewPrompt] = useState("");
    const [pendingMediaPrompt, setPendingMediaPrompt] = useState("");
    const [pendingInputImageUrl, setPendingInputImageUrl] = useState<string | null>(null);
    const [pendingUploadedFlyerS3Key, setPendingUploadedFlyerS3Key] = useState<string | null>(null);
    const [pendingMediaType, setPendingMediaType] = useState<"image" | "video">("image");
    const [isGeneratingMedia, setIsGeneratingMedia] = useState(false);
    const [personalizedStarterChips, setPersonalizedStarterChips] = useState<string[]>([]);
    const [welcomeTitle, setWelcomeTitle] = useState<string>("");
    const [welcomeSub, setWelcomeSub] = useState<string>("");
    const [welcomeBanners, setWelcomeBanners] = useState<Array<{
        id: string;
        title: string | null;
        category_slug: string;
        image_url: string | null;
        video_url?: string | null;
        banner_type?: "category" | "seller_deal" | "direct_link";
        seller_id?: string | null;
        seller_role?: string | null;
        deal_id?: string | null;
        direct_link?: string | null;
    }>>([]);
    const [loadingSessions, setLoadingSessions] = useState(false);
    const [deletingId, setDeletingId] = useState<string | null>(null);
    const [sidebarOpen, setSidebarOpen] = useState(() =>
        typeof window !== "undefined" ? window.matchMedia("(min-width: 641px)").matches : true,
    );
    const [enquiryDeal, setEnquiryDeal] = useState<Deal | null>(null);
    // Stage 3 — DMC enquiry state. Holds the DMC card that was tapped +
    // the itinerary text we pre-seed in the modal. Opens `DmcEnquiryModal`.
    const [enquiryDmc, setEnquiryDmc] = useState<
        { dmc: DmcCard; itinerary: string } | null
    >(null);
    const openEnquiry = (deal: Deal) => {
        fireBoostClick(deal);
        setEnquiryDeal(deal);
    };
    const openDmcEnquiry = (dmc: DmcCard, itinerary: string) => {
        setEnquiryDmc({ dmc, itinerary });
    };

    const [myAssets, setMyAssets] = useState<UserAsset[]>([]);
    const [assetsLoading, setAssetsLoading] = useState(false);
    const [assetsLoadingMore, setAssetsLoadingMore] = useState(false);
    const [assetsHasMore, setAssetsHasMore] = useState(false);
    const [assetsSkip, setAssetsSkip] = useState(0);
    const [assetsActiveTab, setAssetsActiveTab] = useState("all");
    const [chatNavTabs, setChatNavTabs] = useState<ChatTab[]>([]);
    const [myAssetsFilterTabs, setMyAssetsFilterTabs] = useState<ChatTab[]>([]);
    const [templatePanelHeader, setTemplatePanelHeader] = useState<{ title: string; subtitle: string } | null>(null);
    const [myAssetsPanelHeader, setMyAssetsPanelHeader] = useState<{ title: string; subtitle: string } | null>(null);

    // ── Templates (flyer / video / itinerary / slides) ───────────────────────
    const [templates, setTemplates] = useState<FlyerTemplate[]>([]);
    const [templateCategories, setTemplateCategories] = useState<TemplateCategory[]>([]);
    const [templatesLoading, setTemplatesLoading] = useState(false);
    const [activeTemplate, setActiveTemplate] = useState<FlyerTemplate | null>(null);
    const [templateCategoryFilter, setTemplateCategoryFilter] = useState<string>("all");
    /** Dev UI (forms, EDIT) — default off; only toggled when NODE_ENV=development. */
    const [templatesIsDevelopment, setTemplatesIsDevelopment] = useState(false);
    // Separate edit states per asset type — prevents cross-tab bleed
    const [editingTemplate, setEditingTemplate] = useState<FlyerTemplate | null>(null);
    const [editingVideoTemplate, setEditingVideoTemplate] = useState<FlyerTemplate | null>(null);
    const [editingItineraryTemplate, setEditingItineraryTemplate] = useState<FlyerTemplate | null>(null);
    const [toastMsg, setToastMsg] = useState<string | null>(null);
    // Auth gate: anonymous visitors can browse/type in the chat, but generation
    // (template/flyer/itinerary/edit/connect) requires an account.
    const [showAuthModal, setShowAuthModal] = useState(false);
    const [generatingFromTemplate, setGeneratingFromTemplate] = useState(false);
    const [tabQuotas, setTabQuotas] = useState<Record<string, TabQuotaInfo>>({});
    const [templateSessionStarting, setTemplateSessionStarting] = useState(false);
    const [pendingTemplateGen, setPendingTemplateGen] = useState<PendingTemplateGeneration | null>(null);
    // Ref so sendMessage can read last submitted template context without stale-closure issues
    const lastTemplateContextRef = useRef<{
        templateId: string;
        prompt: string;
        template: AnyTemplateItem;
        categoryLabel: string;
        mediaType: "image" | "video";
    } | null>(null);
    /** Persisted across turns: uploaded flyer for + edit (session also stores this server-side). */
    const lastFlyerEditContextRef = useRef<{
        s3Key: string;
        url?: string;
        prompt: string;
        sessionId?: string;
        fromTemplate?: boolean;
    } | null>(null);
    const [mediaFullscreen, setMediaFullscreen] = useState<string | null>(null);
    const [editingVideoDeal, setEditingVideoDeal] = useState<Deal | null>(null);
    const [templatePreview, setTemplatePreview] = useState<FlyerTemplate | null>(null);
    const [showInlineTemplatePicker, setShowInlineTemplatePicker] = useState(false);
    const [flyerAttachments, setFlyerAttachments] = useState<FlyerMediaAttachment[]>([]);
    const [flyerRefsUploading, setFlyerRefsUploading] = useState(false);

    // ── Itinerary PDF upload state ─────────────────────────────────────────────
    const itineraryFileRef = useRef<HTMLInputElement>(null);
    const [itineraryUploading, setItineraryUploading] = useState(false);
    const [itineraryUploadedFiles, setItineraryUploadedFiles] = useState<Array<{ name: string; s3_key: string; session_id: string }>>([]);
    const [itineraryGenerating, setItineraryGenerating] = useState(false);
    // True when the restored session has itinerary_edit_mode (upload-based session)
    const itineraryEditSessionRef = useRef(false);
    const flyer_state_has_itinerary_upload = itineraryEditSessionRef.current || itineraryUploadedFiles.length > 0;

    const showToast = (msg: string, duration = 3000) => {
        setToastMsg(msg);
        setTimeout(() => setToastMsg(null), duration);
    };

    // Returns false (and opens the login/signup modal) when the visitor is not
    // authenticated. Call at the top of any handler that fires a generation API.
    const requireAuthForGeneration = (): boolean => {
        if (isAuthenticated) return true;
        setShowAuthModal(true);
        return false;
    };

    // Upload one or more PDF/DOCX/image files to S3 via /rag/itinerary-references/upload
    const handleItineraryFileUpload = async (files: FileList | null) => {
        if (!files || files.length === 0) return;
        const allowed = ["application/pdf", "application/vnd.openxmlformats-officedocument.wordprocessingml.document", "application/msword", "image/jpeg", "image/png", "image/webp"];
        const validFiles = Array.from(files).filter(f =>
            allowed.includes(f.type) || f.name.endsWith(".pdf") || f.name.endsWith(".docx") || f.name.endsWith(".doc")
        );
        if (validFiles.length === 0) {
            showToast("Only PDF, DOC, DOCX, or image files are supported.");
            return;
        }
        setItineraryUploading(true);
        try {
            const form = new FormData();
            for (const f of validFiles) form.append("files", f, f.name);
            if (activeSessionId) form.append("session_id", activeSessionId);
            const res = await fetch(`${apiBase}/rag/itinerary-references/upload`, {
                method: "POST",
                headers: authHeadersMultipart(),
                body: form,
            });
            if (!res.ok) {
                const { message } = await apiErrorResponse(res, "Upload failed. Please try again.");
                showToast(message);
                return;
            }
            const data = await res.json();
            const sid = data.session_id || activeSessionId || "";
            if (sid && sid !== activeSessionId) {
                setActiveSessionId(sid);
                void loadSessions();
            }
            const newEntries = (data.uploads as Array<{ s3_key: string; filename?: string }> | undefined ?? [])
                .filter(u => u.s3_key)
                .map(u => ({ name: u.filename || validFiles.find(f => f.name === u.filename)?.name || u.s3_key.split("/").pop() || "file", s3_key: u.s3_key, session_id: sid }));
            setItineraryUploadedFiles(prev => {
                const existing = new Set(prev.map(p => p.s3_key));
                return [...prev, ...newEntries.filter(e => !existing.has(e.s3_key))];
            });
            const label = newEntries.length === 1 ? `"${newEntries[0].name}"` : `${newEntries.length} files`;
            showToast(`${label} uploaded. Describe any changes or click ✨ to generate.`);
        } catch (err) {
            showToast(err instanceof Error ? err.message : "Upload failed.");
        } finally {
            setItineraryUploading(false);
        }
    };

    // Stream-generate itinerary from uploaded files via /rag/itinerary-upload/stream
    const handleItineraryUploadGenerate = async (prompt: string) => {
        if (itineraryUploadedFiles.length === 0 && !activeSessionId) return;
        if (!requireAuthForGeneration()) return;
        const sessionId = itineraryUploadedFiles[0]?.session_id || activeSessionId || "";
        const s3Keys = itineraryUploadedFiles.map(f => f.s3_key).filter(Boolean);

        setItineraryGenerating(true);
        setInput("");
        if (textareaRef.current) textareaRef.current.style.height = "auto";

        setMessages(prev => [...prev,
            { id: `user-itin-upload-${Date.now()}`, role: "user" as const, content: prompt || "Generate itinerary from my uploaded file", timestamp: new Date() },
        ]);

        const streamMsgId = `itin-upload-stream-${Date.now()}`;
        setMessages(prev => [...prev, {
            id: streamMsgId,
            role: "assistant" as const,
            content: "Reading your itinerary…",
            generation_title: "Analysing your itinerary",
            generation_detail: "Extracting content from your file",
            generation_progress: 5,
            generation_phase: "prep",
            generation_media_type: "image" as const,
            generation_asset_type: "itinerary",
            generation_template_name: itineraryUploadedFiles[0]?.name || "Uploaded itinerary",
            is_generating: true,
            timestamp: new Date(),
        }]);

        try {
            let finished = false;
            const body = {
                prompt: prompt || "",
                session_id: sessionId,
                selected_tab: "ai_itinerary",
                uploaded_itinerary_s3_keys: s3Keys,
            };
            await streamGenerateFromTemplate(
                `${apiBase}/rag/itinerary-upload/stream`,
                authHeaders(),
                body,
                (event, data) => {
                    if (event === "started" || event === "progress") {
                        const sid = String(data.session_id ?? "");
                        if (event === "started" && sid) trackAiMedia(sid, "itinerary", "Uploaded itinerary");
                        const mapped = mapTemplateProgressToMessage(data);
                        setMessages(prev => prev.map(m =>
                            m.id === streamMsgId ? {
                                ...m,
                                content: mapped.generation_title,
                                is_generating: true,
                                generation_status: mapped.generation_status,
                                generation_stage: mapped.generation_stage,
                                generation_title: mapped.generation_title,
                                generation_detail: mapped.generation_detail,
                                generation_progress: mapped.generation_progress,
                                generation_phase: mapped.generation_phase,
                                generation_asset_type: (data.asset_type as string | undefined) ?? m.generation_asset_type,
                                show_generation_skeleton: mapped.show_generation_skeleton,
                                generation_steps: mergeStreamStep(m.generation_steps ?? [], mapped),
                            } : m,
                        ));
                    } else if (event === "complete") {
                        finished = true;
                        const generated = data as unknown as GenerateMediaApiResponse;
                        setMessages(prev => prev.filter(m => m.id !== streamMsgId));
                        handleTemplateGenerateResult(generated, "Itinerary");
                    } else if (event === "followup") {
                        // Backend needs clarification before generating (e.g. how many days?)
                        finished = true;
                        const question = String(data.caption ?? data.message ?? "Could you provide more details?");
                        const chips = Array.isArray((data as any).suggestions) ? (data as any).suggestions as string[] : ["Provide more details", "Keep as is"];
                        const sid = String(data.session_id ?? "");
                        if (sid && sid !== activeSessionId) { setActiveSessionId(sid); void loadSessions(); }
                        setMessages(prev => prev.filter(m => m.id !== streamMsgId));
                        setMessages(prev => [...prev, {
                            id: `itin-followup-${Date.now()}`,
                            role: "assistant" as const,
                            content: question,
                            suggestions: chips,
                            timestamp: new Date(),
                        }]);
                    } else if (event === "error") {
                        finished = true;
                        const errText = String(data.detail ?? data.message ?? "Generation failed. Please try again.");
                        const errChips = Array.isArray((data as any).suggestions) ? (data as any).suggestions as string[] : ["Try again", "Edit prompt"];
                        setMessages(prev => prev.map(m =>
                            m.id === streamMsgId ? {
                                ...m, content: errText, is_generating: false,
                                generation_status: undefined, generation_stage: undefined,
                                generation_title: undefined, generation_detail: undefined,
                                generation_progress: undefined, generation_phase: undefined,
                                show_generation_skeleton: false, suggestions: errChips,
                            } : m,
                        ));
                    }
                },
            );
            if (!finished) {
                setMessages(prev => prev.filter(m => m.id !== streamMsgId));
                setMessages(prev => [...prev, { id: `itin-err-${Date.now()}`, role: "assistant" as const, content: "Generation ended unexpectedly. Please try again.", suggestions: ["Try again"], timestamp: new Date() }]);
            }
        } catch (err) {
            const msg = err instanceof Error ? err.message : "Generation failed. Please try again.";
            setMessages(prev => [...prev.filter(m => m.id !== streamMsgId), { id: `itin-err-${Date.now()}`, role: "assistant" as const, content: msg, suggestions: ["Try again"], timestamp: new Date() }]);
        } finally {
            setItineraryGenerating(false);
            setIsTyping(false);
        }
    };

    const applyFlyerEditContextFromState = (
        sessionId: string,
        flyerState?: Record<string, unknown>,
        sessionMessages?: SessionMessagePayload[],
    ): boolean => {
        const fs = flyerState ?? {};
        let s3Key = String(fs.uploaded_flyer_edit_s3_key ?? "").trim();
        let url = String(fs.uploaded_flyer_edit_url ?? "").trim();
        const prompt = String(fs.last_edit_prompt ?? "").trim();
        if (!s3Key && sessionMessages?.length) {
            for (let i = sessionMessages.length - 1; i >= 0; i--) {
                const mk = String(sessionMessages[i].uploaded_flyer_s3_key ?? "").trim();
                if (mk) {
                    s3Key = mk;
                    url = String(sessionMessages[i].image_url ?? url).trim();
                    break;
                }
            }
        }
        if (!s3Key) {
            return false;
        }
        lastFlyerEditContextRef.current = {
            s3Key,
            url: url || undefined,
            prompt: prompt || lastFlyerEditContextRef.current?.prompt || "",
            sessionId,
        };
        return true;
    };

    // ── Edit with AI (My Assets) ─────────────────────────────────────────────
    const [editingAssetUrl, setEditingAssetUrl] = useState<string | null>(null);
    const [editingAssetText, setEditingAssetText] = useState("");
    const [editingAssetLoading, setEditingAssetLoading] = useState(false);
    // Unified search — no category pre-selection. Backend classifies intent from the query.

    // Most recent gathered_info from any assistant message, used to prefill the Enquiry modal.
    const lastGatheredInfo = useMemo<GatheredHints | undefined>(() => {
        for (let i = messages.length - 1; i >= 0; i -= 1) {
            const m = messages[i];
            if (m.role === "assistant" && m.gathered_info) {
                return m.gathered_info as GatheredHints;
            }
        }
        return undefined;
    }, [messages]);

    // Handle connect-with-seller for a specific AI message and deal index
    const handleConnect = async (_messageId: string, _dealIndex: number, deal: Deal) => {
        // Route through chat flow so backend BUG-17 confirmation intercept runs
        // ("On it. I'll pass X to the seller. Anything to add?") before enquiry fires.
        void sendMessage(`Connect with seller for ${deal.title}`);
    };

    const bottomRef = useRef<HTMLDivElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const mirrorRef = useRef<HTMLDivElement>(null);

    // Highlighted HTML for {variable} mirror overlay — only active when a template is selected
    const highlightedInputHtml = useMemo(() => {
        if (!activeTemplate) return "";
        // Escape HTML then wrap {variable} in red span
        const escaped = input
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/\n/g, "<br/>");
        return escaped.replace(
            /\{([^}]+)\}/g,
            '<span style="color:#f87171;font-weight:700">{$1}</span>'
        );
    }, [input, activeTemplate]);

    // Sync mirror scroll to textarea scroll
    const syncMirrorScroll = () => {
        if (mirrorRef.current && textareaRef.current) {
            mirrorRef.current.scrollTop = textareaRef.current.scrollTop;
        }
    };

    // Fetch session list — resets to page 0, replaces all sessions
    // /rag/sessions already merges legacy (user_conversations) + fresh_rag sessions
    const loadSessions = useCallback(async () => {
        if (!isAuthReady || !isAuthenticated) return;
        setLoadingSessions(true);
        try {
            const { sessions: list, has_more } = await apiListSessions(0, 20);
            setSessions(list);
            setSessionsSkip(list.length);
            setSessionsHasMore(has_more);
        } catch (err) {
            if ((err as Error & { status?: number }).status === 401) logout();
        } finally {
            setLoadingSessions(false);
        }
    }, [isAuthReady, isAuthenticated, logout]);

    // Load next page and append to existing list
    const loadMoreSessions = useCallback(async () => {
        if (loadingMoreSessions || !sessionsHasMore) return;
        setLoadingMoreSessions(true);
        try {
            const { sessions: more, has_more } = await apiListSessions(sessionsSkip, 20);
            setSessions(prev => {
                const existingIds = new Set(prev.map(s => s.session_id));
                const fresh = more.filter(s => !existingIds.has(s.session_id));
                return [...prev, ...fresh];
            });
            setSessionsSkip(prev => prev + more.length);
            setSessionsHasMore(has_more);
        } catch {
            // silently ignore
        } finally {
            setLoadingMoreSessions(false);
        }
    }, [loadingMoreSessions, sessionsHasMore, sessionsSkip]);

    useEffect(() => {
        void loadSessions();
    }, [loadSessions]);

    // ?session=<id> deep-link: load the session + its messages once the list is ready
    const deepLinkedRef = useRef<string | null>(null);
    useEffect(() => {
        const paramSession = searchParams.get("session");
        if (!paramSession || !sessions.length) return;
        if (deepLinkedRef.current === paramSession) return; // already loaded
        const match = sessions.find((s) => s.session_id === paramSession);
        if (match) {
            deepLinkedRef.current = paramSession;
            void loadSession(match.session_id, match.selected_tab, match.source);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams, sessions]);

    const fetchTabQuotas = useCallback(async () => {
        if (!isAuthenticated) return;
        try {
            // Tier-aware unified usage endpoint — monthly numbers are the
            // real per-tier product cap (daily is just an anti-abuse floor).
            const data = await subscriptionsService.getMyUsage();
            const tabs: Record<string, TabQuotaInfo> = {};
            for (const info of Object.values(data.usage)) {
                const period = info.monthly.limit !== null ? info.monthly : info.daily;
                const entry: TabQuotaInfo = {
                    used: period.used,
                    limit: period.limit ?? -1,
                    remaining: period.remaining ?? -1,
                    warning: period.warning,
                    action_type: info.action,
                };
                const tabIds = info.tabs.length ? info.tabs : [info.action];
                for (const tabId of tabIds) tabs[tabId] = entry;
            }
            setTabQuotas(tabs);
        } catch {
            /* ignore */
        }
    }, [isAuthenticated]);

    useEffect(() => {
        if (isAuthenticated) void fetchTabQuotas();
    }, [isAuthenticated, fetchTabQuotas]);

    useEffect(() => {
        if (!isAuthenticated && isAuthReady) {
            setSessions([]);
            setMessages([]);
            setActiveSessionId(null);
        }
    }, [isAuthenticated, isAuthReady]);

    const activeQuotaWarning = useMemo(() => {
        const tab = activeTemplate
            ? templateTabForAsset(activeTemplate.asset_type)
            : selectedTab;
        if (tab === "deals" || tab === "market_research" || tab === "my_assets") return null;
        return tabQuotas[tab]?.warning ?? null;
    }, [activeTemplate, selectedTab, tabQuotas]);

    const loadChatNavTabs = useCallback(async () => {
        try {
            const res = await fetch(`${apiBase}/rag/chat-tabs`, { headers: authHeaders() });
            if (res.ok) {
                const data = (await res.json()) as ChatTabsResponse;
                if (data.tabs?.length) setChatNavTabs(data.tabs);
            }
        } catch {
            // keep empty; templates/my-assets responses also include tabs
        }
    }, []);

    useEffect(() => {
        if (isAuthReady) void loadChatNavTabs();
    }, [isAuthReady, loadChatNavTabs]);

    const fetchStarterChips = useCallback(async (route?: string) => {
        try {
            // Market Research renders a taller "Try asking" list (up to 8 rows),
            // every other tab still shows the 3-chip pill row.
            const p = new URLSearchParams({ limit: route === "market_research" ? "8" : "3" });
            p.set("now_hour", String(new Date().getHours()));
            // Market Research has its own welcome endpoint — personalized off the
            // user's own research history (not buyer_signals), buyer/seller phrasing,
            // and deliberately no banners field (that tab never shows banners).
            const endpoint = route === "market_research" ? "/rag/market-research/welcome" : "/rag/chips";
            if (route && route !== "market_research") p.set("route", route);
            const res = await fetch(`${apiBase}${endpoint}?${p.toString()}`, { headers: authHeaders() });
            if (res.ok) {
                const data = (await res.json()) as { chips: string[]; welcome_title: string; welcome_sub: string; banners?: Array<{ id: string; title: string | null; category_slug: string; image_url: string | null; video_url?: string | null }> };
                if (data.chips?.length) setPersonalizedStarterChips(data.chips);
                if (data.welcome_title) setWelcomeTitle(data.welcome_title);
                if (data.welcome_sub) setWelcomeSub(data.welcome_sub);
                setWelcomeBanners(data.banners?.length ? data.banners : []);
            }
        } catch {
            // silently fall back to DEFAULT_STARTER_CHIPS and static welcome text
        }
    }, []);

    useEffect(() => {
        if (!isAuthReady) return;
        // Clear the PREVIOUS tab's chips/greeting immediately, before the new
        // tab's fetch resolves — otherwise switching tabs briefly shows the old
        // tab's chips/sub-message until the async response for the new tab lands.
        setPersonalizedStarterChips([]);
        setWelcomeTitle("");
        setWelcomeSub("");
        const route = selectedTab === "deals" ? "deals"
            : selectedTab === "ai_itinerary" ? "ai_itinerary"
            : selectedTab === "market_research" ? "market_research"
            : undefined;
        void fetchStarterChips(route);
    }, [isAuthReady, isAuthenticated, fetchStarterChips, selectedTab]);

    // Scroll to bottom
    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages, isTyping]);

    useEffect(() => {
        if (mediaMode === "chat") {
            setPromptSidebarOpen(false);
        }
    }, [mediaMode]);

const fetchTemplates = async (tab: SessionTab) => {
        setTemplatesLoading(true);
        setTemplates([]);
        setTemplateCategoryFilter("all");
        try {
            const res = await fetch(
                `${apiBase}/rag/templates?tab=${encodeURIComponent(tab)}`,
                { headers: authHeaders() },
            );
            if (res.ok) {
                const data = (await res.json()) as TemplatesListResponse;
                setTemplates(data.templates ?? []);
                setTemplateCategories(data.categories ?? []);
                if (data.tabs?.length) setChatNavTabs(prev => (prev.length ? prev : data.tabs));
                if (data.header_title) {
                    setTemplatePanelHeader({
                        title: data.header_title,
                        subtitle: data.header_subtitle ?? "",
                    });
                }
            }
        } catch { /* silently ignore */ }
        finally { setTemplatesLoading(false); }
    };

    const selectTemplate = async (tmpl: FlyerTemplate) => {
        setActiveTemplate(tmpl);
        setInput("");
        setFlyerAttachments([]);
        const tab = templateTabForAsset(tmpl.asset_type);
        setSelectedTab(tab);
        setTemplateSessionStarting(true);
        try {
            const res = await fetch(`${apiBase}/rag/template-session/start`, {
                method: "POST",
                headers: authHeaders(),
                body: JSON.stringify({
                    template_id: tmpl.id,
                    session_id: activeSessionId ?? null,
                    selected_tab: tab,
                }),
            });
            if (!res.ok) {
                const msg = await apiErrorMessage(res, "Could not start template session.");
                showToast(msg, 5000);
                return;
            }
            const data = (await res.json()) as {
                session_id: string;
                quota_warning?: string | null;
            };
            if (data.session_id) {
                setActiveSessionId(data.session_id);
                void loadSessions();
            }
            if (data.quota_warning) showToast(data.quota_warning, 6000);
            void fetchTabQuotas();
        } catch {
            showToast("Could not start session. Your first message will create one.");
        } finally {
            setTemplateSessionStarting(false);
        }
    };

    const clearTemplateSelection = () => {
        setActiveTemplate(null);
        setInput("");
        if (textareaRef.current) {
            textareaRef.current.style.height = "auto";
        }
    };

    const resolveFlyerUploadForEdit = async (
        attachments: FlyerMediaAttachment[],
    ): Promise<{ uploadedFlyerS3Key?: string; inputImageUrl?: string; sessionId?: string }> => {
        if (!attachments.length) return {};

        const stashUploadRef = (s3Key?: string, url?: string, sessionId?: string) => {
            if (!s3Key) return { uploadedFlyerS3Key: undefined, inputImageUrl: url, sessionId };
            lastFlyerEditContextRef.current = {
                s3Key,
                url,
                prompt: lastFlyerEditContextRef.current?.prompt || "",
                sessionId,
            };
            return {
                uploadedFlyerS3Key: s3Key,
                inputImageUrl: url,
                sessionId,
            };
        };

        const primary = attachments[0];
        setFlyerRefsUploading(true);
        try {
            if (primary.fromAsset && primary.s3_key) {
                const res = await uploadFlyerReferenceImages(
                    apiBase,
                    authHeadersMultipart(),
                    {
                        sessionId: activeSessionId,
                        sourceS3Key: primary.s3_key,
                        presignedUrl: primary.url,
                        dealId: primary.dealId,
                    },
                );
                if (res.fallback) {
                    // Source image isn't reachable in S3 right now — the backend
                    // returned the deal's own data instead so we can still proceed
                    // text-only rather than hard-failing the edit.
                    showToast("That image isn't available right now — continuing with the deal's details instead.", 6000);
                    return { sessionId: res.session_id };
                }
                return stashUploadRef(
                    res.primary_s3_key ?? res.uploads[0]?.s3_key,
                    res.primary_url ?? res.uploads[0]?.url,
                    res.session_id,
                );
            }
            if (primary.file) {
                const res = await uploadFlyerReferenceImages(
                    apiBase,
                    authHeadersMultipart(),
                    {
                        sessionId: activeSessionId,
                        files: attachments.filter((a) => a.file).map((a) => a.file!),
                    },
                );
                return stashUploadRef(
                    res.primary_s3_key ?? res.uploads[0]?.s3_key,
                    res.primary_url ?? res.uploads[0]?.url,
                    res.session_id,
                );
            }
            if (primary.url && primary.s3_key) {
                return stashUploadRef(primary.s3_key, primary.url);
            }
            return {};
        } finally {
            setFlyerRefsUploading(false);
        }
    };

    const runFlyerEditStream = async (
        prompt: string,
        uploadedFlyerS3Key?: string,
        inputImageUrl?: string,
        options?: { skipUserMessage?: boolean; userVisibleText?: string; sessionId?: string },
    ) => {
        if (!requireAuthForGeneration()) return;
        const displayPrompt = prompt.trim() || "Edit my uploaded flyer.";
        if (uploadedFlyerS3Key) {
            lastFlyerEditContextRef.current = {
                s3Key: uploadedFlyerS3Key,
                url: inputImageUrl,
                prompt: displayPrompt,
                sessionId: options?.sessionId ?? lastFlyerEditContextRef.current?.sessionId,
                // Preserve fromTemplate so that "Generate again" still routes to
                // the template path even after an intermediate edit attempt.
                fromTemplate: lastFlyerEditContextRef.current?.fromTemplate,
            };
        } else if (lastFlyerEditContextRef.current) {
            lastFlyerEditContextRef.current.prompt = displayPrompt;
            if (options?.sessionId) {
                lastFlyerEditContextRef.current.sessionId = options.sessionId;
            }
        }

        if (!options?.skipUserMessage) {
            setMessages((prev) => [
                ...prev,
                {
                    id: `user-flyer-edit-${Date.now()}`,
                    role: "user",
                    content: options?.userVisibleText ?? displayPrompt,
                    timestamp: new Date(),
                },
            ]);
        }
        setIsTyping(true);

        const streamMsgId = `flyer-edit-stream-${Date.now()}`;
        setMessages((prev) => [
            ...prev,
            {
                id: streamMsgId,
                role: "assistant",
                content: "Reading your flyer…",
                generation_title: "Starting flyer edit",
                generation_detail: "Using your uploaded flyer from this session",
                generation_progress: 2,
                generation_phase: "prep" as TemplateGenerationPhase,
                generation_media_type: "image",
                generation_template_name: "Flyer edit",
                is_generating: true,
                timestamp: new Date(),
            },
        ]);

        const s3Key =
            (uploadedFlyerS3Key || lastFlyerEditContextRef.current?.s3Key || "").trim();
        const imageUrl =
            inputImageUrl ?? lastFlyerEditContextRef.current?.url ?? undefined;
        const streamSessionId =
            options?.sessionId
            ?? lastFlyerEditContextRef.current?.sessionId
            ?? activeSessionId
            ?? null;

        if (!s3Key && !streamSessionId) {
            setMessages((prev) => prev.filter((m) => m.id !== streamMsgId));
            showToast("Attach a flyer with + before editing.");
            setIsTyping(false);
            return;
        }

        const body: Record<string, unknown> = {
            prompt: displayPrompt,
            media_type: "image",
            session_id: streamSessionId,
            selected_tab: selectedTab,
            preview_only: false,
            ...(s3Key ? { uploaded_flyer_s3_key: s3Key } : {}),
            ...(imageUrl ? { input_image_url: imageUrl } : {}),
        };

        let finished = false;
        try {
            await streamGenerateFlyerMedia(
                `${apiBase}/rag/generate-media/stream`,
                authHeaders(),
                body,
                (event, data) => {
                    if (event === "started" || event === "progress") {
                        const sid = String(data.session_id ?? "");
                        if (event === "started" && sid) {
                            trackAiMedia(sid, String(data.asset_type ?? "image"), String(data.template_name ?? "Flyer"));
                        }
                        const mapped = mapTemplateProgressToMessage(data);
                        setMessages((prev) => prev.map((m) =>
                            m.id === streamMsgId
                                ? {
                                    ...m,
                                    content: mapped.generation_title,
                                    is_generating: true,
                                    generation_status: mapped.generation_status,
                                    generation_stage: mapped.generation_stage,
                                    generation_title: mapped.generation_title,
                                    generation_detail: mapped.generation_detail,
                                    generation_progress: mapped.generation_progress,
                                    generation_phase: mapped.generation_phase,
                                    show_generation_skeleton: mapped.show_generation_skeleton,
                                    generation_steps: mergeStreamStep(m.generation_steps ?? [], mapped),
                                }
                                : m,
                        ));
                    } else if (event === "complete") {
                        finished = true;
                        const generated = data as unknown as GenerateMediaApiResponse;
                        const sid = generated.session_id ?? streamSessionId ?? activeSessionId;
                        if (sid) {
                            if (sid !== activeSessionId) {
                                setActiveSessionId(sid);
                                void loadSessions();
                            }
                            if (s3Key) {
                                lastFlyerEditContextRef.current = {
                                    s3Key,
                                    url: imageUrl,
                                    prompt: displayPrompt,
                                    sessionId: sid,
                                    fromTemplate: true,
                                };
                            }
                        }
                        setMessages((prev) => prev.filter((m) => m.id !== streamMsgId));
                        appendMediaAssistantMessage(generated, "Flyer");
                    } else if (event === "error") {
                        finished = true;
                        const apiDetail = (data.detail_payload ?? data.detail) as
                            | Record<string, unknown>
                            | undefined;
                        const errText = String(
                            data.message ?? apiDetail?.message ?? data.detail ?? "Flyer edit failed.",
                        );
                        maybeEmitQuotaFromStream(apiDetail, errText);
                        setMessages((prev) => prev.map((m) =>
                            m.id === streamMsgId
                                ? {
                                    ...m,
                                    content: errText,
                                    is_generating: false,
                                    generation_title: undefined,
                                    generation_detail: undefined,
                                    suggestions: ["Try again", "Edit again"],
                                }
                                : m,
                        ));
                    }
                },
            );
        } catch (err) {
            finished = true;
            setMessages((prev) => [
                ...prev.filter((m) => m.id !== streamMsgId),
                {
                    id: `flyer-edit-err-${Date.now()}`,
                    role: "assistant",
                    content: err instanceof Error ? err.message : "Flyer edit failed.",
                    suggestions: ["Try again", "Edit again"],
                    timestamp: new Date(),
                },
            ]);
        }
        if (!finished) {
            setMessages((prev) => prev.filter((m) => m.id !== streamMsgId));
            setMessages((prev) => [
                ...prev,
                {
                    id: `flyer-edit-err-${Date.now()}`,
                    role: "assistant",
                    content: "Flyer edit ended unexpectedly. Please try again.",
                    suggestions: ["Try again", "Edit again"],
                    timestamp: new Date(),
                },
            ]);
        }
        setIsTyping(false);
    };

    const appendMediaAssistantMessage = (
        generated: GenerateMediaApiResponse,
        categoryLabel: string,
        options?: { isPreview?: boolean },
    ) => {
        const category = assetTypeToCategory(generated.asset_type) || categoryLabel;
        const htmlUrl = generated.html_url ?? undefined;
        const mediaUrl = generated.media_type === "video"
            ? generated.video_url
            : (htmlUrl || generated.image_url);

        // Video chat's style picker: 5 real rendered previews shown as a
        // carousel of cards, tap-to-select via the accompanying suggestion
        // chips (matching preset names) — not a single-media message.
        const styleOptionDeals: Deal[] | undefined = generated.style_options?.length
            ? generated.style_options.map((opt, i) => ({
                id: `style-${opt.name}-${i}-${Date.now()}`,
                title: opt.name,
                video_url: opt.video_url ?? undefined,
                image: opt.video_url || buildStyleCardImage(opt),
                category,
                item_type: "NEWS", // suppresses DealCard's "Send Enquiry" button — these aren't real deals
            }))
            : undefined;

        const mediaDeal: Deal | undefined = mediaUrl
            ? {
                id: `media-${Date.now()}`,
                title: generated.caption || categoryLabel,
                image: generated.thumbnail_url ?? (htmlUrl || mediaUrl),
                html_url: htmlUrl,
                thumbnail_url: generated.thumbnail_url ?? undefined,
                page_urls: (generated as any).page_urls ?? undefined,
                session_id: generated.session_id ?? undefined,
                category,
                description: generated.s3_key ? `Stored at: ${generated.s3_key}` : undefined,
            }
            : options?.isPreview
                ? { id: `media-preview-${Date.now()}`, title: generated.caption || categoryLabel, category }
                : undefined;

        // If a template_id is present in the last template context, this
        // response came from a template flow — persist s3_key with fromTemplate
        // so that follow-up chips always route back to the template path.
        if (lastTemplateContextRef.current?.templateId && generated.s3_key) {
            lastFlyerEditContextRef.current = {
                s3Key: generated.s3_key,
                url: generated.thumbnail_url ?? generated.image_url ?? undefined,
                prompt: lastTemplateContextRef.current?.prompt ?? "",
                sessionId: generated.session_id ?? activeSessionId ?? undefined,
                fromTemplate: true,
            };
        }

        const suggestions: string[] = generated.suggestions ?? [];

        setMessages(prev => [
            ...prev,
            {
                id: `ai-media-${Date.now()}`,
                role: "assistant",
                content: options?.isPreview
                    ? (generated.caption || "Prompt preview is ready. Review the prompt below, then click Generate.")
                    : (generated.caption || `Your ${categoryLabel.toLowerCase()} is ready!`),
                deals: styleOptionDeals ?? (mediaDeal ? [mediaDeal] : undefined),
                prompt_used: generated.prompt_used || undefined,
                is_media_preview: options?.isPreview,
                suggestions: suggestions.length ? suggestions : undefined,
                timestamp: new Date(),
            },
        ]);
    };

    const handleTemplateGenerateResult = (
        generated: GenerateMediaApiResponse,
        categoryLabel: string,
    ) => {
        if (generated.session_id && generated.session_id !== activeSessionId) {
            setActiveSessionId(generated.session_id);
            void loadSessions();
        }
        if (generated.quota_warning) showToast(generated.quota_warning, 6000);
        void fetchTabQuotas();

        if (generated.needs_followup) {
            // Video review/style-pick turns carry a real video_url and/or
            // style_options alongside the follow-up question — show them,
            // don't drop them like a plain text-only ask.
            if (generated.image_url || generated.video_url) {
                appendMediaAssistantMessage(generated, categoryLabel);
                return;
            }
            setMessages(prev => [...prev, {
                id: `tmpl-followup-${Date.now()}`,
                role: "assistant" as const,
                content: generated.caption,
                suggestions: generated.suggestions?.length ? generated.suggestions : ["I'll add the details"],
                timestamp: new Date(),
            }]);
            return;
        }
        if (generated.is_preview) {
            appendMediaAssistantMessage(generated, categoryLabel, { isPreview: true });
            return;
        }
        if (generated.image_url || generated.video_url) {
            setActiveTemplate(null);
            appendMediaAssistantMessage(generated, categoryLabel);
        } else {
            setMessages(prev => [...prev,
                { id: `tmpl-err-${Date.now()}`, role: "assistant" as const, content: "Generation completed but no output was returned.", suggestions: ["Try again", "Edit prompt"], timestamp: new Date() },
            ]);
        }
    };

    // Re-run a template generation purely over the streaming endpoint.
    // Shared by the regenerate, "Generate again" and confirm-generate paths —
    // the non-stream /rag/generate-from-template endpoint has been removed.
    const streamTemplateRegeneration = async (
        args: { templateId: string; prompt: string; selectedTab?: SessionTab; categoryLabel: string },
    ): Promise<void> => {
        let finished = false;
        try {
            await streamGenerateFromTemplate(
                `${apiBase}/rag/generate-from-template/stream`,
                authHeaders(),
                {
                    template_id: args.templateId,
                    custom_prompt: args.prompt,
                    session_id: activeSessionId ?? null,
                    selected_tab: args.selectedTab ?? selectedTab,
                    preview_only: false,
                },
                (event, data) => {
                    if (event === "complete") {
                        finished = true;
                        const generated = data as unknown as GenerateMediaApiResponse;
                        if (generated.session_id && generated.session_id !== activeSessionId) {
                            setActiveSessionId(generated.session_id);
                            void loadSessions();
                        }
                        appendMediaAssistantMessage(generated, args.categoryLabel);
                    } else if (event === "error") {
                        finished = true;
                        const apiDetail = (data.detail_payload ?? data.detail) as
                            | Record<string, unknown>
                            | undefined;
                        const errText = String(
                            data.detail ?? apiDetail?.message ?? data.message
                            ?? "Generation failed. Please try again.",
                        );
                        maybeEmitQuotaFromStream(apiDetail, errText);
                        setMessages(prev => [...prev, {
                            id: `tmpl-regen-err-${Date.now()}`, role: "assistant" as const,
                            content: errText, suggestions: ["Try again", "Edit prompt"], timestamp: new Date(),
                        }]);
                    }
                },
            );
            if (!finished) {
                setMessages(prev => [...prev, {
                    id: `tmpl-regen-err-${Date.now()}`, role: "assistant" as const,
                    content: "Generation ended unexpectedly. Please try again.",
                    suggestions: ["Try again"], timestamp: new Date(),
                }]);
            }
        } catch (err) {
            setMessages(prev => [...prev, {
                id: `tmpl-regen-err-${Date.now()}`, role: "assistant" as const,
                content: err instanceof Error ? err.message : "Generation failed. Please try again.",
                suggestions: ["Try again", "Edit prompt"], timestamp: new Date(),
            }]);
        }
    };

    const submitTemplateGeneration = async (promptOverride?: string) => {
        if (!activeTemplate || generatingFromTemplate) return;
        if (!requireAuthForGeneration()) return;

        const cleanInput = stripTemplateInputTip(promptOverride ?? input);
        const hasItineraryUploads = activeTemplate.asset_type === "itinerary" && itineraryUploadedFiles.length > 0;
        if (!cleanInput && !hasItineraryUploads) {
            showToast(
                activeTemplate.asset_type === "itinerary"
                    ? "Describe your trip or attach a file (PDF / DOCX / image) to continue."
                    : "Describe your trip or package details to continue."
            );
            return;
        }

        const assetType = activeTemplate.asset_type;
        const categoryLabel = assetType === "video" ? "Video" : assetType === "itinerary" ? "Itinerary" : assetType === "slides" ? "Slides" : "Flyer";
        const mediaType: "image" | "video" = activeTemplate.media_type === "video" ? "video" : "image";
        const tmplId = activeTemplate.id;
        const tmplSnapshot = activeTemplate;
        const usePreview = false;
        const tab = templateTabForAsset(assetType);
        const useStream = assetType === "flyer" || assetType === "video" || assetType === "itinerary" || assetType === "slides";

        lastTemplateContextRef.current = {
            templateId: tmplId,
            prompt: cleanInput,
            template: tmplSnapshot as AnyTemplateItem,
            categoryLabel,
            mediaType,
        };

        // ── Video: attach any personal photos as references on the generate call ──
        let referenceS3Keys: string[] = [];
        if (assetType === "video" && flyerAttachments.length > 0) {
            const attachmentsToUpload = [...flyerAttachments];
            setFlyerAttachments([]);
            const fromAssetKeys = attachmentsToUpload
                .filter(a => a.fromAsset && a.s3_key)
                .map(a => a.s3_key as string);
            referenceS3Keys.push(...fromAssetKeys);
            const filesToUpload = attachmentsToUpload
                .filter(a => a.file)
                .map(a => a.file as File);
            if (filesToUpload.length > 0) {
                try {
                    const uploaded = await uploadFlyerReferenceImages(
                        apiBase,
                        authHeadersMultipart(),
                        { sessionId: activeSessionId, files: filesToUpload },
                    );
                    referenceS3Keys.push(...uploaded.uploads.map(u => u.s3_key).filter(Boolean));
                } catch (e) {
                    showToast("Could not upload your photo — generating without it.");
                }
            }
        }

        // ── Itinerary: attach uploaded reference files (images + PDF/DOCX/DOC) so the
        // backend parses them (vision / OCR→OpenRouter) and overrides the template
        // fields. The keys persist server-side in the session for subsequent turns. ──
        if (assetType === "itinerary" && itineraryUploadedFiles.length > 0) {
            referenceS3Keys.push(...itineraryUploadedFiles.map(f => f.s3_key).filter(Boolean));
            setItineraryUploadedFiles([]);
        }

        setGeneratingFromTemplate(true);
        setInput("");
        if (textareaRef.current) textareaRef.current.style.height = "auto";
        const tmplPreviewUrl = tmplSnapshot.preview_image_url || (tmplSnapshot.template_page_image_urls ?? [])[0] || undefined;
        setMessages(prev => {
            const alreadyShownForTemplate = prev.some(m => m.role === "user" && m.attachmentUrl === tmplPreviewUrl);
            return [...prev, {
                id: `user-tmpl-${Date.now()}`, role: "user" as const,
                content: cleanInput || "Generate itinerary from my uploaded file(s)", timestamp: new Date(),
                attachmentUrl: alreadyShownForTemplate ? undefined : tmplPreviewUrl,
            }];
        });

        const streamMsgId = `tmpl-stream-${Date.now()}`;
        if (useStream) {
            setMessages(prev => [...prev, {
                id: streamMsgId,
                role: "assistant" as const,
                content: `Creating your ${categoryLabel.toLowerCase()}…`,
                generation_title: "Starting your creative",
                generation_detail: "Connecting to the design studio",
                generation_progress: 2,
                generation_phase: "prep",
                generation_media_type: mediaType,
                generation_asset_type: assetType,
                generation_template_name: tmplSnapshot.name,
                is_generating: true,
                timestamp: new Date(),
            }]);
        } else {
            setIsTyping(true);
        }

        const body = {
            template_id: tmplId,
            custom_prompt: cleanInput,
            session_id: activeSessionId ?? null,
            selected_tab: tab,
            preview_only: usePreview,
            ...(referenceS3Keys.length > 0 ? { reference_s3_keys: referenceS3Keys } : {}),
        };

        try {
            if (useStream) {
                let finished = false;
                await streamGenerateFromTemplate(
                    `${apiBase}/rag/generate-from-template/stream`,
                    authHeaders(),
                    body,
                    (event, data) => {
                        if (event === "started" || event === "progress") {
                            const sid = String(data.session_id ?? "");
                            if (event === "started" && sid) {
                                trackAiMedia(sid, String(data.asset_type ?? data.media_type ?? "image"), String(data.template_name ?? categoryLabel));
                            }
                            const mapped = mapTemplateProgressToMessage(data);
                            setMessages(prev => prev.map(m =>
                                m.id === streamMsgId
                                    ? {
                                        ...m,
                                        content: mapped.generation_title,
                                        is_generating: true,
                                        generation_status: mapped.generation_status,
                                        generation_stage: mapped.generation_stage,
                                        generation_title: mapped.generation_title,
                                        generation_detail: mapped.generation_detail,
                                        generation_progress: mapped.generation_progress,
                                        generation_phase: mapped.generation_phase,
                                        generation_media_type:
                                            mapped.generation_media_type ?? m.generation_media_type,
                                        generation_asset_type:
                                            (data.asset_type as string | undefined) ?? m.generation_asset_type,
                                        generation_template_name:
                                            mapped.generation_template_name ?? m.generation_template_name,
                                        show_generation_skeleton: mapped.show_generation_skeleton,
                                        generation_steps: mergeStreamStep(m.generation_steps ?? [], mapped),
                                    }
                                    : m,
                            ));
                        } else if (event === "complete") {
                            finished = true;
                            const generated = data as unknown as GenerateMediaApiResponse;
                            setMessages(prev => prev.filter(m => m.id !== streamMsgId));
                            handleTemplateGenerateResult(generated, categoryLabel);
                        } else if (event === "error") {
                            finished = true;
                            const apiDetail = (data.detail_payload ?? data.detail) as
                                | Record<string, unknown>
                                | undefined;
                            const errText = String(
                                data.detail ?? apiDetail?.message ?? data.message
                                ?? "Generation failed. Please try again.",
                            );
                            const errChips = Array.isArray(apiDetail?.suggestions)
                                ? (apiDetail.suggestions as string[])
                                : ["Try again", "Edit prompt"];
                            maybeEmitQuotaFromStream(apiDetail, errText);
                            setMessages(prev => prev.map(m =>
                                m.id === streamMsgId
                                    ? {
                                        ...m,
                                        content: errText,
                                        is_generating: false,
                                        generation_status: undefined,
                                        generation_stage: undefined,
                                        generation_title: undefined,
                                        generation_detail: undefined,
                                        generation_progress: undefined,
                                        generation_phase: undefined,
                                        show_generation_skeleton: false,
                                        suggestions: errChips,
                                    }
                                    : m,
                            ));
                        }
                    },
                );
                if (!finished) {
                    setMessages(prev => prev.filter(m => m.id !== streamMsgId));
                    setMessages(prev => [...prev, {
                        id: `tmpl-err-${Date.now()}`,
                        role: "assistant" as const,
                        content: "Generation ended unexpectedly. Please try again.",
                        suggestions: ["Try again"],
                        timestamp: new Date(),
                    }]);
                }
            } else {
                // Non-stream template generation has been removed — streaming is the only path.
                setMessages(prev => [...prev,
                    { id: `tmpl-err-${Date.now()}`, role: "assistant" as const, content: "Generation failed. Please try again.", suggestions: ["Try again", "Edit prompt"], timestamp: new Date() },
                ]);
            }
        } catch (err) {
            const msg = err instanceof Error ? err.message : "Generation failed. Please try again.";
            setMessages(prev => {
                const withoutStream = prev.filter(m => m.id !== streamMsgId);
                return [...withoutStream,
                    { id: `tmpl-err-${Date.now()}`, role: "assistant" as const, content: msg, suggestions: ["Try again", "Edit prompt"], timestamp: new Date() },
                ];
            });
        } finally {
            setGeneratingFromTemplate(false);
            setIsTyping(false);
        }
    };


    const handleEditAssetSubmit = async (assetUrl: string) => {
        if (!editingAssetText.trim() || editingAssetLoading) return;
        if (!requireAuthForGeneration()) return;
        setEditingAssetLoading(true);
        const promptText = editingAssetText.trim();
        try {
            const res = await fetch(`${apiBase}/rag/edit-asset`, {
                method: "POST",
                headers: authHeaders(),
                body: JSON.stringify({
                    asset_url: assetUrl,
                    edit_prompt: promptText,
                    session_id: null, // always start a new session for edits
                }),
            });
            if (!res.ok) throw new Error(await apiErrorMessage(res, "Edit failed. Please try again."));

            const data: GenerateMediaApiResponse = await res.json();

            // Close edit panel
            setEditingAssetUrl(null);
            setEditingAssetText("");

            // Switch to the new session and show in chat
            if (data.session_id) {
                setActiveSessionId(data.session_id);
                void loadSessions();
            }

            // Add user message showing original image + edit prompt
            setMessages([
                {
                    id: `edit-user-${Date.now()}`,
                    role: "assistant" as const,
                    content: "",
                    attachmentUrl: assetUrl,
                    timestamp: new Date(),
                },
                {
                    id: `edit-user-prompt-${Date.now()}`,
                    role: "user" as const,
                    content: promptText,
                    timestamp: new Date(),
                },
            ]);

            // Append the edited result as assistant message
            appendMediaAssistantMessage(data, "Flyer");

            // Refresh assets in background
            void fetchMyAssets(assetsActiveTab);
        } catch {
            alert("Edit failed. Please try again.");
        } finally {
            setEditingAssetLoading(false);
        }
    };

    const handleDeleteAsset = async (s3Key: string) => {
        if (!s3Key) { showToast("Cannot delete: no S3 key for this asset."); return; }
        if (!confirm("Permanently delete this asset? This cannot be undone.")) return;
        try {
            const res = await fetch(
                `${apiBase}/rag/my-assets?s3_key=${encodeURIComponent(s3Key)}`,
                { method: "DELETE", headers: authHeaders() },
            );
            if (!res.ok) throw new Error(await apiErrorMessage(res, "Delete failed."));
            setMyAssets(prev => prev.filter(a => a.s3_key !== s3Key));
            showToast("Asset deleted.");
        } catch (e) {
            showToast(e instanceof Error ? e.message : "Delete failed.");
        }
    };

    const handleTabSelect = (tab: SessionTab) => {
        if (tab !== selectedTab) {
            trackingService.tabSwitch(selectedTab, tab, activeSessionId ?? "");
        }
        setSelectedTab(tab);
        void fetchTabQuotas();
        const tabMeta = chatNavTabs.find(t => t.id === tab);
        const mode = (tabMeta?.media_mode === "image" || tabMeta?.media_mode === "video" || tabMeta?.media_mode === "chat")
            ? tabMeta.media_mode as MediaMode
            : tabToMediaMode(tab);
        setMediaMode(mode);
        if (activeTemplate) { setActiveTemplate(null); setInput(""); }
        setFlyerAttachments([]);
        if (tab !== "ai_itinerary") { setItineraryUploadedFiles([]); itineraryEditSessionRef.current = false; }
        if (tab !== "flyers") {
            lastFlyerEditContextRef.current = null;
        }
        setEditingTemplate(null);
        setEditingVideoTemplate(null);
        setEditingItineraryTemplate(null);
        if (tab === "my_assets") void fetchMyAssets("all");
        if (tab === "ai_itinerary") void fetchTemplates("ai_itinerary");
        // AI Slides is disabled ("Coming Soon") — do not fetch slide templates.
        if (tab === "flyers") {
            void fetchTemplates("flyers");
            void fetchMyAssets("all");
        }
        if (tab === "videos") void fetchTemplates("videos");
    };

    const fetchMyAssets = async (tab: string = assetsActiveTab, skipVal = 0, append = false) => {
        if (append) setAssetsLoadingMore(true);
        else setAssetsLoading(true);
        try {
            const res = await fetch(
                `${apiBase}/rag/my-assets?tab=${encodeURIComponent(tab)}&skip=${skipVal}&limit=20`,
                { headers: authHeaders() },
            );
            if (res.ok) {
                const data = (await res.json()) as MyAssetsListResponse;
                const incoming = data.assets ?? [];
                if (append) {
                    setMyAssets(prev => {
                        const existingKeys = new Set(prev.map(a => a.s3_key ?? a.url));
                        return [...prev, ...incoming.filter(a => !existingKeys.has(a.s3_key ?? a.url))];
                    });
                } else {
                    setMyAssets(incoming);
                }
                setAssetsSkip(skipVal + incoming.length);
                setAssetsHasMore(data.has_more ?? false);
                setAssetsActiveTab(data.active_tab ?? tab);
                if (data.tabs?.length) setMyAssetsFilterTabs(data.tabs);
                if (data.header_title) {
                    setMyAssetsPanelHeader({
                        title: data.header_title,
                        subtitle: data.header_subtitle ?? "",
                    });
                }
            }
        } catch {
            // silently ignore
        } finally {
            setAssetsLoading(false);
            setAssetsLoadingMore(false);
        }
    };

    const recordAssetDownload = (url: string, type: "image" | "video" | "itinerary" | "slides", caption?: string, sessionId?: string, s3Key?: string | null) => {
        const apiBase = import.meta.env.VITE_API_BASE_URL || "";
        const token = authStorage.getToken();
        void fetch(`${apiBase}/rag/my-assets/download`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify({
                image_clean: type === "video" || type === "itinerary" ? undefined : url,
                video_url: type === "video" ? url : undefined,
                pdf_url: type === "itinerary" ? url : undefined,
                asset_type: type === "image" ? undefined : type,
                caption,
                session_id: sessionId,
                s3_key: s3Key || undefined,
            }),
        })
            .then((r) => r.json())
            .then((data: { already_downloaded?: boolean; message?: string | null }) => {
                if (data.already_downloaded && data.message) showToast(data.message);
            })
            .catch(() => { /* logging-only — must never block the actual download */ });
    };

    const handleDownloadMedia = async (url: string, type: "image" | "video" | "itinerary" | "slides", caption?: string, sessionId?: string, s3Key?: string | null) => {
        trackingService.flyerAction("downloaded", { mediaType: type });
        recordAssetDownload(url, type, caption, sessionId, s3Key);
        // Itinerary: always download as PDF via backend endpoint
        if (type === "itinerary" && sessionId) {
            const apiBase = import.meta.env.VITE_API_BASE_URL || "";
            const token = authStorage.getToken();
            const pdfUrl = `${apiBase}/rag/itinerary/download/${sessionId}?format=pdf`;
            try {
                const resp = await fetch(pdfUrl, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
                const blob = await resp.blob();
                const a = document.createElement("a");
                a.href = URL.createObjectURL(blob);
                a.download = (caption?.slice(0, 40).replace(/[^a-z0-9]/gi, "_") || "itinerary") + ".pdf";
                document.body.appendChild(a);
                a.click();
                a.remove();
                // Revoking synchronously right after click() can race the browser's
                // download handoff and silently cancel it — give it a beat first.
                setTimeout(() => URL.revokeObjectURL(a.href), 1000);
            } catch {
                window.open(pdfUrl, "_blank");
            }
            return;
        }
        const ext = isPptxMediaUrl(url) ? "pptx" : isPdfMediaUrl(url) ? "pdf" : type === "video" ? "mp4" : "jpg";
        const filename = (caption?.slice(0, 40).replace(/[^a-z0-9]/gi, "_") || `travel-${type}`) + `.${ext}`;
        // Fetching the S3 URL directly from the browser fails CORS whenever the
        // bucket allow-list doesn't include the current origin, which silently
        // opens the file in a new tab instead of downloading it. Route through
        // the backend download-proxy (same-origin as our API) instead.
        const apiBase = import.meta.env.VITE_API_BASE_URL || "";
        const token = authStorage.getToken();
        const proxyUrl = `${apiBase}/rag/download-proxy?url=${encodeURIComponent(url)}&filename=${encodeURIComponent(filename)}`;
        try {
            const resp = await fetch(proxyUrl, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
            const blob = await resp.blob();
            const a = document.createElement("a");
            a.href = URL.createObjectURL(blob);
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            // Revoking synchronously right after click() can race the browser's
            // download handoff and silently cancel it — give it a beat first.
            setTimeout(() => URL.revokeObjectURL(a.href), 1000);
        } catch {
            window.open(url, "_blank");
        }
    };

    // Load a session's messages
    const loadSession = async (sessionId: string, _tab?: SessionTab, source?: "fresh_rag" | "legacy") => {
        setActiveSessionId(sessionId);
        setMessages([]);
        try {
            const data = source === "fresh_rag"
                ? await apiFreshRagGetSession(sessionId)
                : await apiGetSession(sessionId);
            const restoredTab = data.selected_tab ?? "deals";
            setSelectedTab(restoredTab);
            setMediaMode(tabToMediaMode(restoredTab));
            const persistedDeals = (data.agent_state?.last_deals ?? []).map((deal) => mapApiDeal(deal));
            const assistantMessages = (data.messages ?? [])
                .map((msg, index) => ({ msg, index }))
                .filter(({ msg }) => msg.role === "assistant");
            const lastResultAssistantIndex = [...assistantMessages]
                .reverse()
                .find(({ msg }) => (msg.resultsCount ?? 0) > 0)?.index;

            let firstTemplateMsgShown = false;
            const converted: ChatMessage[] = (data.messages ?? []).map((m, i) => {
                const fallbackDeals = (m.searchResults ?? []).map((result) =>
                    mapApiDeal({
                        id: result.id ?? result.dealId ?? "",
                        deal_id: result.dealId ?? result.id,
                        title: result.title ?? "Untitled",
                    }),
                );
                const isVideoMsg = m.type === "video";
                const isItineraryMsg = m.type === "itinerary";
                const mediaAssetUrl = isVideoMsg
                    ? (m.video_url || m.image_url)
                    : isItineraryMsg
                    ? (m.html_url || m.image_url)
                    : m.image_url;
                const styleOptionDeals = m.role === "assistant" && m.style_options?.length
                    ? m.style_options.map((opt, oi) => ({
                        id: `style-${sessionId}-${i}-${oi}`,
                        title: opt.name,
                        video_url: opt.video_url ?? undefined,
                        image: opt.video_url || buildStyleCardImage(opt),
                        category: "Video",
                        item_type: "NEWS",
                    }))
                    : null;
                const mediaDeal = styleOptionDeals ?? (
                    m.role === "assistant" &&
                    (m.type === "image" || m.type === "video" || m.type === "itinerary" || m.type === "slides") &&
                    mediaAssetUrl
                        ? [{
                            id: `media-${sessionId}-${i}`,
                            title: m.message || "AI Media",
                            image: isVideoMsg ? (m.image_url || "") : (isItineraryMsg ? (m.thumbnail_url || m.image_url || "") : (mediaAssetUrl || "")),
                            video_url: isVideoMsg ? mediaAssetUrl : undefined,
                            html_url: isItineraryMsg ? mediaAssetUrl : undefined,
                            thumbnail_url: isItineraryMsg ? (m.thumbnail_url ?? undefined) : undefined,
                            page_urls: isItineraryMsg ? (m.page_urls ?? undefined) : undefined,
                            // Video needs session_id too (not just itinerary) — the
                            // manual overlay editor resolves the platform job via
                            // this session's persisted flyer_state.
                            session_id: (isVideoMsg || isItineraryMsg) ? sessionId : undefined,
                            category: assetTypeToCategory(m.asset_type ?? undefined) || (isVideoMsg ? "Video" : isItineraryMsg ? "Itinerary" : "Flyer"),
                            description: m.s3_key ? `Stored at: ${m.s3_key}` : undefined,
                        }]
                        : []
                );
                // Fresh RAG: use per-turn deals/cross_sell/chips embedded in each message
                const freshRagDeals = restoredTab === "deals" && m.role === "assistant" && Array.isArray(m.deals) && m.deals.length > 0
                    ? m.deals.map(mapApiDeal)
                    : null;
                const freshRagCrossSell = restoredTab === "deals" && m.role === "assistant" && m.cross_sell && Object.keys(m.cross_sell).length > 0
                    ? Object.entries(m.cross_sell)
                        .filter(([, v]) => Array.isArray(v) && v.length > 0)
                        .map(([category, deals]) => ({
                            category,
                            label: category.charAt(0).toUpperCase() + category.slice(1),
                            deals: deals.map(mapApiDeal),
                        }))
                    : null;
                const freshRagChips = restoredTab === "deals" && m.role === "assistant" && Array.isArray(m.chips) && m.chips.length > 0
                    ? (m.chips as string[])
                    : null;

                const restoredDeals = freshRagDeals
                    ?? (i === lastResultAssistantIndex && persistedDeals.length > 0
                        ? persistedDeals
                        : (mediaDeal.length > 0 ? mediaDeal : fallbackDeals));

                const isFirstTemplateMsg =
                    m.role === "assistant" && m.template_name && !mediaAssetUrl && !firstTemplateMsgShown;
                if (isFirstTemplateMsg) firstTemplateMsgShown = true;

                return {
                    id: `${sessionId}-${i}`,
                    role: m.role === "assistant" ? "assistant" : "user",
                    content: m.message,
                    template_name: isFirstTemplateMsg ? m.template_name : undefined,
                    intent: m.intent,
                    deals: m.role === "assistant" && restoredDeals.length > 0 ? restoredDeals : undefined,
                    cross_sell_sections: freshRagCrossSell ?? undefined,
                    timestamp: parseInstant(m.timestamp) ?? new Date(),
                    suggestions: freshRagChips ?? (m.suggestions && m.suggestions.length > 0 ? m.suggestions : undefined),
                    prompt_used: m.prompt_used ?? undefined,
                };
            });
            // Attach persisted gathered_info to last assistant message so
            // lastGatheredInfo (used by EnquiryModal) is pre-filled on session restore.
            const restoredGathered = data.agent_state?.gathered_info;
            if (restoredGathered && Object.keys(restoredGathered).length > 0) {
                for (let i = converted.length - 1; i >= 0; i--) {
                    if (converted[i].role === "assistant") {
                        converted[i] = { ...converted[i], gathered_info: restoredGathered };
                        break;
                    }
                }
            }
            // Merge persisted confirmation messages (sorted by timestamp)
            const persisted = loadConfirmMsgs(sessionId).map((p) => ({
                id: p.id,
                role: "assistant" as const,
                content: p.content,
                timestamp: new Date(p.timestamp),
            }));
            const finalMsgs: ChatMessage[] = persisted.length > 0
                ? [...converted, ...persisted].sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime())
                : converted;

            // Restore cross-sell sections AFTER merge+sort, matched by saved timestamp.
            const csStore = loadCrossSell(sessionId);
            if (csStore && csStore.sections.length > 0) {
                let targetIdx = -1;
                if (csStore.ts) {
                    // Find the assistant message whose timestamp is closest to when cross-sell was generated.
                    const savedTime = new Date(csStore.ts).getTime();
                    let minDiff = Infinity;
                    for (let i = 0; i < finalMsgs.length; i++) {
                        if (finalMsgs[i].role === "assistant") {
                            const diff = Math.abs(finalMsgs[i].timestamp.getTime() - savedTime);
                            if (diff < minDiff) { minDiff = diff; targetIdx = i; }
                        }
                    }
                }
                if (targetIdx === -1) {
                    // Fallback: last assistant message with deals
                    for (let i = finalMsgs.length - 1; i >= 0; i--) {
                        if (finalMsgs[i].role === "assistant" && finalMsgs[i].deals?.length) {
                            targetIdx = i; break;
                        }
                    }
                }
                if (targetIdx >= 0) {
                    finalMsgs[targetIdx] = { ...finalMsgs[targetIdx], cross_sell_sections: csStore.sections };
                }
            }
            setMessages(finalMsgs);
            // Detect upload-based itinerary session from persisted flyer_state
            itineraryEditSessionRef.current = !!(data.flyer_state?.itinerary_edit_mode && (data.flyer_state?.uploaded_itinerary_s3_keys as unknown[])?.length);

            const templateRestored = applyFlyerEditContextFromState(sessionId, data.flyer_state, data.messages);

            // Inject template thumbnail into first user message so it persists on session reload
            const tmplThumb = data.template_context?.thumbnail_url;
            if (tmplThumb) {
                const firstUserIdx = finalMsgs.findIndex((m) => m.role === "user");
                if (firstUserIdx >= 0 && !finalMsgs[firstUserIdx].templateThumbnailUrl) {
                    finalMsgs[firstUserIdx] = { ...finalMsgs[firstUserIdx], templateThumbnailUrl: tmplThumb };
                }
            }

            // Restore template context so subsequent messages route to generate-from-template
            const tmplCtx = data.template_context;
            if (tmplCtx?.template_id) {
                const assetType = tmplCtx.asset_type ?? "flyer";
                const inferredMediaType: "image" | "video" = assetType === "video" ? "video" : "image";
                lastTemplateContextRef.current = {
                    templateId: tmplCtx.template_id,
                    prompt: "",
                    // Minimal stub — only templateId + categoryLabel are used by sendMessage routing
                    template: {
                        id: tmplCtx.template_id,
                        name: tmplCtx.template_name ?? "",
                        category: tmplCtx.category_slug ?? "",
                        asset_type: assetType,
                        media_type: inferredMediaType,
                    } as AnyTemplateItem,
                    categoryLabel: tmplCtx.template_name ?? tmplCtx.category_slug ?? "",
                    mediaType: inferredMediaType,
                };
                // Also mark the flyer edit context as fromTemplate so sendMessage
                // routes to generate-from-template instead of generate-media
                if (!templateRestored) {
                    lastFlyerEditContextRef.current = {
                        s3Key: "",
                        url: undefined,
                        prompt: "",
                        sessionId,
                        fromTemplate: true,
                    };
                } else if (lastFlyerEditContextRef.current) {
                    lastFlyerEditContextRef.current.fromTemplate = true;
                }
            }
        } catch {
            setMessages([{
                id: "err",
                role: "assistant",
                content: "⚠️ Failed to load this conversation.",
                timestamp: new Date(),
            }]);
        }
    };

    // Start new chat
    const startNewChat = () => {
        setActiveSessionId(null);
        setMessages([]);
        setInput("");
        setMediaMode("chat");
        setPromptSidebarOpen(false);
        setPreviewPrompt("");
        setPendingMediaPrompt("");
        lastFlyerEditContextRef.current = null;
        itineraryEditSessionRef.current = false;
        setItineraryUploadedFiles([]);
        textareaRef.current?.focus();
        // Stay on whatever tab the user was already on — this used to force
        // selectedTab back to "deals" every time, so New Chat while on Market
        // Research (or any other non-deals tab) silently kicked you out of it.
        // The chips-refetch effect is keyed on selectedTab, so it won't fire on
        // its own when the tab doesn't change — force a refetch here (same route
        // mapping as that effect) so New Chat always shows the latest
        // personalized chips for the CURRENT tab, not stale ones from before.
        const route = selectedTab === "deals" ? "deals"
            : selectedTab === "ai_itinerary" ? "ai_itinerary"
            : selectedTab === "market_research" ? "market_research"
            : undefined;
        // Clear the previous chips/greeting before refetching, same as the
        // tab-switch effect — avoids a brief flash of the pre-search chips.
        setPersonalizedStarterChips([]);
        setWelcomeTitle("");
        setWelcomeSub("");
        void fetchStarterChips(route);
    };

    // Delete session
    const deleteSession = async (sessionId: string, e: React.MouseEvent, _tab?: SessionTab, source?: "fresh_rag" | "legacy") => {
        e.stopPropagation();
        setDeletingId(sessionId);
        try {
            if (source === "fresh_rag") {
                await apiFreshRagDeleteSession(sessionId);
            } else {
                await apiDeleteSession(sessionId);
            }
            setSessions((prev) => prev.filter((s) => s.session_id !== sessionId));
            if (activeSessionId === sessionId) {
                setActiveSessionId(null);
                setMessages([]);
            }
        } catch {
            // silently fail
        } finally {
            setDeletingId(null);
        }
    };

    const runConfirmedMediaGeneration = async () => {
        if ((!pendingMediaPrompt && !pendingTemplateGen) || isGeneratingMedia || isTyping) return;

        setIsGeneratingMedia(true);
        setIsTyping(true);
        try {
            let generated: GenerateMediaApiResponse;

            const categoryLabel = pendingTemplateGen?.categoryLabel
                ?? (pendingMediaType === "video" ? "Video" : "Flyer");

            if (pendingTemplateGen) {
                const pending = pendingTemplateGen;
                setPendingTemplateGen(null);
                await streamTemplateRegeneration({
                    templateId: pending.templateId,
                    prompt: pending.customPrompt,
                    selectedTab,
                    categoryLabel,
                });
                setPreviewPrompt("");
                setPendingMediaPrompt("");
                setPendingInputImageUrl(null);
                setPendingUploadedFlyerS3Key(null);
                return;
            } else if (pendingMediaType === "image") {
                if (!pendingUploadedFlyerS3Key) {
                    showToast("Attach a flyer with + or choose a template.");
                    return;
                }
                await runFlyerEditStream(
                    pendingMediaPrompt,
                    pendingUploadedFlyerS3Key,
                    pendingInputImageUrl ?? undefined,
                    { sessionId: activeSessionId ?? undefined },
                );
                return;
            } else {
                generated = await apiGenerateMedia(
                    pendingMediaPrompt,
                    pendingMediaType,
                    activeSessionId ?? undefined,
                    selectedTab,
                    false,
                    pendingInputImageUrl,
                    null,
                );
            }

            if (generated.session_id && generated.session_id !== activeSessionId) {
                setActiveSessionId(generated.session_id);
                void loadSessions();
            }

            appendMediaAssistantMessage(generated, categoryLabel);
            setPreviewPrompt("");
            setPendingMediaPrompt("");
            setPendingInputImageUrl(null);
            setPendingUploadedFlyerS3Key(null);
        } catch {
            setMessages((prev) => [
                ...prev,
                {
                    id: `err-media-${Date.now()}`,
                    role: "assistant",
                    content: "⚠️ Media generation failed. Please try again.",
                    timestamp: new Date(),
                },
            ]);
        } finally {
            setIsGeneratingMedia(false);
            setIsTyping(false);
        }
    };

    // Send message — uses /rag/chat in chat mode and /rag/generate-media in media modes.
    // directQuery bypasses the input state (used by deal interest button)
    const sendMessage = async (directQuery?: string) => {
        const query = (directQuery ?? input).trim();
        const flyerAttachActive = showFlyerMediaAttach(selectedTab, !!activeTemplate);
        const hasFlyerAttach = flyerAttachActive && flyerAttachments.length > 0;
        if ((!query && !hasFlyerAttach) || isTyping || flyerRefsUploading) return;
        if (!requireAuthForGeneration()) return;

        // ── Regen intent: restore last flyer/video template context and regenerate ──
        // Matches any natural expression of "do that again" — try again, build again,
        // create again, make another, go ahead, one more time, just do it, etc.
        const REGEN_RE = new RegExp(
            // explicit action + again/more
            "\\b(generate|create|build|make|run|do|start|produce|render|try|craft|design)(\\s+it|\\s+one|\\s+another|\\s+that|\\s+this)?\\s+(again|more|once\\s+more|one\\s+more\\s+time)\\b" +
            "|\\b(regenerate|re-generate|re-create|re-run|re-do|redo|retry|re-try)\\b" +
            // "try again", "go again"
            "|\\b(try|go|run|start|do)\\s+again\\b" +
            // "one more time", "once more"
            "|\\bone\\s+more\\s+time\\b|\\bonce\\s+more\\b" +
            // "same one again", "use same / last / previous"
            "|\\bsame\\s+(template|prompt|one|thing)\\b" +
            "|\\buse\\s+(same|the\\s+same|last|previous|that)\\b" +
            // short imperatives that clearly mean "go"
            "|\\b(go\\s+ahead|just\\s+do\\s+it|proceed|start\\s+generat|let'?s\\s+go|yes\\s+go|yep\\s+go|yeah\\s+go|sure\\s+go)\\b" +
            // bare "again" as the whole message or with filler
            "|^\\s*(again|retry|redo|re-do|re-run)\\s*$" +
            // "yes generate / yes create / yes make"
            "|\\b(yes|yep|yeah|sure|ok|okay|alright)\\s*(,\\s*)?(generate|create|build|make|run|start)\\b",
            "i",
        );
        const flyerEditCtx = lastFlyerEditContextRef.current;
        if (
            REGEN_RE.test(query)
            && flyerEditCtx?.s3Key
            && !flyerEditCtx.fromTemplate
            && showFlyerMediaAttach(selectedTab, !!activeTemplate)
            && !generatingFromTemplate
        ) {
            setInput("");
            await runFlyerEditStream(
                flyerEditCtx.prompt,
                flyerEditCtx.s3Key,
                flyerEditCtx.url,
                { userVisibleText: query, sessionId: flyerEditCtx.sessionId ?? activeSessionId ?? undefined },
            );
            return;
        }

        const lastCtx = lastTemplateContextRef.current;
        if (REGEN_RE.test(query) && lastCtx && !generatingFromTemplate) {
            setInput("");
            setMessages(prev => [...prev, {
                id: `user-${Date.now()}`, role: "user" as const,
                content: query, timestamp: new Date(),
            }]);
            // Re-trigger generation with the last template + prompt
            setGeneratingFromTemplate(true);
            setIsTyping(true);
            try {
                await streamTemplateRegeneration({
                    templateId: lastCtx.templateId,
                    prompt: lastCtx.prompt,
                    selectedTab,
                    categoryLabel: lastCtx.categoryLabel,
                });
            } finally {
                setGeneratingFromTemplate(false);
                setIsTyping(false);
            }
            return;
        }

        const attachCount = hasFlyerAttach ? flyerAttachments.length : 0;
        const displayQuery = query
            || (hasFlyerAttach ? "Edit and improve my uploaded flyer image." : "");
        const userMsg: ChatMessage = {
            id: `user-${Date.now()}`,
            role: "user",
            content: attachCount
                ? `${displayQuery}${displayQuery ? "\n" : ""}📎 ${attachCount} image(s)`
                : displayQuery,
            timestamp: new Date(),
        };

        setMessages((prev) => [...prev, userMsg]);
        setInput("");
        trackingService.ragQuery(query, activeSessionId ?? "", { tab: selectedTab });
        const attachmentsSnapshot = [...flyerAttachments];
        setFlyerAttachments([]);
        setIsTyping(true);

        try {
            if (mediaMode !== "chat") {
                const mediaType: "image" | "video" = mediaMode === "video" ? "video" : "image";
                const sessionForMedia = activeSessionId ?? undefined;

                // ═══════════════════════════════════════════════════════════
                // FLYERS TAB — upload (+) or template-based flyer edit stream
                // ═══════════════════════════════════════════════════════════
                if (selectedTab === "flyers") {
                    // Case 1: user attached a flyer image via + button
                    if (attachmentsSnapshot.length > 0) {
                        let uploadedFlyerS3Key: string | undefined;
                        let inputImageUrl: string | undefined;
                        let resolvedSession = sessionForMedia;
                        try {
                            const resolved = await resolveFlyerUploadForEdit(attachmentsSnapshot);
                            uploadedFlyerS3Key = resolved.uploadedFlyerS3Key;
                            inputImageUrl = resolved.inputImageUrl;
                            if (resolved.sessionId) resolvedSession = resolved.sessionId;
                        } catch (err) {
                            showToast(err instanceof Error ? err.message : "Image upload failed.");
                            setIsTyping(false);
                            return;
                        }
                        if (!uploadedFlyerS3Key) {
                            showToast("Could not save your flyer for editing.");
                            setIsTyping(false);
                            return;
                        }
                        // Update user message to show the actual S3 presigned URL so it's visible in session
                        if (inputImageUrl) {
                            setMessages(prev => prev.map(m =>
                                m.id === userMsg.id
                                    ? { ...m, attachmentUrl: inputImageUrl }
                                    : m,
                            ));
                        }
                        if (resolvedSession) { setActiveSessionId(resolvedSession); void loadSessions(); }
                        await runFlyerEditStream(displayQuery, uploadedFlyerS3Key, inputImageUrl, {
                            skipUserMessage: true, userVisibleText: userMsg.content, sessionId: resolvedSession,
                        });
                        return;
                    }

                    // Case 2: follow-up on a template-generated flyer — use stream for live progress
                    const flyerCtx = lastFlyerEditContextRef.current;
                    const editSessionId = flyerCtx?.sessionId ?? sessionForMedia ?? activeSessionId ?? undefined;
                    const tmplCtx = lastTemplateContextRef.current;
                    // A template session with no completed generation yet (still gathering
                    // fields via the "Template Magic" confirm card, e.g. after reopening a
                    // session before ever tapping Generate) has no flyerCtx at all — any typed
                    // reply here is just continuing that gather conversation, not a "regenerate"
                    // of an existing image, so it shouldn't require regen-phrase wording. Once a
                    // flyer has actually been generated, keep the stricter regen-phrase gate so
                    // plain edit text (e.g. "use my phone and contact") doesn't accidentally
                    // re-trigger a full regeneration.
                    const hasGeneratedFlyer = !!flyerCtx?.s3Key;
                    const isTemplateFollowup = !!tmplCtx?.templateId && !generatingFromTemplate && (
                        !hasGeneratedFlyer || (!!flyerCtx?.fromTemplate && REGEN_RE.test(query))
                    );
                    if (isTemplateFollowup) {
                        setGeneratingFromTemplate(true);
                        setIsTyping(true);
                        const streamMsgId = `tmpl-flyer-followup-${Date.now()}`;
                        const followupLabel = hasGeneratedFlyer ? "Regenerating" : "Generating";
                        setMessages(prev => [...prev, {
                            id: streamMsgId, role: "assistant" as const,
                            content: `${followupLabel} your ${tmplCtx.categoryLabel.toLowerCase() || "flyer"}…`,
                            generation_title: followupLabel, generation_progress: 5,
                            generation_phase: "prep", generation_media_type: tmplCtx.mediaType,
                            generation_asset_type: tmplCtx.template ? (tmplCtx.template as AnyTemplateItem).asset_type : "flyer",
                            is_generating: true, timestamp: new Date(),
                        }]);
                        try {
                            let finished = false;
                            await streamGenerateFromTemplate(
                                `${apiBase}/rag/generate-from-template/stream`,
                                authHeaders(),
                                { template_id: tmplCtx.templateId, custom_prompt: displayQuery, session_id: activeSessionId ?? null, selected_tab: selectedTab, preview_only: false },
                                (event, data) => {
                                    if (event === "started" || event === "progress") {
                                        const mapped = mapTemplateProgressToMessage(data);
                                        setMessages(prev => prev.map(m => m.id === streamMsgId ? {
                                            ...m, content: mapped.generation_title, is_generating: true,
                                            generation_status: mapped.generation_status, generation_stage: mapped.generation_stage,
                                            generation_title: mapped.generation_title, generation_detail: mapped.generation_detail,
                                            generation_progress: mapped.generation_progress, generation_phase: mapped.generation_phase,
                                            show_generation_skeleton: mapped.show_generation_skeleton,
                                            generation_steps: mergeStreamStep(m.generation_steps ?? [], mapped),
                                        } : m));
                                    } else if (event === "complete") {
                                        finished = true;
                                        setMessages(prev => prev.filter(m => m.id !== streamMsgId));
                                        handleTemplateGenerateResult(data as unknown as GenerateMediaApiResponse, tmplCtx.categoryLabel);
                                    } else if (event === "error") {
                                        finished = true;
                                        setMessages(prev => prev.map(m => m.id === streamMsgId ? {
                                            ...m, content: String(data.detail ?? data.message ?? "Generation failed."),
                                            is_generating: false, generation_status: undefined, generation_stage: undefined,
                                            generation_title: undefined, generation_detail: undefined,
                                            generation_progress: undefined, generation_phase: undefined,
                                            show_generation_skeleton: false, suggestions: ["Try again", "Edit prompt"],
                                        } : m));
                                    }
                                },
                            );
                            if (!finished) {
                                setMessages(prev => prev.filter(m => m.id !== streamMsgId));
                                setMessages(prev => [...prev, { id: `tmpl-err-${Date.now()}`, role: "assistant" as const, content: "Generation ended unexpectedly.", suggestions: ["Try again"], timestamp: new Date() }]);
                            }
                        } catch (err) {
                            setMessages(prev => [...prev.filter(m => m.id !== streamMsgId), {
                                id: `tmpl-err-${Date.now()}`, role: "assistant" as const,
                                content: err instanceof Error ? err.message : "Generation failed. Please try again.",
                                suggestions: ["Try again", "Edit prompt"], timestamp: new Date(),
                            }]);
                        } finally { setGeneratingFromTemplate(false); setIsTyping(false); }
                        return;
                    }

                    // Case 3: follow-up edit on a previously uploaded flyer (no template).
                    // Gated on s3Key alone — an active sessionId isn't enough, since every
                    // reopened session (including template-only ones with no uploaded
                    // image) has one, which used to route template sessions here too.
                    if (flyerCtx?.s3Key) {
                        await runFlyerEditStream(displayQuery, flyerCtx?.s3Key, flyerCtx?.url, {
                            skipUserMessage: true, userVisibleText: userMsg.content, sessionId: editSessionId,
                        });
                        return;
                    }

                    showToast("Attach a flyer with + to edit, or pick a template to create a new one.");
                    setIsTyping(false);
                    return;
                }

                // ═══════════════════════════════════════════════════════════
                // AI ITINERARY TAB
                // ═══════════════════════════════════════════════════════════
                if (selectedTab === "ai_itinerary") {
                    // Follow-up on an upload-based itinerary session
                    if (itineraryUploadedFiles.length > 0 || flyer_state_has_itinerary_upload) {
                        setIsTyping(false);
                        void handleItineraryUploadGenerate(displayQuery);
                        return;
                    }
                    // Follow-up on a template-based itinerary session
                    const itinTmplCtx = lastTemplateContextRef.current;
                    if (itinTmplCtx?.templateId && messages.length > 0 && REGEN_RE.test(query)) {
                        setGeneratingFromTemplate(true);
                        setIsTyping(true);
                        try {
                            await streamGenerateFromTemplate(
                                `${apiBase}/rag/generate-from-template/stream`,
                                authHeaders(),
                                { template_id: itinTmplCtx.templateId, custom_prompt: displayQuery, session_id: activeSessionId ?? null, selected_tab: "ai_itinerary", preview_only: false },
                                (event, data) => {
                                    if (event === "complete") {
                                        handleTemplateGenerateResult(data as unknown as GenerateMediaApiResponse, itinTmplCtx.categoryLabel || "Itinerary");
                                    } else if (event === "error") {
                                        setMessages(prev => [...prev, { id: `itin-err-${Date.now()}`, role: "assistant" as const, content: String(data.detail ?? data.message ?? "Generation failed."), suggestions: ["Try again"], timestamp: new Date() }]);
                                    }
                                },
                            );
                        } catch (err) {
                            setMessages(prev => [...prev, { id: `itin-err-${Date.now()}`, role: "assistant" as const, content: err instanceof Error ? err.message : "Generation failed.", suggestions: ["Try again"], timestamp: new Date() }]);
                        } finally { setGeneratingFromTemplate(false); setIsTyping(false); }
                        return;
                    }
                    // No context at all — guard empty state
                    if (messages.length === 0) {
                        showToast("Upload your itinerary with ＋ or pick a template above to get started.");
                        setIsTyping(false);
                        return;
                    }
                    // Has messages but no known context (e.g. needs_followup reply) — fall through to chat
                }

                // ═══════════════════════════════════════════════════════════
                // AI SLIDES TAB — template-only, no upload
                // ═══════════════════════════════════════════════════════════
                if (selectedTab === "ai_slide") {
                    showToast("AI Slides is coming soon.");
                    setIsTyping(false);
                    return;
                }

                // ═══════════════════════════════════════════════════════════
                // VIDEO TAB — template or free-form video generation
                // ═══════════════════════════════════════════════════════════
                const preview = await apiGenerateMedia(
                    displayQuery,
                    mediaType,
                    sessionForMedia,
                    selectedTab,
                    env.flyerPreviewEnabled,
                    null,
                    null,
                );
                const sessionId = preview.session_id ?? activeSessionId ?? "";
                if (sessionId && sessionId !== activeSessionId) {
                    setActiveSessionId(sessionId);
                    void loadSessions();
                }

                if (preview.needs_followup) {
                    // Two-pass video flow's mid-conversation questions (approval,
                    // style) carry a real video_url alongside the follow-up
                    // question — show it, don't drop it like a plain text-only ask.
                    if (preview.image_url || preview.video_url) {
                        appendMediaAssistantMessage(preview, mediaType === "video" ? "Video" : "Flyer");
                        return;
                    }
                    setMessages((prev) => [
                        ...prev,
                        {
                            id: `ai-media-followup-${Date.now()}`,
                            role: "assistant",
                            content: preview.caption || "Please share a few more details.",
                            suggestions: preview.suggestions?.length ? preview.suggestions : undefined,
                            timestamp: new Date(),
                        },
                    ]);
                    return;
                }

                if (preview.image_url || preview.video_url) {
                    appendMediaAssistantMessage(preview, mediaType === "video" ? "Video" : "Flyer");
                    return;
                }

                setPreviewPrompt(preview.prompt_used || "");
                setPendingMediaPrompt(displayQuery);
                setPendingInputImageUrl(null);
                setPendingUploadedFlyerS3Key(null);
                setPendingMediaType(mediaType);
                setPendingTemplateGen(null);
                setPromptSidebarOpen(true);
                appendMediaAssistantMessage(preview, mediaType === "video" ? "Video" : "Flyer", { isPreview: true });
                return;
            }
            {
                // Unified chat: no category filter — backend auto-classifies intent.
                // Build full conversation history for context
                const history = messages.map((m) => ({
                    role: m.role === "user" ? "user" : "assistant",
                    content: m.content,
                }));

                if (selectedTab === "market_research") {
                    // Market Research SSE streaming path — real inventory grounding +
                    // destination extraction happen server-side before the first token
                    // (backend/app/modules/rag/market_research/routes.py), then Perplexity's
                    // answer streams in live. No deals/dmc/chips here, plain text only.
                    const streamMsgId = `ai-${Date.now()}`;
                    let streamText = "";

                    // Kill typing indicator — streaming bubble IS the indicator
                    setIsTyping(false);
                    setMessages(prev => [...prev, {
                        id: streamMsgId,
                        role: "assistant" as const,
                        content: "",
                        timestamp: new Date(),
                        streaming: true,
                    }]);

                    try {
                        await streamMarketResearchChat(
                            query,
                            activeSessionId ?? undefined,
                            (sid) => {
                                if (sid && sid !== activeSessionId) {
                                    setActiveSessionId(sid);
                                }
                            },
                            (text) => {
                                streamText += text;
                                const cleaned = stripCitationMarkers(streamText);
                                setMessages(prev => prev.map(m =>
                                    m.id === streamMsgId ? { ...m, content: cleaned } : m
                                ));
                            },
                            (_stage, label) => {
                                setMessages(prev => prev.map(m =>
                                    m.id === streamMsgId ? { ...m, researchStageLabel: label } : m
                                ));
                            },
                        );
                    } finally {
                        setMessages(prev => prev.map(m =>
                            m.id === streamMsgId ? { ...m, streaming: false } : m
                        ));
                        void loadSessions();
                    }
                    return;
                }

                if (selectedTab === "deals") {
                    // Fresh RAG SSE streaming path
                    const streamMsgId = `ai-${Date.now()}`;
                    let streamText = "";
                    let metadataParsed: Record<string, unknown> = {};
                    let freshSessionId: string | undefined;

                    // Kill typing indicator — streaming bubble IS the indicator
                    setIsTyping(false);
                    setMessages(prev => [...prev, {
                        id: streamMsgId,
                        role: "assistant" as const,
                        content: "",
                        timestamp: new Date(),
                        streaming: true,
                    }]);

                    try {
                        await streamFreshRagChat(
                            query,
                            activeSessionId ?? undefined,
                            selectedTab,
                            (sid) => {
                                // Just track session id — don't reload yet, session not saved to DB until done
                                if (sid && sid !== activeSessionId) {
                                    freshSessionId = sid;
                                    setActiveSessionId(sid);
                                }
                            },
                            (text) => {
                                streamText += text;
                                setMessages(prev => prev.map(m =>
                                    m.id === streamMsgId ? { ...m, content: streamText } : m
                                ));
                            },
                            (meta) => { metadataParsed = meta; },
                            (_stage, label) => {
                                setMessages(prev => prev.map(m =>
                                    m.id === streamMsgId ? { ...m, researchStageLabel: label } : m
                                ));
                            },
                        );
                    } finally {
                        // On error this fires before the outer catch adds its own ⚠️ bubble —
                        // without it, this placeholder's `streaming: true` never clears and it
                        // stays stuck showing the stage label / dots forever.
                        setMessages(prev => prev.map(m =>
                            m.id === streamMsgId ? { ...m, streaming: false } : m
                        ));
                    }

                    // Session is now saved to MongoDB (pipeline saves after done frame)
                    if (freshSessionId) void loadSessions();

                    // eslint-disable-next-line no-console
                    console.log("[boost] raw streamed deals", ((metadataParsed.deals as Deal[]) ?? []).map(d => ({
                        title: d.title, is_boosted: d.is_boosted, boost_campaign_id: d.boost_campaign_id,
                    })));
                    const resultDeals: Deal[] = ((metadataParsed.deals as Deal[]) ?? []).map(mapApiDeal);
                    const rawCrossSell = (metadataParsed.cross_sell ?? {}) as Record<string, Deal[]>;
                    const crossSellSections = Object.entries(rawCrossSell)
                        .filter(([, v]) => Array.isArray(v) && v.length > 0)
                        .map(([category, deals]) => ({
                            category,
                            label: category.charAt(0).toUpperCase() + category.slice(1),
                            deals: deals.map(mapApiDeal),
                        }));
                    const chips = (metadataParsed.chips as string[]) ?? [];

                    setMessages(prev => prev.map(m =>
                        m.id === streamMsgId
                            ? {
                                ...m,
                                content: (metadataParsed.generated_text as string) || streamText || "Here are the results I found for you.",
                                deals: resultDeals.length > 0 ? resultDeals : undefined,
                                cross_sell_sections: crossSellSections.length > 0 ? crossSellSections : undefined,
                                suggestions: chips.length > 0 ? chips : undefined,
                                gathered_info: metadataParsed.gathered_info as Record<string, unknown> | undefined,
                                streaming: false,
                            }
                            : m
                    ));
                    return;
                }

                const result = await apiChat(
                    query,
                    activeSessionId ?? undefined,
                    history,
                    tabToCategoryFilter(selectedTab),
                    selectedTab,
                );
                const sessionId: string = result.session_id ?? activeSessionId ?? "";
                const metadata = (result.processing_metadata ?? {}) as ChatProcessingMetadata;
                const profile = metadata.personalization_profile;
                const profileChips = buildPersonalizedSuggestions(profile);

                if (profileChips.length) {
                    setPersonalizedStarterChips(profileChips);
                }

                if (sessionId && sessionId !== activeSessionId) {
                    setActiveSessionId(sessionId);
                    void loadSessions();
                }

                // Map deals — include all CTA fields from backend
                const resultDeals: Deal[] = (result.deals ?? []).map((d: Deal) => mapApiDeal(d));

                // Resolve cta_deal — specific deal for CTA panel
                const ctaDealData = result.cta_deal as Deal | null;
                const ctaDeal = ctaDealData
                    ? resultDeals.find((d) => d.deal_id === ctaDealData.deal_id) ?? mapApiDeal(ctaDealData) ?? resultDeals[0]
                    : (resultDeals[0] ?? null);

                const gatheredFromBackend = (
                    metadata.gathered_info_snapshot
                    ?? (result as Record<string, unknown>).gathered_info_snapshot
                    ?? (result as Record<string, unknown>).gathered_info
                    ?? undefined
                ) as Record<string, unknown> | undefined;

                // Stage 3 — harvest recommended DMC cards from the backend
                // response. Each gets rendered below the deals rail with its
                // own Send Enquiry button that pre-fills `dealer_id` and no
                // `deal_id`. Empty when the turn has no DMC context.
                const rawDmcs = Array.isArray((result as { dmcs?: unknown[] }).dmcs)
                    ? ((result as { dmcs: unknown[] }).dmcs as Array<Record<string, unknown>>)
                    : [];
                const dmcs: DmcCard[] = rawDmcs
                    .map((d): DmcCard | null => {
                        const dealerId = String(
                            d?.dealer_id ?? d?.id ?? d?._id ?? "",
                        );
                        if (!dealerId) return null;
                        return {
                            dealer_id: dealerId,
                            seller_name: String(
                                d?.seller_name ?? d?.name ?? d?.organization_name ?? "DMC Seller",
                            ),
                            is_verified: Boolean(d?.is_verified),
                            verified_name: d?.verified_name as string | undefined,
                            covered: Array.isArray(d?.covered)
                                ? (d.covered as string[])
                                : undefined,
                            destination: d?.destination as string | string[] | undefined,
                            destinations: d?.destinations as string[] | undefined,
                            cities: d?.cities as string[] | undefined,
                            countries: d?.countries as string[] | undefined,
                            coverage_areas: d?.coverage_areas as string[] | undefined,
                            segments: d?.segments as string[] | undefined,
                            tags: d?.tags as string[] | undefined,
                            city: d?.city as string | undefined,
                            country: d?.country as string | undefined,
                            avatar_url: d?.avatar_url as string | undefined,
                            profile_image: d?.profile_image as string | undefined,
                            seller_match: d?.seller_match as
                                | { score?: number; reasons?: string[] }
                                | undefined,
                        };
                    })
                    .filter((d): d is DmcCard => d !== null);

                const crossSellSections = Array.isArray(
                    (result as { cross_sell_sections?: unknown }).cross_sell_sections,
                )
                    ? (result as { cross_sell_sections: Array<{ category: string; label: string; deals: Deal[] }> }).cross_sell_sections
                    : [];

                const aiMsg: ChatMessage = {
                    id: `ai-${Date.now()}`,
                    role: "assistant",
                    content: result.message ?? result.response ?? "Here are the results I found for you.",
                    deals: resultDeals,
                    dmcs,
                    cross_sell_sections: crossSellSections,
                    intent: result.intent,
                    stage: (result.stage as "SEARCH" | "INTERESTED" | "CONNECT") ?? "SEARCH",
                    show_cta: result.show_cta ?? false,
                    cta_deal: ctaDeal ?? null,
                    suggestions: Array.from(new Set([...(result.suggestions ?? []), ...profileChips])).slice(0, 8),
                    timestamp: new Date(),
                    gathered_info: gatheredFromBackend,
                };

                setMessages((prev) => [...prev, aiMsg]);
                // Persist cross-sell sections so they survive session switches
                const _csSid = result.session_id ?? activeSessionId;
                if (_csSid && crossSellSections.length > 0) {
                    saveCrossSell(_csSid, crossSellSections, aiMsg.timestamp);
                    void analyticsService.trackEvent({
                        event_type: "cross_sell_carousel_viewed",
                        source: "rag_chat",
                        item_id: _csSid,
                        item_type: "cross_sell",
                        session_id: _csSid,
                        metadata: { categories: crossSellSections.map(s => s.category) },
                    });
                }
            }
        } catch (err) {
            const errMsg = err instanceof Error && err.message && err.message !== "Stream error"
                ? err.message
                : "We are experiencing high volume right now. Please try again in a moment.";
            setMessages((prev) => [
                ...prev,
                {
                    id: `err-${Date.now()}`,
                    role: "assistant",
                    content: `⚠️ ${errMsg}`,
                    timestamp: new Date(),
                },
            ]);
        } finally {
            setIsTyping(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            if (activeTemplate) {
                void submitTemplateGeneration();
            } else if (selectedTab === "ai_itinerary" && !activeTemplate && itineraryUploadedFiles.length > 0) {
                void handleItineraryUploadGenerate(input.trim());
            } else {
                void sendMessage();
            }
        }
    };

    const sessionPreview = (s: Session) => {
        if (s.preview) return s.preview;
        if (s.first_message) return s.first_message;
        if (s.selected_tab) {
            return chatNavTabs.find(t => t.id === s.selected_tab)?.label ?? TAB_LABELS[s.selected_tab];
        }
        return "New conversation";
    };

    const activeSessionLabel = useMemo(() => {
        if (!activeSessionId) return "";
        const activeSession = sessions.find((s) => s.session_id === activeSessionId);
        if (!activeSession) {
            return chatNavTabs.find(t => t.id === selectedTab)?.label ?? TAB_LABELS[selectedTab];
        }
        return sessionPreview(activeSession);
    }, [activeSessionId, sessions, selectedTab]);

    // ── Auth loading / unauthenticated gate ──────────────────────────────────
    if (!isAuthReady) {
        // Still bootstrapping — show a neutral loading screen instead of flashing the gate
        return (
            <div className="rag-gate">
                <div className="rag-gate-card">
                    <div className="rag-gate-icon">✈️</div>
                    <p style={{ color: "#888", fontSize: 14 }}>Loading…</p>
                </div>
            </div>
        );
    }

    // Anonymous visitors can browse and type in the chat UI; generation actions
    // are gated per-action via requireAuthForGeneration() + LoginSignupModal.

    // ── Main layout ───────────────────────────────────────────────────────────
    return (
        <>
            <div className={`rag-root${sidebarOpen ? " sidebar-mobile-open" : ""}`}>
                {sidebarOpen && (
                    <button
                        type="button"
                        className="rag-sidebar-backdrop"
                        aria-label="Close menu"
                        onClick={() => setSidebarOpen(false)}
                    />
                )}
                {/* ── Sidebar ── */}
                <aside className={`sidebar ${sidebarOpen ? "open" : "collapsed"}`}>
                    <div className="sidebar-header">
                        <button
                            type="button"
                            className="sidebar-brand-link"
                            onClick={() => navigate("/")}
                            title="Home"
                        >
                            <span className="sidebar-brand-mark">G</span>
                            <span className="sidebar-title-wrap">
                                <span className="sidebar-title">Gullivr AI</span>
                                <span className="sidebar-tagline">Travel workspace</span>
                            </span>
                        </button>
                    </div>
                    <div style={{ padding: "0 10px" }}>
                        <button type="button" className="new-chat-btn" onClick={startNewChat}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                            </svg>
                            New Chat
                        </button>
                    </div>

                    <div
                        className="sessions-list"
                        ref={sessionsListRef}
                        onScroll={(e) => {
                            const el = e.currentTarget;
                            const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
                            if (nearBottom && sessionsHasMore && !loadingMoreSessions) {
                                void loadMoreSessions();
                            }
                        }}
                    >
                        {loadingSessions && (
                            <div className="sessions-empty">Loading sessions…</div>
                        )}
                        {!loadingSessions && sessions.length === 0 && (
                            <div className="sessions-empty">No previous chats yet.<br />Start a new conversation!</div>
                        )}
                        {sessions.map((s) => (
                            <div
                                key={s.session_id}
                                className={`session-item ${activeSessionId === s.session_id ? "active" : ""}`}
                                onClick={() => void loadSession(s.session_id, s.selected_tab, s.source)}
                            >
                                <div className="session-item-row">
                                    <span className="session-preview">{sessionPreview(s)}</span>
                                    <span className="session-date">
                                        {formatISTSessionDayLabel(s.created_at)}{" "}
                                        {formatISTTimeShort(s.last_message_at || s.updated_at || s.created_at)}
                                    </span>
                                </div>
                                <div className="session-item-row" style={{ marginTop: 2 }}>
                                    <span className="session-count">
                                        {s.message_count ? `${s.message_count} messages` : ""}
                                    </span>
                                    <button
                                        type="button"
                                        className="session-del-btn"
                                        onClick={(e) => void deleteSession(s.session_id, e, s.selected_tab, s.source)}
                                        disabled={deletingId === s.session_id}
                                        title="Delete session"
                                    >
                                        {deletingId === s.session_id ? "…" : "🗑"}
                                    </button>
                                </div>
                            </div>
                        ))}
                        {loadingMoreSessions && (
                            <div className="sessions-empty" style={{ padding: "8px 0", fontSize: 12 }}>Loading more…</div>
                        )}
                    </div>

                    <div className="sidebar-footer">
                        {isAuthenticated ? (
                            <>
                                <button
                                    type="button"
                                    className="sidebar-profile"
                                    onClick={() => navigate("/profile")}
                                    title="Your profile"
                                >
                                    <div className="sidebar-avatar">{(user?.name ?? user?.email ?? "U")[0].toUpperCase()}</div>
                                    <div className="sidebar-user">
                                        <div className="sidebar-username">
                                            {user?.name ?? user?.email ?? "User"}
                                            {user?.is_verified && (
                                                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" style={{display:"inline-block",verticalAlign:"middle",marginLeft:"5px",flexShrink:0}} aria-label="Verified">
                                                    <circle cx="7" cy="7" r="7" fill="#22c55e"/>
                                                    <path d="M4 7l2 2 4-4" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                                </svg>
                                            )}
                                        </div>
                                        <div className="sidebar-role">{user?.role ?? "traveller"}</div>
                                    </div>
                                </button>
                                <div className="sidebar-footer-actions">
                                    <button type="button" className="sidebar-icon-btn" onClick={() => navigate("/profile")}>
                                        Profile
                                    </button>
                                    <button type="button" className="sidebar-icon-btn sidebar-logout" onClick={logout} title="Logout">
                                        Sign out
                                    </button>
                                </div>
                            </>
                        ) : (
                            <button type="button" className="sidebar-icon-btn" style={{ width: "100%" }} onClick={() => setShowAuthModal(true)}>
                                Login / Sign up
                            </button>
                        )}
                    </div>
                </aside>

                <div className="chat-main-wrap">
                {/* ── Chat panel ── */}
                <div className="chat-panel">
                    {/* Header: top nav bar + tab strip */}
                    <header>
                        <div className="chat-top-bar">
                            <button
                                type="button"
                                className="toggle-sidebar-btn"
                                onClick={() => setSidebarOpen((v) => !v)}
                                title="Toggle sidebar"
                                aria-expanded={sidebarOpen}
                            >
                                ☰
                            </button>
                            <div className="chat-top-bar-title">
                                <h1>Gullivr AI</h1>
                                <div className="chat-top-bar-sub">Search deals · Create flyers · Plan itineraries</div>
                            </div>
                            {activeSessionId && <span className="session-badge">{activeSessionLabel}</span>}
                            {isSuperAdmin(user?.role) && (
                                <button
                                    type="button"
                                    className={`rag-dev-toggle${templatesIsDevelopment ? " rag-dev-toggle--on" : ""}`}
                                    onClick={() => {
                                        setTemplatesIsDevelopment((v) => {
                                            const next = !v;
                                            if (!next) {
                                                setEditingTemplate(null);
                                                setEditingVideoTemplate(null);
                                                setEditingItineraryTemplate(null);
                                            }
                                            return next;
                                        });
                                    }}
                                    title="Show template development tools (create & edit)"
                                    aria-pressed={templatesIsDevelopment}
                                >
                                    <span className="rag-dev-toggle-track" aria-hidden="true">
                                        <span className="rag-dev-toggle-knob" />
                                    </span>
                                    <span className="rag-dev-toggle-label">Dev mode</span>
                                </button>
                            )}
                            <div className="chat-header-nav">
                                <button type="button" className="top-nav-btn top-nav-btn--hide-md" onClick={() => navigate("/enquiries")}>Enquiries</button>
                                <button type="button" className="top-nav-btn" onClick={() => navigate("/")}>Explore</button>
                                <button type="button" className="top-nav-btn" onClick={() => navigate("/dm")}>Messages</button>
                                <NotificationBell />
                            </div>
                        </div>
                        <div className="tab-strip">
                            {(chatNavTabs.length > 0
                                ? chatNavTabs.map(t => ({ key: t.id as SessionTab, icon: t.icon, label: t.label }))
                                : [
                                    { key: "flyers" as SessionTab, icon: "🖼️", label: "Flyers" },
                                    { key: "videos" as SessionTab, icon: "🎬", label: "Videos" },
                                    { key: "deals" as SessionTab, icon: "✈️", label: "Deals" },
                                    { key: "market_research" as SessionTab, icon: "📊", label: "Market Research" },
                                    { key: "ai_itinerary" as SessionTab, icon: "🗺️", label: "AI Itinerary" },
                                    { key: "ai_slide" as SessionTab, icon: "📽️", label: "AI Slide" },
                                    { key: "my_assets" as SessionTab, icon: "⭐", label: "My Assets" },
                                ]
                            ).map(({ key, icon, label }) => (
                                <button
                                    key={key}
                                    className={`tab-strip-btn ${selectedTab === key ? (key === "my_assets" ? "ts-active-gold" : "ts-active") : ""}`}
                                    onClick={() => handleTabSelect(key)}
                                >
                                    <span className="tab-icon">{icon}</span>
                                    {label}
                                </button>
                            ))}
                        </div>
                    </header>

                    {/* Messages */}
                    <div className="messages-area">
                        {selectedTab === "ai_slide" ? (
                            <div className="template-workspace">
                                <div style={{ textAlign: "center", padding: "72px 24px", maxWidth: 480, margin: "0 auto" }}>
                                    <div style={{ fontSize: 52, marginBottom: 16 }}>📽️</div>
                                    <h2 className="template-workspace-title" style={{ marginBottom: 10 }}>AI Slides — Coming Soon</h2>
                                    <p className="template-workspace-sub">
                                        AI-generated presentation slide decks are on the way. This feature isn’t available just yet — check back soon!
                                    </p>
                                </div>
                            </div>
                        ) : (selectedTab === "flyers" || selectedTab === "videos" || selectedTab === "ai_itinerary") && messages.length === 0 && !isTyping ? (
                            <div className="template-workspace">
                                <div className="template-workspace-header">
                                    <div>
                                        <h2 className="template-workspace-title">
                                            {templatePanelHeader?.title
                                                ?? chatNavTabs.find(t => t.id === selectedTab)?.header_title
                                                ?? "Templates"}
                                        </h2>
                                        <p className="template-workspace-sub">
                                            {templatePanelHeader?.subtitle
                                                ?? chatNavTabs.find(t => t.id === selectedTab)?.header_subtitle
                                                ?? "Pick a template or describe your trip below"}
                                        </p>
                                    </div>
                                    <div className="template-pills">
                                        {([{ id: "all", label: "All", display_group: "", template_count: 0, has_templates: false } as TemplateCategory, ...templateCategories]).map(cat => {
                                            const count =
                                                cat.id === "all"
                                                    ? templates.length
                                                    : (cat.template_count || templates.filter(t => t.category === cat.id).length);
                                            const pillLabel =
                                                cat.id === "all"
                                                    ? `All (${count})`
                                                    : `${cat.label ?? cat.id} (${count})`;
                                            return (
                                                <button
                                                    key={cat.id}
                                                    type="button"
                                                    className={`template-pill${templateCategoryFilter === cat.id ? " template-pill--active" : ""}`}
                                                    onClick={() => setTemplateCategoryFilter(cat.id)}
                                                >
                                                    {pillLabel}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>

                                {/* Flyer dev form */}
                                {templatesIsDevelopment && selectedTab === "flyers" && (
                                    <div className="dev-panel">
                                    <span className="dev-panel-badge">Development</span>
                                    <FlyerTemplateDevForm
                                        onCreated={() => {
                                            showToast(editingTemplate ? "Flyer template updated" : "Flyer template saved");
                                            setEditingTemplate(null);
                                            void fetchTemplates("flyers");
                                        }}
                                        onError={msg => showToast(msg)}
                                        initialTemplate={editingTemplate ? {
                                            id: editingTemplate.id,
                                            name: editingTemplate.name,
                                            category: editingTemplate.category,
                                            base_prompt: editingTemplate.base_prompt ?? editingTemplate.prompt_template ?? "",
                                            fields: editingTemplate.fields,
                                            cta_text: editingTemplate.cta_text,
                                            tagline: editingTemplate.tagline,
                                            style: editingTemplate.style,
                                            mood: editingTemplate.mood,
                                            color_tone: editingTemplate.color_tone ?? "",
                                            flyer_type: editingTemplate.flyer_type ?? "",
                                            overlay_style: editingTemplate.overlay_style ?? "",
                                            time_of_day: editingTemplate.time_of_day ?? "",
                                            aspect_ratio: editingTemplate.aspect_ratio,
                                            preview_image_url: editingTemplate.preview_image_url,
                                        } : null}
                                        onCancelEdit={() => setEditingTemplate(null)}
                                    />
                                    </div>
                                )}

                                {/* Video dev form */}
                                {templatesIsDevelopment && selectedTab === "videos" && (
                                    <div className="dev-panel">
                                    <span className="dev-panel-badge">Development</span>
                                    <VideoTemplateDevForm
                                        onCreated={() => {
                                            showToast(editingVideoTemplate ? "Video template updated" : "Video template saved");
                                            setEditingVideoTemplate(null);
                                            void fetchTemplates("videos");
                                        }}
                                        onError={msg => showToast(msg)}
                                        initialTemplate={editingVideoTemplate ? ({
                                            id: editingVideoTemplate.id,
                                            name: editingVideoTemplate.name,
                                            category: editingVideoTemplate.category,
                                            base_prompt: editingVideoTemplate.base_prompt ?? editingVideoTemplate.prompt_template ?? "",
                                            fields: editingVideoTemplate.fields,
                                            cta_text: editingVideoTemplate.cta_text,
                                            tagline: editingVideoTemplate.tagline,
                                            mood: editingVideoTemplate.mood,
                                            aspect_ratio: editingVideoTemplate.aspect_ratio,
                                            preview_image_url: editingVideoTemplate.preview_image_url,
                                            camera_movement: editingVideoTemplate.camera_movement ?? "",
                                            motion_style: editingVideoTemplate.motion_style ?? "",
                                            transition_style: editingVideoTemplate.transition_style ?? "",
                                            text_animation_style: editingVideoTemplate.text_animation_style ?? "",
                                            audio_mood: editingVideoTemplate.audio_mood ?? "",
                                            scene_structure: editingVideoTemplate.scene_structure ?? "",
                                            video_duration: ((editingVideoTemplate as import("../modules/ai_media/common/templateTypes").VideoTemplateItem).video_duration === 5 ? 5 : 10) as 5 | 10,
                                        } satisfies VideoTemplateInit) : null}
                                        onCancelEdit={() => setEditingVideoTemplate(null)}
                                    />
                                    </div>
                                )}

                                {/* Itinerary — upload your own */}
                                {selectedTab === "ai_itinerary" && (
                                    <div style={{ marginBottom: 16 }}>
                                        <input
                                            ref={itineraryFileRef}
                                            type="file"
                                            accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/jpeg,image/png,image/webp"
                                            multiple
                                            style={{ display: "none" }}
                                            onChange={e => void handleItineraryFileUpload(e.target.files)}
                                            onClick={e => { (e.target as HTMLInputElement).value = ""; }}
                                        />
                                        {itineraryUploadedFiles.length > 0 ? (
                                            <div style={{ display: "flex", flexDirection: "column", gap: 6, padding: "10px 14px", background: "rgba(52,211,153,0.08)", border: "1.5px solid rgba(52,211,153,0.25)", borderRadius: 12, fontSize: 13 }}>
                                                {itineraryUploadedFiles.map((f, i) => (
                                                    <div key={f.s3_key} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                                        <span style={{ fontSize: 15 }}>📄</span>
                                                        <span style={{ color: "#6ee7b7", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.name}</span>
                                                        <button
                                                            type="button"
                                                            onClick={() => setItineraryUploadedFiles(prev => prev.filter((_, idx) => idx !== i))}
                                                            style={{ background: "none", border: "none", color: "#6b7280", cursor: "pointer", padding: "2px 4px", fontSize: 13, lineHeight: 1 }}
                                                            title="Remove"
                                                        >✕</button>
                                                    </div>
                                                ))}
                                                <button
                                                    type="button"
                                                    onClick={() => itineraryFileRef.current?.click()}
                                                    disabled={itineraryUploading}
                                                    style={{ marginTop: 4, display: "flex", alignItems: "center", gap: 6, background: "none", border: "1px dashed rgba(52,211,153,0.4)", borderRadius: 8, padding: "5px 10px", color: "#6ee7b7", fontSize: 12, cursor: "pointer" }}
                                                >
                                                    {itineraryUploading ? "Uploading…" : "＋ Add more files"}
                                                </button>
                                            </div>
                                        ) : (
                                            <button
                                                type="button"
                                                className="template-upload-btn"
                                                onClick={() => itineraryFileRef.current?.click()}
                                                disabled={itineraryUploading}
                                                style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "12px 16px", background: "rgba(52,211,153,0.06)", border: "1.5px dashed rgba(52,211,153,0.3)", borderRadius: 12, color: "#6ee7b7", fontSize: 13, fontWeight: 500, cursor: "pointer", transition: "all 0.2s" }}
                                            >
                                                {itineraryUploading ? (
                                                    <><span style={{ display: "inline-block", width: 14, height: 14, border: "2px solid rgba(110,231,183,0.3)", borderTopColor: "#6ee7b7", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} /> Uploading…</>
                                                ) : (
                                                    <><span style={{ fontSize: 16 }}>＋</span> Upload your own itinerary</>
                                                )}
                                            </button>
                                        )}
                                    </div>
                                )}

                                {/* Itinerary dev form */}
                                {templatesIsDevelopment && selectedTab === "ai_itinerary" && (
                                    <div className="dev-panel">
                                    <span className="dev-panel-badge">Development</span>
                                    <ItineraryTemplateDevForm
                                        onCreated={() => {
                                            showToast(editingItineraryTemplate ? "Itinerary template updated" : "Itinerary template saved");
                                            setEditingItineraryTemplate(null);
                                            void fetchTemplates("ai_itinerary");
                                        }}
                                        onError={msg => showToast(msg)}
                                        initialTemplate={editingItineraryTemplate ? ({
                                            id: editingItineraryTemplate.id,
                                            name: editingItineraryTemplate.name,
                                            category: editingItineraryTemplate.category,
                                            base_prompt: editingItineraryTemplate.base_prompt ?? "",
                                            prompt_template: editingItineraryTemplate.prompt_template ?? "",
                                            fields: editingItineraryTemplate.fields,
                                            cta_text: editingItineraryTemplate.cta_text,
                                            tagline: editingItineraryTemplate.tagline,
                                            style: editingItineraryTemplate.style,
                                            mood: editingItineraryTemplate.mood,
                                            color_tone: editingItineraryTemplate.color_tone ?? "",
                                            overlay_style: editingItineraryTemplate.overlay_style ?? "",
                                            time_of_day: editingItineraryTemplate.time_of_day ?? "",
                                            layout_type: editingItineraryTemplate.layout_type ?? "",
                                            page_structure: editingItineraryTemplate.page_structure ?? [],
                                            preview_image_url: editingItineraryTemplate.preview_image_url,
                                        } satisfies ItineraryTemplateInit) : null}
                                        onCancelEdit={() => setEditingItineraryTemplate(null)}
                                    />
                                    </div>
                                )}

                                {/* Phase 4 media-platform trigger — separate from the template/FAL
                                    video flow above. Generates via the standalone reel-generation
                                    platform; result lands on the deal's video_url asynchronously. */}
                                {templatesLoading ? (
                                    <div className="template-empty">Loading templates…</div>
                                ) : templates.length === 0 ? (
                                    <div className="template-empty template-empty--narrow">
                                        {(selectedTab === "flyers" || selectedTab === "videos") && templatesIsDevelopment ? (
                                            <>
                                                <p style={{ margin: "0 0 8px", fontWeight: 600, color: "#aaa" }}>
                                                    No published {selectedTab === "videos" ? "video" : "flyer"} templates yet
                                                </p>
                                                <p style={{ margin: 0, fontSize: 12, lineHeight: 1.5 }}>
                                                    Use the form above to add a template (prompt + preview image).
                                                    Category filters only appear once a template exists for that subcategory.
                                                </p>
                                            </>
                                        ) : selectedTab === "flyers" ? (
                                            "No flyer templates are available yet. Check back soon."
                                        ) : selectedTab === "videos" ? (
                                            "No video templates are available yet. Check back soon."
                                        ) : (
                                            "No templates found."
                                        )}
                                    </div>
                                ) : (() => {
                                    const visible = templates.filter(
                                        t => templateCategoryFilter === "all" || t.category === templateCategoryFilter,
                                    );
                                    if (visible.length === 0) {
                                        const catLabel =
                                            templateCategories.find(c => c.id === templateCategoryFilter)?.label
                                            ?? templateCategoryFilter;
                                        return (
                                            <div className="template-empty">
                                                {templatesIsDevelopment ? (
                                                    <>
                                                        <p style={{ margin: "0 0 8px", fontWeight: 600, color: "#aaa" }}>
                                                            No templates in “{catLabel}”
                                                        </p>
                                                        <p style={{ margin: 0, fontSize: 12 }}>
                                                            Create one with the development form above, or pick another category.
                                                        </p>
                                                    </>
                                                ) : (
                                                    `No templates in “${catLabel}”.`
                                                )}
                                            </div>
                                        );
                                    }
                                    return (
                                    <div className="template-grid">
                                        {visible.map(tmpl => (
                                                <div
                                                    key={tmpl.id}
                                                    role="button"
                                                    tabIndex={0}
                                                    className="template-card"
                                                    style={{
                                                        aspectRatio: tmpl.aspect_ratio === "16:9" ? "16/9" : tmpl.aspect_ratio === "1:1" ? "1/1" : "9/14",
                                                    }}
                                                    onClick={() => setTemplatePreview(tmpl)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === "Enter" || e.key === " ") {
                                                            e.preventDefault();
                                                            setTemplatePreview(tmpl);
                                                        }
                                                    }}
                                                >
                                                    {tmpl.preview_image_url ? (
                                                        <img
                                                            src={tmpl.preview_image_url}
                                                            alt={tmpl.name}
                                                            className="template-card-media"
                                                            loading="lazy"
                                                        />
                                                    ) : (
                                                        <div className="template-card-placeholder">{tmpl.emoji}</div>
                                                    )}
                                                    <div className="template-card-gradient" />
                                                    <div className="template-card-meta">
                                                        <div className="template-card-name">{tmpl.name}</div>
                                                        <div className="template-card-cat">{tmpl.category_label}</div>
                                                    </div>
                                                    <div className="template-card-emoji">{tmpl.emoji}</div>
                                                    <div className="template-card-badge">
                                                        {tmpl.asset_type === "slides" ? "📊 Slides" : tmpl.asset_type === "itinerary" ? "🗺️ Itinerary" : tmpl.asset_type === "video" ? "🎬 Video" : "🖼️ Flyer"}
                                                    </div>
                                                    {templatesIsDevelopment && (
                                                        <button
                                                            type="button"
                                                            className="template-card-edit"
                                                            onClick={e => {
                                                                e.stopPropagation();
                                                                if (tmpl.asset_type === "video") {
                                                                    setEditingVideoTemplate(tmpl);
                                                                } else if (tmpl.asset_type === "itinerary") {
                                                                    setEditingItineraryTemplate(tmpl);
                                                                } else {
                                                                    setEditingTemplate(tmpl);
                                                                }
                                                                window.scrollTo({ top: 0, behavior: "smooth" });
                                                            }}
                                                        >
                                                            EDIT
                                                        </button>
                                                    )}
                                                </div>
                                            ))}
                                    </div>
                                    );
                                })()}

                                <div className="template-hint-box">
                                    <p>
                                        Or type a prompt in the command bar below — the AI will ask for any missing details.
                                    </p>
                                </div>
                            </div>
                        ) : selectedTab === "my_assets" ? (
                            <div style={{ padding: "24px 20px" }}>
                                {/* Header */}
                                <div style={{ marginBottom: 0 }}>
                                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
                                        <div>
                                            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: "#fde68a" }}>
                                                {myAssetsPanelHeader?.title ?? "My Assets"}
                                            </h2>
                                            <p style={{ margin: "4px 0 0", fontSize: 12, color: "#888" }}>
                                                {myAssetsPanelHeader?.subtitle ?? "All your AI-generated flyers, videos, itineraries & slides"}
                                            </p>
                                        </div>
                                        <button
                                            onClick={() => void fetchMyAssets(assetsActiveTab)}
                                            style={{ padding: "6px 14px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.06)", color: "#aaa", fontSize: 12, cursor: "pointer" }}
                                        >↺ Refresh</button>
                                    </div>

                                    {/* Filter tabs from GET /rag/my-assets → tabs */}
                                    <div style={{ display: "flex", borderBottom: "1px solid rgba(255,255,255,0.08)", marginBottom: 20, gap: 0 }}>
                                        {(myAssetsFilterTabs.length > 0
                                            ? myAssetsFilterTabs
                                            : [
                                                { id: "all", label: "All", icon: "⭐" },
                                                { id: "flyers", label: "Flyers", icon: "🖼️" },
                                                { id: "videos", label: "Videos", icon: "🎬" },
                                                { id: "itinerary", label: "Itineraries", icon: "🗺️" },
                                                { id: "slides", label: "Slides", icon: "📊" },
                                            ]
                                        ).map(f => {
                                            const active = assetsActiveTab === f.id;
                                            return (
                                                <button
                                                    key={f.id}
                                                    onClick={() => {
                                                        setAssetsActiveTab(f.id);
                                                        setAssetsSkip(0);
                                                        setAssetsHasMore(false);
                                                        void fetchMyAssets(f.id, 0, false);
                                                    }}
                                                    style={{
                                                        padding: "9px 14px",
                                                        fontSize: 12, fontWeight: active ? 700 : 500,
                                                        border: "none", borderBottom: active ? "2px solid #fde68a" : "2px solid transparent",
                                                        background: "none",
                                                        color: active ? "#fde68a" : "#6b7280",
                                                        cursor: "pointer", transition: "all 0.15s",
                                                        whiteSpace: "nowrap",
                                                        marginBottom: -1,
                                                    }}
                                                >{f.icon} {f.label}</button>
                                            );
                                        })}
                                    </div>
                                </div>

                                {assetsLoading ? (
                                    <div style={{ textAlign: "center", color: "#888", padding: 40 }}>Loading assets…</div>
                                ) : myAssets.length === 0 ? (
                                    <div style={{ textAlign: "center", color: "#888", padding: 40 }}>
                                        <div style={{ fontSize: 36, marginBottom: 12 }}>🖼️</div>
                                        <p style={{ margin: 0 }}>
                                            {assetsActiveTab === "all"
                                                ? "No assets yet. Generate a flyer, video, itinerary or slide to see it here."
                                                : `No assets in this category yet. Try another filter or generate from the ${myAssetsFilterTabs.find(t => t.id === assetsActiveTab)?.label ?? assetsActiveTab} tab.`}
                                        </p>
                                    </div>
                                ) : (
                                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 16 }}>
                                        {myAssets.map((asset, i) => (
                                            <div key={asset.s3_key || asset.url || i} style={{ borderRadius: 12, overflow: "hidden", border: "1px solid rgba(255,255,255,0.1)", background: "rgba(255,255,255,0.04)" }}>
                                                {asset.type === "video" ? (
                                                    <video src={asset.url} controls playsInline preload="metadata" style={{ width: "100%", display: "block", background: "#000", maxHeight: 180, objectFit: "cover" }} />
                                                ) : asset.type === "itinerary" ? (
                                                    <div
                                                        onClick={() => asset.html_url && window.open(asset.html_url, "_blank")}
                                                        style={{ cursor: asset.html_url ? "pointer" : "default", position: "relative" }}
                                                        title="Click to open itinerary"
                                                    >
                                                        {asset.thumbnail_url ? (
                                                            <img src={asset.thumbnail_url} alt={asset.caption || "Itinerary cover"} style={{ width: "100%", display: "block", height: 180, objectFit: "cover" }} />
                                                        ) : (
                                                            <div style={{ width: "100%", height: 180, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: "rgba(52,211,153,0.08)", gap: 8 }}>
                                                                <span style={{ fontSize: 32 }}>🗺️</span>
                                                                <span style={{ fontSize: 11, color: "#86efac" }}>Click to open itinerary</span>
                                                            </div>
                                                        )}
                                                        {asset.html_url && (
                                                            <div style={{ position: "absolute", top: 8, right: 8, background: "rgba(0,0,0,0.6)", borderRadius: 6, padding: "2px 6px", fontSize: 10, color: "#86efac" }}>⛶ Open</div>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <img src={asset.url} alt={asset.caption || "Generated asset"} style={{ width: "100%", display: "block", height: 180, objectFit: "cover" }} />
                                                )}
                                                <div style={{ padding: "8px 12px", background: "rgba(0,0,0,0.3)" }}>
                                                    <p style={{ margin: "0 0 6px", fontSize: 11, color: "#ccc", lineHeight: 1.4, overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
                                                        {asset.caption || `Generated ${asset.asset_type}`}
                                                    </p>
                                                    <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                                                        <span style={{
                                                            fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em",
                                                            padding: "2px 6px", borderRadius: 4, flex: "0 0 auto",
                                                            background: asset.asset_type === "video" ? "rgba(99,179,237,0.2)" :
                                                                        asset.asset_type === "itinerary" ? "rgba(134,239,172,0.2)" :
                                                                        asset.asset_type === "slides" ? "rgba(251,191,36,0.2)" :
                                                                        "rgba(167,139,250,0.2)",
                                                            color: asset.asset_type === "video" ? "#90cdf4" :
                                                                   asset.asset_type === "itinerary" ? "#86efac" :
                                                                   asset.asset_type === "slides" ? "#fde68a" :
                                                                   "#c4b5fd",
                                                        }}>{asset.asset_type}</span>
                                                        <span style={{ flex: 1 }} />
                                                        {asset.type === "image" && (
                                                            <button
                                                                onClick={() => {
                                                                    setEditingAssetUrl(editingAssetUrl === asset.url ? null : asset.url);
                                                                    setEditingAssetText("");
                                                                }}
                                                                style={{ padding: "4px 8px", borderRadius: 6, border: "1px solid rgba(99,179,237,0.35)", background: "rgba(99,179,237,0.08)", color: "#90cdf4", fontSize: 10, cursor: "pointer" }}
                                                            >
                                                                ✏️ Edit
                                                            </button>
                                                        )}
                                                        <button
                                                            onClick={() => handleDownloadMedia(asset.url, asset.type, asset.caption, asset.type === "itinerary" ? asset.session_id : undefined, asset.s3_key)}
                                                            style={{ padding: "4px 8px", borderRadius: 6, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.08)", color: "#ddd", fontSize: 10, cursor: "pointer" }}
                                                            title={asset.type === "itinerary" ? "Download PDF" : "Download"}
                                                        >
                                                            {asset.type === "itinerary" ? "↓ PDF" : "↓"}
                                                        </button>
                                                        {asset.s3_key && (
                                                            <button
                                                                onClick={() => void handleDeleteAsset(asset.s3_key!)}
                                                                style={{ padding: "4px 8px", borderRadius: 6, border: "1px solid rgba(239,68,68,0.3)", background: "rgba(239,68,68,0.08)", color: "#fca5a5", fontSize: 10, cursor: "pointer" }}
                                                                title="Delete asset permanently"
                                                            >
                                                                🗑
                                                            </button>
                                                        )}
                                                    </div>

                                                    {editingAssetUrl === asset.url && (
                                                        <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 6 }}>
                                                            <textarea
                                                                rows={2}
                                                                value={editingAssetText}
                                                                onChange={e => setEditingAssetText(e.target.value)}
                                                                placeholder="Describe what to change… e.g. change background to sunset, add price ₹35,000"
                                                                style={{
                                                                    width: "100%", boxSizing: "border-box", resize: "none",
                                                                    background: "rgba(255,255,255,0.06)", border: "1px solid rgba(99,179,237,0.3)",
                                                                    borderRadius: 6, color: "#e2e8f0", fontSize: 11, padding: "6px 8px",
                                                                    outline: "none", fontFamily: "inherit",
                                                                }}
                                                            />
                                                            <button
                                                                disabled={editingAssetLoading || !editingAssetText.trim()}
                                                                onClick={() => handleEditAssetSubmit(asset.url)}
                                                                style={{
                                                                    padding: "5px 10px", borderRadius: 6, fontSize: 11, fontWeight: 600,
                                                                    border: "none", cursor: editingAssetLoading ? "not-allowed" : "pointer",
                                                                    background: editingAssetLoading ? "rgba(99,179,237,0.2)" : "rgba(99,179,237,0.85)",
                                                                    color: "#fff",
                                                                }}
                                                            >
                                                                {editingAssetLoading ? "Applying edit…" : "Apply Edit"}
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {assetsHasMore && !assetsLoading && (
                                    <div style={{ textAlign: "center", padding: "16px 0" }}>
                                        <button
                                            onClick={() => void fetchMyAssets(assetsActiveTab, assetsSkip, true)}
                                            disabled={assetsLoadingMore}
                                            style={{
                                                padding: "8px 24px", borderRadius: 8, fontSize: 13, fontWeight: 600,
                                                background: "rgba(253,230,138,0.12)", color: "#fde68a",
                                                border: "1px solid rgba(253,230,138,0.3)", cursor: "pointer",
                                            }}
                                        >
                                            {assetsLoadingMore ? "Loading…" : "Load more assets"}
                                        </button>
                                    </div>
                                )}
                            </div>

                        ) : messages.length === 0 && !isTyping ? (
                            <div className="welcome-state">
                                <span className="welcome-hero-badge">AI travel workspace</span>
                                <div className="welcome-icon">✨</div>
                                <h2 className="welcome-title">
                                    {welcomeTitle}
                                </h2>
                                <p className="welcome-sub">
                                    {welcomeSub}
                                </p>
                                {selectedTab === "market_research" ? (
                                    <>
                                        <span className="mr-try-asking-label">Try asking</span>
                                        <div className="mr-chip-list">
                                            {personalizedStarterChips.slice(0, 8).map((s) => (
                                                <button key={s} className="mr-chip-row" onClick={() => void sendMessage(s)}>
                                                    <span className="mr-chip-row-icon">✨</span>
                                                    <span className="mr-chip-row-text">{s}</span>
                                                    <span className="mr-chip-row-chevron">›</span>
                                                </button>
                                            ))}
                                        </div>
                                    </>
                                ) : (
                                    <div className="suggestion-chips">
                                        {personalizedStarterChips.slice(0, 3).map((s) => (
                                            <button key={s} className="chip" onClick={() => void sendMessage(s)}>
                                                {s}
                                            </button>
                                        ))}
                                    </div>
                                )}
                                {selectedTab !== "market_research" && welcomeBanners.length > 0 && (
                                    <div style={{ display: "flex", flexDirection: "column", gap: "0.625rem", marginTop: "1rem", width: "100%", maxWidth: 480 }}>
                                        {welcomeBanners.filter((b) => b.video_url || b.image_url).map((banner) => (
                                            <button
                                                key={banner.id}
                                                onClick={() => {
                                                    if (banner.banner_type === "direct_link" && banner.direct_link) {
                                                        if (/^https?:\/\//i.test(banner.direct_link)) {
                                                            window.open(banner.direct_link, "_blank", "noopener,noreferrer");
                                                        } else {
                                                            navigate(banner.direct_link);
                                                        }
                                                        return;
                                                    }
                                                    // Partners don't have individual deals — route to their profile page.
                                                    if (banner.seller_role === "partner") {
                                                        if (banner.seller_id) navigate(`/partners/${banner.seller_id}`);
                                                        return;
                                                    }
                                                    const params = new URLSearchParams({ category_slug: banner.category_slug });
                                                    if (banner.banner_type !== "category" && banner.deal_id) {
                                                        params.set("deal_id", banner.deal_id);
                                                    } else {
                                                        // "category" (or missing, for pre-migration rows) — jump straight
                                                        // into the Shorts viewer for this category's feed.
                                                        params.set("view", "shorts");
                                                    }
                                                    navigate(`/?${params.toString()}`);
                                                }}
                                                style={{ display: "block", width: "100%", borderRadius: 14, overflow: "hidden", border: "1px solid rgba(255,255,255,0.08)", cursor: "pointer", padding: 0, background: "none" }}
                                            >
                                                {banner.video_url ? (
                                                    <video
                                                        src={banner.video_url}
                                                        style={{ width: "100%", display: "block", objectFit: "cover" }}
                                                        muted
                                                        loop
                                                        autoPlay
                                                        playsInline
                                                    />
                                                ) : (
                                                    <img
                                                        src={banner.image_url!}
                                                        alt={banner.category_slug}
                                                        style={{ width: "100%", display: "block", objectFit: "cover" }}
                                                    />
                                                )}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <>
                                {messages.map((msg) => (
                                    <div key={msg.id} className={`msg-row msg-row--${msg.role === "assistant" ? "ai" : "user"}`}>
                                        {msg.type === "enquiry_success" ? (
                                            <div style={{ width: "100%", display: "flex", justifyContent: "center", padding: "0.5rem 0 0.75rem" }}>
                                                <div style={{
                                                    background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
                                                    border: "1px solid rgba(99,102,241,0.35)",
                                                    borderRadius: 16,
                                                    padding: "1.25rem 1.5rem",
                                                    maxWidth: 440,
                                                    width: "100%",
                                                    boxShadow: "0 4px 24px rgba(0,0,0,0.35)",
                                                }}>
                                                    <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "0.875rem" }}>
                                                        <div style={{
                                                            width: 36, height: 36, borderRadius: "50%",
                                                            background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
                                                            display: "flex", alignItems: "center", justifyContent: "center",
                                                            fontSize: 18, flexShrink: 0,
                                                        }}>✓</div>
                                                        <div>
                                                            <div style={{ fontWeight: 700, fontSize: "0.95rem", color: "#f1f5f9", letterSpacing: "0.01em" }}>
                                                                Enquiry Submitted
                                                            </div>
                                                            <div style={{ fontSize: "0.75rem", color: "#94a3b8", marginTop: 1 }}>
                                                                Ref <span style={{ fontFamily: "monospace", color: "#a5b4fc", letterSpacing: "0.05em" }}>#{msg.lead_id?.slice(-8).toUpperCase()}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div style={{ borderTop: "1px solid rgba(255,255,255,0.07)", paddingTop: "0.875rem", display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                                                        {msg.seller_name && (
                                                            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontSize: "0.82rem", color: "#cbd5e1" }}>
                                                                <span style={{ color: "#6366f1" }}>→</span>
                                                                <span>Sent to <strong style={{ color: "#f1f5f9" }}>{msg.seller_name}</strong></span>
                                                            </div>
                                                        )}
                                                        {!msg.seller_name && (
                                                            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontSize: "0.82rem", color: "#cbd5e1" }}>
                                                                <span style={{ color: "#6366f1" }}>→</span>
                                                                <span>Matching sellers will be notified</span>
                                                            </div>
                                                        )}
                                                        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontSize: "0.82rem", color: "#cbd5e1" }}>
                                                            <span style={{ color: "#6366f1" }}>→</span>
                                                            <span>Track responses under <strong style={{ color: "#f1f5f9" }}>My Enquiries</strong></span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (<>
                                        <div className={`msg-avatar msg-avatar--${msg.role === "assistant" ? "ai" : "user"}`}>
                                            {msg.role === "assistant" ? "✈️" : (user?.name ?? "U")[0].toUpperCase()}
                                        </div>
                                        <div className="msg-body">
                                            {msg.role === "assistant" && msg.template_name && (
                                                <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "rgba(99,102,241,0.12)", border: "1px solid rgba(99,102,241,0.25)", borderRadius: 20, padding: "3px 10px", fontSize: "0.72rem", color: "#a5b4fc", marginBottom: 4, fontWeight: 600, letterSpacing: "0.03em" }}>
                                                    🖼️ {msg.template_name}
                                                </div>
                                            )}
                                            {msg.role === "assistant" && msg.intent && (
                                                <div className="msg-intent">🎯 {msg.intent}</div>
                                            )}
                                            <div className={`msg-bubble msg-bubble--${msg.role === "assistant" ? "ai" : "user"}`}>
                                                {!msg.is_generating ? (
                                                  msg.content
                                                    ? (
                                                        <div className="msg-markdown">
                                                            <ReactMarkdown
                                                                remarkPlugins={[remarkGfm]}
                                                                components={chatMarkdownComponents}
                                                            >
                                                                {msg.content}
                                                            </ReactMarkdown>
                                                        </div>
                                                      )
                                                    : (msg.streaming ? <StreamingPlaceholder tab={selectedTab} stageLabel={msg.researchStageLabel} /> : null)
                                                ) : null}
                                                {msg.attachmentUrl && msg.role === "user" && (
                                                    <img
                                                        src={msg.attachmentUrl}
                                                        alt="Attached reference"
                                                        style={{ display: "block", maxWidth: 220, maxHeight: 160, borderRadius: 8, marginTop: 6, objectFit: "cover" }}
                                                    />
                                                )}
                                                {msg.templateThumbnailUrl && msg.role === "user" && !msg.attachmentUrl && (
                                                    <img
                                                        src={msg.templateThumbnailUrl}
                                                        alt="Template"
                                                        style={{ display: "block", maxWidth: 160, maxHeight: 220, borderRadius: 8, marginTop: 6, objectFit: "cover" }}
                                                    />
                                                )}
                                                {msg.is_generating && (
                                                    <TemplateGenerationProgress
                                                        steps={msg.generation_steps ?? []}
                                                        progress={msg.generation_progress ?? 0}
                                                        phase={msg.generation_phase || "prep"}
                                                        mediaType={msg.generation_media_type}
                                                        assetType={
                                                            msg.generation_asset_type ??
                                                            (msg.generation_stage?.includes("itinerary") ? "itinerary" : undefined)
                                                        }
                                                        templateName={msg.generation_template_name}
                                                        showSkeleton={msg.show_generation_skeleton}
                                                        fallbackTitle={msg.generation_title || msg.generation_status || "Generating…"}
                                                    />
                                                )}
                                            </div>

                                            {/* Media result card — flyer / video / itinerary / slides (+ prompt preview) */}
                                            {msg.role === "assistant" && msg.deals && msg.deals.length === 1 && (
                                                isGeneratedMediaDeal(msg.deals[0]) || msg.is_media_preview
                                            ) && (msg.deals[0].image || msg.prompt_used) && (
                                                <MediaPreviewCard
                                                    deal={msg.deals[0]}
                                                    caption={msg.content}
                                                    promptUsed={msg.prompt_used}
                                                    isPreview={msg.is_media_preview}
                                                    onFullscreen={setMediaFullscreen}
                                                    onDownload={handleDownloadMedia}
                                                    onEditText={setEditingVideoDeal}
                                                />
                                            )}

                                                                                        {/* Deal cards — contact hidden until interest clicked */}
                                            {msg.role === "assistant" && msg.deals && msg.deals.length > 0 && !(msg.deals.length === 1 && isGeneratedMediaDeal(msg.deals[0])) && !msg.is_media_preview && (
                                                <DealsCarousel
                                                    deals={msg.deals}
                                                    onInterest={(selected) => openEnquiry(selected)}
                                                />
                                            )}

                                            {/* Cross-sell carousels — parallel categories */}
                                            {msg.role === "assistant" && msg.cross_sell_sections && msg.cross_sell_sections.length > 0 && (
                                                <div className="cross-sell-sections">
                                                    <div className="cross-sell-intro">
                                                        <p className="cross-sell-heading">
                                                            {msg.cross_sell_sections.length === 1
                                                                ? `We also found ${msg.cross_sell_sections[0].label}`
                                                                : `We also found ${msg.cross_sell_sections.slice(0, -1).map(s => s.label).join(", ")} & ${msg.cross_sell_sections[msg.cross_sell_sections.length - 1].label}`}
                                                        </p>
                                                        <p className="cross-sell-subheading">Add-ons your clients might need for a complete trip</p>
                                                    </div>
                                                    {msg.cross_sell_sections.map((section) =>
                                                        section.deals && section.deals.length > 0 ? (
                                                            <div key={section.category} className="cross-sell-row">
                                                                <p className="cross-sell-label">{section.label}</p>
                                                                <div className="deals-scroll">
                                                                    {section.deals.map((deal, i) => (
                                                                        <DealCard
                                                                            key={(deal as Deal).deal_id ?? i}
                                                                            deal={mapApiDeal(deal as Deal)}
                                                                            index={i}
                                                                            onInterest={(d) => {
                                                                                void analyticsService.trackEvent({
                                                                                    event_type: "cross_sell_card_clicked",
                                                                                    source: "rag_chat",
                                                                                    item_id: d.deal_id ?? d.id,
                                                                                    item_type: section.category,
                                                                                    item_title: d.title,
                                                                                    seller_id: d.dealer_id,
                                                                                    session_id: activeSessionId ?? undefined,
                                                                                });
                                                                                openEnquiry({
                                                                                    ...d,
                                                                                    category: section.category,
                                                                                    _from_cross_sell: true,
                                                                                });
                                                                            }}
                                                                        />
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        ) : null,
                                                    )}
                                                </div>
                                            )}

                                            {/* Stage 3 — Recommended DMC cards. Each has its own Send
                                                Enquiry button that opens the EnquiryModal with dealer_id
                                                pre-filled (no deal_id). Rendered below the deals rail so
                                                the buyer can see available inventory first, then the
                                                DMCs they can approach directly. */}
                                            {msg.role === "assistant" && msg.dmcs && msg.dmcs.length > 0 && (
                                                <DmcCards
                                                    dmcs={msg.dmcs}
                                                    itineraryText={msg.content}
                                                    onEnquire={(dmc) => openDmcEnquiry(dmc, msg.content)}
                                                />
                                            )}

                                            {/* CTA Panel — WhatsApp + Email (INTERESTED / CONNECT stage) */}
                                            {/* Only auto-shows when bot confirms interest and returns contact details */}
                                            {msg.role === "assistant" && msg.show_cta && msg.cta_deal && (
                                                <CTAPanel deal={msg.cta_deal} sessionId={activeSessionId ?? undefined} />
                                            )}

                                            {/* Legacy connect panel (fallback) */}
                                            {msg.role === "assistant" && msg.showConnectPanel && !msg.show_cta && msg.deals && msg.deals.length > 0 && (
                                                <ConnectPanel
                                                    deals={msg.deals}
                                                    sessionId={activeSessionId ?? ""}
                                                    connectStatus={msg.connectStatus ?? {}}
                                                    onConnect={(idx) => void handleConnect(msg.id, idx, msg.deals![idx])}
                                                />
                                            )}

                                            {/* Suggestion chips (new agentic) */}
                                            {msg.role === "assistant" && msg.suggestions && msg.suggestions.length > 0 && (
                                                <div className="suggested-actions">
                                                    {msg.suggestions.map((s, i) => (
                                                        <button
                                                            key={i}
                                                            className="action-chip"
                                                            onClick={() => {
                                                                const lc = lastTemplateContextRef.current;
                                                                if (s === "Generate again" && lc && !generatingFromTemplate) {
                                                                    // Always re-run template generation — never route to /generate-media/stream
                                                                    setInput("");
                                                                    setMessages(prev => [...prev, {
                                                                        id: `user-${Date.now()}`, role: "user" as const,
                                                                        content: s, timestamp: new Date(),
                                                                    }]);
                                                                    setGeneratingFromTemplate(true);
                                                                    setIsTyping(true);
                                                                    (async () => {
                                                                        try {
                                                                            await streamTemplateRegeneration({
                                                                                templateId: lc.templateId,
                                                                                prompt: lc.prompt,
                                                                                selectedTab,
                                                                                categoryLabel: lc.categoryLabel,
                                                                            });
                                                                        } finally {
                                                                            setGeneratingFromTemplate(false);
                                                                            setIsTyping(false);
                                                                        }
                                                                    })();
                                                                } else if ((s === "Edit prompt" || s === "Generate now") && lc) {
                                                                    // Restore template + prompt into main input
                                                                    setActiveTemplate(lc.template as FlyerTemplate);
                                                                    setInput(lc.prompt);
                                                                    setTimeout(() => textareaRef.current?.focus(), 50);
                                                                } else if (s === "New template") {
                                                                    setActiveTemplate(null);
                                                                    setShowInlineTemplatePicker(true);
                                                                } else if (s === "Update profile photo") {
                                                                    navigate("/profile");
                                                                } else if (
                                                                    (s === "Try again" || s === "Edit again")
                                                                    && showFlyerMediaAttach(selectedTab, !!activeTemplate)
                                                                    && lastFlyerEditContextRef.current?.s3Key
                                                                    && !lastFlyerEditContextRef.current?.fromTemplate
                                                                ) {
                                                                    const fc = lastFlyerEditContextRef.current;
                                                                    void runFlyerEditStream(
                                                                        fc?.prompt || "Apply the requested edits to my uploaded flyer.",
                                                                        fc?.s3Key,
                                                                        fc?.url,
                                                                        {
                                                                            userVisibleText: s,
                                                                            sessionId: fc?.sessionId ?? activeSessionId ?? undefined,
                                                                        },
                                                                    );
                                                                } else if (selectedTab === "ai_itinerary" && !activeTemplate && (itineraryUploadedFiles.length > 0 || itineraryEditSessionRef.current)) {
                                                                    // Itinerary upload clarification chip — auto-submit directly
                                                                    void handleItineraryUploadGenerate(s);
                                                                } else {
                                                                    setInput(s);
                                                                    textareaRef.current?.focus();
                                                                }
                                                            }}
                                                        >
                                                            {s}
                                                        </button>
                                                    ))}
                                                </div>
                                            )}


                                            <div className="msg-time">{formatISTTimeShort(msg.timestamp)}</div>
                                        </div>
                                        </>)}
                                    </div>
                                ))}
                                {isTyping && <TypingIndicator />}
                            </>
                        )}
                        <div ref={bottomRef} />
                    </div>

                    {/* Input bar */}
                    <div className="input-bar">
                        {/* Template selection banner */}
                        {activeQuotaWarning && (
                            <div style={{
                                padding: "8px 12px", marginBottom: 6, borderRadius: 8,
                                background: "rgba(251,146,60,0.12)",
                                border: "1px solid rgba(251,146,60,0.35)",
                                fontSize: 12, color: "#fdba74", lineHeight: 1.4,
                            }}>
                                ⚠️ {activeQuotaWarning}
                            </div>
                        )}
                        {activeTemplate && (
                            <div className="template-banner">
                                <span className="template-banner-label">
                                    {activeTemplate.emoji} {activeTemplate.name}
                                    {templateSessionStarting
                                        ? " — starting session…"
                                        : activeSessionId
                                            ? " — describe your package (session active)"
                                            : " — describe your package, then press Enter"}
                                </span>
                                <button
                                    type="button"
                                    className="template-banner-clear"
                                    onClick={clearTemplateSelection}
                                    title="Clear template"
                                >
                                    ✕
                                </button>
                            </div>
                        )}

                        {showFlyerMediaAttach(selectedTab, !!activeTemplate) && (
                            <FlyerMediaAttach
                                attachments={flyerAttachments}
                                onAttachmentsChange={setFlyerAttachments}
                                myAssets={myAssets.filter((a) => a.type === "image" || a.type === "video") as import("../modules/ai_media/common/TemplateChatAttachments").FlyerMediaAssetPick[]}
                                disabled={isTyping || generatingFromTemplate || templateSessionStarting || flyerRefsUploading}
                            />
                        )}

                        {/* Itinerary upload badges in input bar */}
                        {selectedTab === "ai_itinerary" && (!activeTemplate || activeTemplate.asset_type === "itinerary") && itineraryUploadedFiles.length > 0 && (
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, padding: "6px 10px", background: "rgba(52,211,153,0.07)", borderRadius: 8, marginBottom: 4 }}>
                                {itineraryUploadedFiles.map((f, i) => (
                                    <div key={f.s3_key} style={{ display: "flex", alignItems: "center", gap: 5, padding: "2px 8px", background: "rgba(52,211,153,0.12)", borderRadius: 20, fontSize: 11 }}>
                                        <span>📄</span>
                                        <span style={{ color: "#6ee7b7", maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.name}</span>
                                        <button type="button" onClick={() => setItineraryUploadedFiles(prev => prev.filter((_, idx) => idx !== i))} style={{ background: "none", border: "none", color: "#6b7280", cursor: "pointer", fontSize: 11, padding: "0 1px", lineHeight: 1 }} title="Remove">✕</button>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* ── Chat input ─────────────────────────────────────────────────── */}
                        {(
                            <div className="input-inner" style={{ position: "relative" }}>
                                {showFlyerMediaAttach(selectedTab, !!activeTemplate) && (
                                    <FlyerMediaAttach
                                        inline
                                        attachments={flyerAttachments}
                                        onAttachmentsChange={setFlyerAttachments}
                                        myAssets={myAssets.filter((a) => a.type === "image" || a.type === "video") as import("../modules/ai_media/common/TemplateChatAttachments").FlyerMediaAssetPick[]}
                                        disabled={isTyping || generatingFromTemplate || templateSessionStarting || flyerRefsUploading}
                                    />
                                )}

                                {/* Inline + upload button for itinerary tab (no template, or an itinerary template selected) */}
                                {selectedTab === "ai_itinerary" && (!activeTemplate || activeTemplate.asset_type === "itinerary") && (
                                    <>
                                        <input
                                            ref={itineraryFileRef}
                                            type="file"
                                            accept=".pdf,.docx,.doc,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword,image/jpeg,image/png,image/webp"
                                            multiple
                                            style={{ display: "none" }}
                                            onChange={e => void handleItineraryFileUpload(e.target.files)}
                                            onClick={e => { (e.target as HTMLInputElement).value = ""; }}
                                        />
                                        <button
                                            type="button"
                                            title={itineraryUploadedFiles.length > 0 ? `Add more files (${itineraryUploadedFiles.length} attached)` : "Upload your own itinerary (PDF/DOCX/image)"}
                                            onClick={() => itineraryFileRef.current?.click()}
                                            disabled={itineraryUploading || itineraryGenerating}
                                            style={{
                                                flexShrink: 0, width: 34, height: 34,
                                                display: "flex", alignItems: "center", justifyContent: "center",
                                                background: itineraryUploadedFiles.length > 0 ? "rgba(52,211,153,0.15)" : "rgba(255,255,255,0.06)",
                                                border: itineraryUploadedFiles.length > 0 ? "1.5px solid rgba(52,211,153,0.4)" : "1.5px solid rgba(255,255,255,0.12)",
                                                borderRadius: 8, cursor: "pointer", color: itineraryUploadedFiles.length > 0 ? "#6ee7b7" : "#9ca3af",
                                                fontSize: 16, transition: "all 0.2s", position: "relative",
                                            }}
                                        >
                                            {itineraryUploading
                                                ? <span style={{ display: "inline-block", width: 13, height: 13, border: "2px solid rgba(110,231,183,0.3)", borderTopColor: "#6ee7b7", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
                                                : itineraryUploadedFiles.length > 0
                                                    ? <><span>📄</span><span style={{ position: "absolute", top: -4, right: -4, background: "#34d399", color: "#000", borderRadius: "50%", width: 14, height: 14, fontSize: 9, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700 }}>{itineraryUploadedFiles.length}</span></>
                                                    : "＋"}
                                        </button>
                                    </>
                                )}
                                {/* Mirror overlay for {variable} red highlight — flyer/video only */}
                                {activeTemplate && (
                                    <div
                                        ref={mirrorRef}
                                        aria-hidden="true"
                                        style={{
                                            position: "absolute",
                                            top: 0,
                                            left: showFlyerMediaAttach(selectedTab, !!activeTemplate) || (selectedTab === "ai_itinerary" && (!activeTemplate || activeTemplate.asset_type === "itinerary")) ? 46 : 0,
                                            right: 40,
                                            bottom: 0,
                                            padding: "10px 12px",
                                            fontSize: 14, lineHeight: "1.5",
                                            fontFamily: "inherit",
                                            whiteSpace: "pre-wrap",
                                            wordBreak: "break-word",
                                            overflowY: "auto",
                                            overflowX: "hidden",
                                            pointerEvents: "none",
                                            color: "#e2e8f0",
                                            zIndex: 1,
                                        }}
                                        dangerouslySetInnerHTML={{ __html: highlightedInputHtml }}
                                    />
                                )}
                                <textarea
                                    ref={textareaRef}
                                    className="input-textarea"
                                    rows={activeTemplate ? 4 : 1}
                                    placeholder={
                                        (activeTemplate as AnyTemplateItem | null)?.asset_type === "slides"
                                            ? "Enter your presentation topic and requirements…"
                                            : activeTemplate
                                                ? "Fill in your deal details — replace each {variable} with your value."
                                                : mediaMode === "chat"
                                                    ? "Ask anything about travel — destinations, packages, flights, hotels… (Enter to send)"
                                                    : selectedTab === "ai_itinerary"
                                                        ? itineraryUploadedFiles.length > 0
                                                            ? "Describe changes (e.g. update images, change logo) or press ✨ to generate…"
                                                            : "Upload your itinerary with ＋ or pick a template above."
                                                        : mediaMode === "image"
                                                        ? lastFlyerEditContextRef.current?.s3Key
                                                            ? "Describe the edits you want on your uploaded flyer…"
                                                            : "Use + to attach a flyer to edit, or pick a template above to create one."
                                                        : "Describe the travel video/reel you want."
                                    }
                                    value={input}
                                    onChange={(e) => {
                                        setInput(e.target.value);
                                        e.target.style.height = "auto";
                                        const maxH = activeTemplate ? 300 : 120;
                                        e.target.style.height = `${Math.min(e.target.scrollHeight, maxH)}px`;
                                        syncMirrorScroll();
                                    }}
                                    onScroll={syncMirrorScroll}
                                    onKeyDown={handleKeyDown}
                                    disabled={isTyping || generatingFromTemplate || templateSessionStarting || flyerRefsUploading || itineraryGenerating}
                                    style={{
                                        resize: activeTemplate ? "vertical" : "none",
                                        position: "relative",
                                        zIndex: 2,
                                        ...(activeTemplate && (activeTemplate as AnyTemplateItem).asset_type !== "slides" ? { color: "transparent", background: "transparent", caretColor: "#e2e8f0" } : {}),
                                    }}
                                />
                                <button
                                    className="send-btn"
                                    onClick={() =>
                                        activeTemplate
                                            ? void submitTemplateGeneration()
                                            : selectedTab === "ai_itinerary" && !activeTemplate && itineraryUploadedFiles.length > 0
                                                ? void handleItineraryUploadGenerate(input.trim())
                                                : void sendMessage()
                                    }
                                    disabled={
                                        (!input.trim() && !(showFlyerMediaAttach(selectedTab, !!activeTemplate) && flyerAttachments.length) && !(selectedTab === "ai_itinerary" && itineraryUploadedFiles.length > 0))
                                        || isTyping
                                        || generatingFromTemplate
                                        || templateSessionStarting
                                        || flyerRefsUploading
                                        || itineraryGenerating
                                    }
                                    title={(activeTemplate as unknown as AnyTemplateItem | null) ? `Generate ${(activeTemplate as unknown as AnyTemplateItem).asset_type}` : mediaMode === "chat" ? "Send (Enter)" : "Build prompt preview (Enter)"}
                                >
                                    {generatingFromTemplate ? (
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ animation: "spin 1s linear infinite" }}>
                                            <circle cx="12" cy="12" r="10" strokeDasharray="32" strokeDashoffset="10" />
                                        </svg>
                                    ) : activeTemplate ? (
                                        <span style={{ fontSize: 14 }}>✨</span>
                                    ) : (
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
                                        </svg>
                                    )}
                                </button>
                            </div>
                        )}
                        <div className="input-hint">
                            {activeTemplate
                                ? "Edit the prompt above, then press Enter or click Generate"
                                : selectedTab === "ai_itinerary"
                                    ? "Pick a template above, fill in details, then press Enter or Generate"
                                    : mediaMode === "image"
                                    ? lastFlyerEditContextRef.current?.s3Key
                                        ? "+ to replace flyer · describe edits · Enter to apply"
                                        : "+ to attach a flyer · or pick a template to create"
                                    : mediaMode === "chat"
                                    ? "Shift + Enter for new line · Enter to send"
                                    : env.flyerPreviewEnabled
                                        ? "Shift + Enter for new line · Enter to build prompt preview"
                                        : "Shift + Enter for new line · Enter to generate directly"}
                        </div>
                    </div>
                </div>
                {promptSidebarOpen && (mediaMode !== "chat" || pendingTemplateGen) && (
                    <aside className="prompt-sidebar">
                        <div className="prompt-sidebar-header">
                            <div className="prompt-sidebar-title">
                                Final {pendingTemplateGen?.categoryLabel ?? (pendingMediaType === "video" ? "Video" : "Flyer")} Prompt
                            </div>
                            <button className="toggle-sidebar-btn" onClick={() => setPromptSidebarOpen(false)} title="Close prompt sidebar">
                                ✕
                            </button>
                        </div>
                        <div className="prompt-sidebar-body">
                            <pre>{previewPrompt || "No preview prompt yet."}</pre>
                        </div>
                        <div className="prompt-sidebar-footer">
                            <button
                                className="prompt-generate-btn"
                                onClick={() => void runConfirmedMediaGeneration()}
                                disabled={(!previewPrompt && !pendingTemplateGen) || isGeneratingMedia || isTyping}
                            >
                                {isGeneratingMedia ? "Generating..." : "Generate Now"}
                            </button>
                            <div className="prompt-sidebar-note">
                                This is the final enriched prompt that will be sent to the generation model.
                            </div>
                        </div>
                    </aside>
                )}
                </div>
            </div>

            {enquiryDeal && (
                <EnquiryModal
                    deal={enquiryDeal}
                    sessionId={activeSessionId ?? ""}
                    gatheredInfo={lastGatheredInfo}
                    ownsDeal={Boolean(
                        user?.id && enquiryDeal.dealer_id === user.id
                    )}
                    onClose={() => setEnquiryDeal(null)}
                    onSubmitted={(leadId) => {
                        if (enquiryDeal?._from_cross_sell) {
                            void analyticsService.trackEvent({
                                event_type: "cross_sell_enquiry_sent",
                                source: "rag_chat",
                                item_id: enquiryDeal.deal_id ?? enquiryDeal.id,
                                item_type: enquiryDeal.category ?? "cross_sell",
                                item_title: enquiryDeal.title,
                                seller_id: enquiryDeal.dealer_id,
                                session_id: activeSessionId ?? undefined,
                                metadata: { lead_id: leadId },
                            });
                        }
                        setEnquiryDeal(null);
                        setMessages((prev) => [
                            ...prev,
                            {
                                id: `system-${Date.now()}`,
                                role: "assistant" as const,
                                type: "enquiry_success" as const,
                                content: "",
                                lead_id: leadId,
                                timestamp: new Date(),
                            },
                        ]);
                    }}
                />
            )}

            {/* ── Inline template picker (shown when "New template" chip is clicked) ── */}
            {showInlineTemplatePicker && (
                <div
                    onClick={() => setShowInlineTemplatePicker(false)}
                    style={{
                        position: "fixed", inset: 0, zIndex: 1850,
                        background: "rgba(0,0,0,0.8)", backdropFilter: "blur(6px)",
                        display: "flex", alignItems: "flex-end", justifyContent: "center",
                        padding: "0",
                    }}
                >
                    <div
                        onClick={e => e.stopPropagation()}
                        style={{
                            background: "#0f1117", borderRadius: "20px 20px 0 0",
                            border: "1px solid rgba(255,255,255,0.1)", borderBottom: "none",
                            width: "100%", maxWidth: 860,
                            maxHeight: "80vh", display: "flex", flexDirection: "column",
                            overflow: "hidden",
                        }}
                    >
                        {/* Header */}
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "18px 20px 14px", borderBottom: "1px solid rgba(255,255,255,0.08)", flexShrink: 0 }}>
                            <div>
                                <div style={{ fontSize: 16, fontWeight: 700, color: "#f1f5f9" }}>Choose a Template</div>
                                <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>Flyers &amp; Videos</div>
                            </div>
                            <button onClick={() => setShowInlineTemplatePicker(false)} style={{ background: "none", border: "none", color: "#6b7280", fontSize: 22, cursor: "pointer", lineHeight: 1, padding: 0 }}>✕</button>
                        </div>
                        {/* Grid */}
                        <div style={{ overflowY: "auto", padding: "16px 20px 24px", flex: 1 }}>
                            {(() => {
                                const flyerAndVideo = templates.filter(t => t.asset_type === "flyer" || t.asset_type === "video");
                                if (!flyerAndVideo.length) return (
                                    <div style={{ color: "#6b7280", textAlign: "center", padding: "40px 0", fontSize: 14 }}>No templates available.</div>
                                );
                                const flyers = flyerAndVideo.filter(t => t.asset_type === "flyer");
                                const videos = flyerAndVideo.filter(t => t.asset_type === "video");
                                return (
                                    <>
                                        {flyers.length > 0 && (
                                            <>
                                                <div style={{ fontSize: 12, fontWeight: 700, color: "#9ca3af", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 10 }}>🖼️ Flyers</div>
                                                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 10, marginBottom: 20 }}>
                                                    {flyers.map(tmpl => (
                                                        <button
                                                            key={tmpl.id}
                                                            onClick={() => { setShowInlineTemplatePicker(false); setTemplatePreview(tmpl); }}
                                                            style={{
                                                                position: "relative", border: "1px solid rgba(255,255,255,0.12)",
                                                                borderRadius: 10, cursor: "pointer", overflow: "hidden",
                                                                textAlign: "left", background: "#111", padding: 0,
                                                                aspectRatio: tmpl.aspect_ratio === "16:9" ? "16/9" : "9/14",
                                                                transition: "all 0.15s",
                                                            }}
                                                            onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = "rgba(167,139,250,0.6)"; (e.currentTarget as HTMLButtonElement).style.transform = "scale(1.03)"; }}
                                                            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = "rgba(255,255,255,0.12)"; (e.currentTarget as HTMLButtonElement).style.transform = "scale(1)"; }}
                                                        >
                                                            {tmpl.preview_image_url
                                                                ? <img src={tmpl.preview_image_url} alt={tmpl.name} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} loading="lazy" />
                                                                : <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 34 }}>{tmpl.emoji}</div>
                                                            }
                                                            <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(0,0,0,0.85) 38%, transparent)" }} />
                                                            <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, padding: "8px" }}>
                                                                <div style={{ fontSize: 11, fontWeight: 700, color: "#fff", lineHeight: 1.2 }}>{tmpl.name}</div>
                                                                <div style={{ fontSize: 10, color: "#9ca3af", marginTop: 2 }}>{tmpl.category_label}</div>
                                                            </div>
                                                        </button>
                                                    ))}
                                                </div>
                                            </>
                                        )}
                                        {videos.length > 0 && (
                                            <>
                                                <div style={{ fontSize: 12, fontWeight: 700, color: "#9ca3af", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 10 }}>🎬 Videos</div>
                                                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 10 }}>
                                                    {videos.map(tmpl => (
                                                        <button
                                                            key={tmpl.id}
                                                            onClick={() => { setShowInlineTemplatePicker(false); setTemplatePreview(tmpl); }}
                                                            style={{
                                                                position: "relative", border: "1px solid rgba(255,255,255,0.12)",
                                                                borderRadius: 10, cursor: "pointer", overflow: "hidden",
                                                                textAlign: "left", background: "#111", padding: 0,
                                                                aspectRatio: "16/9", transition: "all 0.15s",
                                                            }}
                                                            onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = "rgba(99,102,241,0.6)"; (e.currentTarget as HTMLButtonElement).style.transform = "scale(1.03)"; }}
                                                            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = "rgba(255,255,255,0.12)"; (e.currentTarget as HTMLButtonElement).style.transform = "scale(1)"; }}
                                                        >
                                                            {tmpl.preview_image_url
                                                                ? <img src={tmpl.preview_image_url} alt={tmpl.name} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} loading="lazy" />
                                                                : <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 34 }}>{tmpl.emoji}</div>
                                                            }
                                                            <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(0,0,0,0.85) 38%, transparent)" }} />
                                                            <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, padding: "8px" }}>
                                                                <div style={{ fontSize: 11, fontWeight: 700, color: "#fff", lineHeight: 1.2 }}>{tmpl.name}</div>
                                                                <div style={{ fontSize: 10, color: "#818cf8", marginTop: 2 }}>{tmpl.category_label}</div>
                                                            </div>
                                                        </button>
                                                    ))}
                                                </div>
                                            </>
                                        )}
                                    </>
                                );
                            })()}
                        </div>
                    </div>
                </div>
            )}

            {/* ── Template preview dialog ───────────────────────────────────── */}
            {templatePreview && (
                <div
                    onClick={() => setTemplatePreview(null)}
                    style={{
                        position: "fixed", inset: 0, zIndex: 1900,
                        background: "rgba(0,0,0,0.75)", backdropFilter: "blur(6px)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        padding: "20px 16px",
                    }}
                >
                    <div
                        onClick={e => e.stopPropagation()}
                        style={{
                            background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)",
                            border: "1px solid rgba(255,255,255,0.1)",
                            borderRadius: 18, boxShadow: "0 24px 80px rgba(0,0,0,0.7)",
                            width: "100%", maxWidth: 520,
                            maxHeight: "90vh", overflowY: "auto",
                            display: "flex", flexDirection: "column",
                        }}
                    >
                        {/* Dialog header */}
                        <div style={{ padding: "20px 22px 16px", borderBottom: "1px solid rgba(255,255,255,0.07)", display: "flex", alignItems: "flex-start", gap: 14 }}>
                            <div style={{ fontSize: 36, lineHeight: 1, flexShrink: 0 }}>{templatePreview.emoji}</div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 17, fontWeight: 700, color: "#f1f5f9", marginBottom: 4 }}>{templatePreview.name}</div>
                                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                                    <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 20, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", background: templatePreview.asset_type === "video" ? "rgba(99,102,241,0.25)" : templatePreview.asset_type === "itinerary" ? "rgba(52,211,153,0.2)" : templatePreview.asset_type === "slides" ? "rgba(251,191,36,0.2)" : "rgba(167,139,250,0.2)", color: templatePreview.asset_type === "video" ? "#818cf8" : templatePreview.asset_type === "itinerary" ? "#6ee7b7" : templatePreview.asset_type === "slides" ? "#fcd34d" : "#c4b5fd" }}>
                                        {templatePreview.asset_type === "slides" ? "📊 Slides" : templatePreview.asset_type === "itinerary" ? "🗺️ Itinerary" : templatePreview.asset_type === "video" ? "🎬 Video" : "🖼️ Flyer"}
                                    </span>
                                    <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 20, background: "rgba(255,255,255,0.07)", color: "#9ca3af", fontWeight: 600 }}>{templatePreview.category_label}</span>
                                    {templatePreview.style && <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 20, background: "rgba(255,255,255,0.07)", color: "#9ca3af", fontWeight: 600 }}>{templatePreview.style}</span>}
                                    {templatePreview.aspect_ratio && <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 20, background: "rgba(255,255,255,0.07)", color: "#9ca3af", fontWeight: 600 }}>{templatePreview.aspect_ratio}</span>}
                                </div>
                            </div>
                            <button onClick={() => setTemplatePreview(null)} style={{ background: "none", border: "none", color: "#6b7280", fontSize: 20, cursor: "pointer", flexShrink: 0, padding: 0, lineHeight: 1 }}>✕</button>
                        </div>

                        {/* Preview image (flyer/video) */}
                        {(templatePreview.asset_type === "flyer" || templatePreview.asset_type === "video") && templatePreview.preview_image_url && (
                            <div style={{ padding: "14px 22px 0" }}>
                                <img src={templatePreview.preview_image_url} alt={templatePreview.name} style={{ width: "100%", borderRadius: 10, objectFit: "cover", maxHeight: templatePreview.aspect_ratio === "16:9" ? 180 : 260, display: "block" }} />
                            </div>
                        )}

                        {/* Itinerary — slide-wise PDF page preview */}
                        {templatePreview.asset_type === "itinerary" && (templatePreview.template_page_image_urls ?? []).length > 0 && (
                            <ItineraryPageSlider pages={templatePreview.template_page_image_urls ?? []} />
                        )}
                        {/* Itinerary — fallback cover thumbnail when no page slides */}
                        {templatePreview.asset_type === "itinerary" && (templatePreview.template_page_image_urls ?? []).length === 0 && templatePreview.preview_image_url && (
                            <div style={{ padding: "14px 22px 0" }}>
                                <img src={templatePreview.preview_image_url} alt={templatePreview.name} style={{ width: "100%", borderRadius: 10, objectFit: "cover", maxHeight: 320, display: "block" }} />
                            </div>
                        )}

                        {/* Body */}
                        <div style={{ padding: "16px 22px", flex: 1 }}>
                            {/* Tagline / mood */}
                            {(templatePreview.tagline || templatePreview.mood) && (
                                <p style={{ margin: "0 0 14px", fontSize: 13, color: "#cbd5e1", fontStyle: "italic", lineHeight: 1.5 }}>
                                    {templatePreview.tagline || templatePreview.mood}
                                </p>
                            )}

                            {templatePreview.asset_type !== "itinerary" && (
                                <div style={{ marginBottom: 14, padding: "10px 12px", borderRadius: 10, background: "rgba(15,23,42,0.55)", border: "1px solid rgba(99,102,241,0.18)" }}>
                                    <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 6 }}>
                                        Template prompt preview
                                    </div>
                                    <pre style={{ margin: 0, fontSize: 11, lineHeight: 1.55, color: "#cbd5e1", whiteSpace: "pre-wrap", wordBreak: "break-word", fontFamily: "ui-monospace, monospace", maxHeight: 160, overflow: "auto" }}>
                                        {templatePreview.fields?.filter(f => f.required).map(f => f.label).join(" · ") || "Describe your package in the chat after selecting this template."}
                                    </pre>
                                </div>
                            )}

                            {/* Slides structure */}
                            {templatePreview.asset_type === "slides" && templatePreview.slides && templatePreview.slides.length > 0 && (
                                <div style={{ marginBottom: 14 }}>
                                    <div style={{ fontSize: 11, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 10 }}>
                                        📊 Slide Structure — {templatePreview.slides.length} slides
                                    </div>
                                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                        {templatePreview.slides.map(slide => (
                                            <div key={slide.num} style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "8px 12px", borderRadius: 8, background: "rgba(251,191,36,0.06)", border: "1px solid rgba(251,191,36,0.1)" }}>
                                                <span style={{ fontSize: 11, fontWeight: 800, color: "#fbbf24", minWidth: 20, flexShrink: 0 }}>{slide.num}</span>
                                                <div>
                                                    <div style={{ fontSize: 12, fontWeight: 600, color: "#f1f5f9", lineHeight: 1.3 }}>{slide.title}</div>
                                                    {slide.hint && <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>{slide.hint}</div>}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Itinerary fields preview */}
                            {templatePreview.asset_type === "itinerary" && templatePreview.fields.length > 0 && (
                                <div style={{ marginBottom: 14 }}>
                                    <div style={{ fontSize: 11, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 10 }}>
                                        🗺️ Itinerary Fields
                                    </div>
                                    <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
                                        {templatePreview.fields.map(f => (
                                            <div key={f.key} style={{ display: "flex", alignItems: "center", gap: 5, padding: "5px 10px", borderRadius: 8, background: "rgba(52,211,153,0.07)", border: "1px solid rgba(52,211,153,0.12)" }}>
                                                <span style={{ fontSize: 12, color: "#6ee7b7", fontWeight: 600 }}>{f.label}</span>
                                                {f.required && <span style={{ fontSize: 9, color: "#f87171" }}>*</span>}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}


                            {/* Required fields (flyer/video) */}
                            {(templatePreview.asset_type === "flyer" || templatePreview.asset_type === "video") && templatePreview.fields.filter(f => f.required).length > 0 && (
                                <div style={{ marginBottom: 14 }}>
                                    <div style={{ fontSize: 11, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 8 }}>Required Details</div>
                                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                                        {templatePreview.fields.filter(f => f.required).map(f => (
                                            <span key={f.key} style={{ fontSize: 11, padding: "3px 9px", borderRadius: 6, background: "rgba(167,139,250,0.12)", border: "1px solid rgba(167,139,250,0.2)", color: "#c4b5fd", fontWeight: 600 }}>
                                                {f.label}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* CTA text */}
                            {templatePreview.cta_text && (
                                <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4 }}>
                                    <span style={{ fontWeight: 600, color: "#9ca3af" }}>CTA: </span>{templatePreview.cta_text}
                                </div>
                            )}
                        </div>

                        {/* Footer actions */}
                        <div style={{ padding: "14px 22px 20px", borderTop: "1px solid rgba(255,255,255,0.07)", display: "flex", gap: 10 }}>
                            <button
                                onClick={() => setTemplatePreview(null)}
                                style={{ flex: 1, padding: "10px", borderRadius: 10, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.05)", color: "#9ca3af", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
                            >Cancel</button>
                            <button
                                onClick={() => {
                                    const tmpl = templatePreview;
                                    setTemplatePreview(null);
                                    selectTemplate(tmpl);
                                }}
                                style={{
                                    flex: 2, padding: "10px", borderRadius: 10, border: "none",
                                    background: templatePreview.asset_type === "itinerary"
                                        ? "linear-gradient(135deg, #059669, #34d399)"
                                        : templatePreview.asset_type === "slides"
                                            ? "linear-gradient(135deg, #d97706, #fbbf24)"
                                            : templatePreview.asset_type === "video"
                                                ? "linear-gradient(135deg, #4f46e5, #818cf8)"
                                                : "linear-gradient(135deg, #7c3aed, #a78bfa)",
                                    color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer",
                                }}
                            >✨ Use This Template</button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Media fullscreen overlay ──────────────────────────────────── */}
            {mediaFullscreen && (
                <div
                    onClick={() => setMediaFullscreen(null)}
                    style={{
                        position: "fixed", inset: 0, zIndex: 2000,
                        background: "rgba(0,0,0,0.92)", display: "flex",
                        alignItems: "center", justifyContent: "center",
                        cursor: "zoom-out",
                    }}
                >
                    {isVideoMediaUrl(mediaFullscreen) ? (
                        <video
                            src={mediaFullscreen}
                            controls
                            autoPlay
                            playsInline
                            onClick={e => e.stopPropagation()}
                            style={{ maxWidth: "90vw", maxHeight: "90vh", borderRadius: 12, boxShadow: "0 20px 80px rgba(0,0,0,0.8)" }}
                        />
                    ) : isPdfMediaUrl(mediaFullscreen) ? (
                        <iframe
                            src={mediaFullscreen}
                            title="Itinerary PDF"
                            onClick={(e: React.MouseEvent) => e.stopPropagation()}
                            style={{ width: "85vw", height: "90vh", borderRadius: 12, border: "none", boxShadow: "0 20px 80px rgba(0,0,0,0.8)" }}
                        />
                    ) : (
                        <img
                            src={mediaFullscreen}
                            alt="Fullscreen preview"
                            style={{ maxWidth: "90vw", maxHeight: "90vh", borderRadius: 12, objectFit: "contain", boxShadow: "0 20px 80px rgba(0,0,0,0.8)" }}
                        />
                    )}
                    <button
                        onClick={() => setMediaFullscreen(null)}
                        style={{
                            position: "absolute", top: 20, right: 20,
                            background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.2)",
                            borderRadius: "50%", width: 40, height: 40, fontSize: 18, color: "#fff",
                            cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                        }}
                    >✕</button>
                </div>
            )}

            {editingVideoDeal && (
                <div
                    onClick={() => setEditingVideoDeal(null)}
                    style={{
                        position: "fixed", inset: 0, zIndex: 2000,
                        background: "rgba(0,0,0,0.92)", display: "flex",
                        alignItems: "flex-start", justifyContent: "center",
                        overflowY: "auto", cursor: "default",
                    }}
                >
                    <div onClick={e => e.stopPropagation()} style={{ marginTop: "4vh", background: "#0a0e1a", borderRadius: 16, border: "1px solid rgba(255,255,255,0.08)" }}>
                        <ChatOverlayEditor
                            sessionId={editingVideoDeal.session_id!}
                            videoUrl={editingVideoDeal.video_url || editingVideoDeal.image || ""}
                            onCancel={() => setEditingVideoDeal(null)}
                            onSaved={(newVideoUrl) => {
                                const dealId = editingVideoDeal.id;
                                setMessages(prev => prev.map(m =>
                                    m.deals?.some(d => d.id === dealId)
                                        ? { ...m, deals: m.deals!.map(d => d.id === dealId ? { ...d, image: newVideoUrl, video_url: newVideoUrl } : d) }
                                        : m
                                ));
                                setEditingVideoDeal(null);
                            }}
                        />
                    </div>
                </div>
            )}

            {/* ── Toast notification ────────────────────────────────────────── */}
            {toastMsg && (
                <div style={{
                    position: "fixed", bottom: 90, left: "50%", transform: "translateX(-50%)",
                    zIndex: 1200, background: "#1e1e2e", border: "1px solid rgba(248,113,113,0.4)",
                    borderRadius: 10, padding: "12px 20px",
                    color: "#f87171", fontSize: 13, fontWeight: 600,
                    boxShadow: "0 4px 20px rgba(0,0,0,0.5)",
                    pointerEvents: "none", whiteSpace: "nowrap",
                }}>
                    ⚠️ {toastMsg}
                </div>
            )}

            {enquiryDmc && (
                <DmcEnquiryModal
                    dmc={enquiryDmc.dmc}
                    itineraryText={enquiryDmc.itinerary}
                    sessionId={activeSessionId ?? ""}
                    gatheredInfo={lastGatheredInfo}
                    ownsSelf={Boolean(
                        user?.id && enquiryDmc.dmc.dealer_id === user.id,
                    )}
                    onClose={() => setEnquiryDmc(null)}
                    onSubmitted={(leadId) => {
                        const dmcName = enquiryDmc.dmc.seller_name;
                        setEnquiryDmc(null);
                        setMessages((prev) => [
                            ...prev,
                            {
                                id: `system-${Date.now()}`,
                                role: "assistant" as const,
                                type: "enquiry_success" as const,
                                content: "",
                                lead_id: leadId,
                                seller_name: dmcName,
                                timestamp: new Date(),
                            },
                        ]);
                    }}
                />
            )}

            <LoginSignupModal
                open={showAuthModal}
                onClose={() => setShowAuthModal(false)}
                returnTo="/chat"
                message="Create a free account or sign in to generate flyers, itineraries, slides and videos with the AI assistant."
            />
        </>
    );
};

export default RagChatPage;
